import {rememberFileAccessReturn,takeFileAccessReturn} from './file-access-recovery.js';
import {copySelectedMessage} from './message-copy.js';
import {anchorRestoreClock,restoreTimeLeft} from './message-restore-clock.js';
import {restoreWithReconciliation} from './message-restore-action.js';
import {canModerateMessages} from './message-moderation.js';
import {attachmentPath,type ChatAttachment} from '../shared/managed-attachment.js';
import {uploadChatManagedFile,cancelChatManagedUpload,cancelManagedPending,managedAttachmentUnlinkCommand,managedPendingCommand,waitForManagedAttachment,type ManagedUploadAttachment} from './managed-files.js';
import { isAttachmentImage } from '../shared/attachment-media.js';
import {createMessageInput, normalizeMessageInput, type MessageInput} from './rich-message-input.js';
import {messageLengthError} from '../shared/message-length.js';
import {uploadRequest,type UploadTransfer} from './upload-request.js';
import {uploadProgressChip,updateUploadProgress,type ComposerUpload} from './upload-progress.js';
import {toWireMessageCommand} from '../shared/message-command.js';
import {showStickerPicker,closeStaleStickerPicker} from './sticker-picker.js';
import {showEmojiPicker} from './emoji-picker.js';
import {serviceNavigation} from './service-navigation.js';
import {readPendingSnapshot, clearPendingSnapshot, type PendingOwner} from './outbox-recovery.js';
import {openChatDatabase as openDb, completeStorageTransaction as transactionDone, readStorageRequest} from './chat-database.js';
import {SidebarWidth} from './sidebar-width.js';
import {FilesCards,fileLinks,loadFileCard} from './files-cards.js';
import type {FilesIdentity} from '../shared/files-card.js';
import { LoadingStatus } from './loading-status.js';
import { showTodoList, type TodoDialog } from './todo-list.js';
import { parseTodoCommand, type TodoSummary } from '../shared/todo.js';
import { MessageNodes } from './message-nodes.js';
import {isStickerAttachment,isStickerOnlyMessage,syncMessageAvatarRails} from './message-layout.js';
import { pmTaskLinks } from './pm-task-card.js';
import { PmTaskPreviews } from './pm-task-previews.js';
import { pmMessageSnapshotPath } from '../shared/pm-message-snapshot.js';
import { createdEventIds, changedEventIds, mergeCreatedMessages, onlyReadReceiptEvents } from './history-delta.js';
import { refreshApplicationAssets } from './app-update.js';
import { readApiResponse } from './api-response.js';
import { ConnectionStatus } from './connection-status';
import { MESSAGE_SOUNDS, SOUND_LEVELS, readSoundPreferences } from './sound-preferences.js';
import { applyTheme, readTheme, saveTheme, type Theme } from './theme.js';
import { shouldSuppressOpenChatPush } from './push-focus.js';
import { LogoutFence, logoutFenceKey, logoutFencePrefix, type LogoutIntent } from './logout-fence.js';
import { ReplyWork } from './reply-work.js';
import { actionMenu, suspendMenu, restoreMenu, pageZoom } from './action-menu.js';
import { installMessageMenuGestures } from './message-menu-gestures.js';
import { installTooltips } from './tooltip.js';
import { blurOnKeyboardClose, isSoftEnter } from './soft-keyboard.js';
import { chatHistoryUpdate, chatPath, parseChatRoute, updateMobileChatHistory } from './chat-route.js';
import { carriesFiles, installFileDrop } from './file-drop.js';
import { fitComposerInput } from './composer-size.js';
import { readStartupFailure, reportStartupFailure } from './startup-failure.js';
import { dayLabel, fullStamp, messageTime, sameDay } from './message-dates.js';
import { ConversationPages, type ConversationDetail, type ConversationRequest, type ConversationRow } from './conversation-pages';

type Bootstrap = {
  server_time: string;
  managed_files_enabled?:boolean;
  attachment_transfer_queue_enabled?:boolean;
  user: { id: string; workspace_id: string; display_name: string; avatar_key: string | null; status_text: string | null; email?: string | null; is_workspace_admin?: boolean };
  csrf_token: string;
  can_export?: boolean;
  session_handle: string;
  unread: number;
  bot_analysis_notice: string;
  files_preview_identity?: FilesIdentity|null;
  files_requires_reauthentication?: boolean;
  pm_task_previews?: { enabled: boolean; requires_reauthentication?: boolean };
  push: { enabled: boolean; vapid_public_key: string; vapid_key_id: string };
};
type Conversation = ConversationRow;
type CurrentConversation = { id: string; type: string; display_title: string; archived_at: string | null };
type Attachment = ChatAttachment&{upload?:ManagedUploadAttachment['upload']};
export type KeyboardButton = { text: string; callbackData?: string; url?: string; style?: 'base' | 'primary' | 'attention' };
export type Message = { restore_until?: string|null; retained_native?: boolean; id: string; conversation_id: string; seq: string; sender: { id: string; name: string } | null; kind: string; body: string; body_html?: string; entities?: unknown[]; reply_to_message_id?: string | null; reply_source?: Message['replySource']; replySource?: { state: 'available' | 'deleted' | 'unavailable' | 'error'; text?: string; author?: string; images?: Attachment[] }; mentions?: string[]; mentioned?: { id: string; name: string }[]; attachments?: Attachment[]; version: number; created_at: string; edited_at: string | null; deleted_at: string | null; reactions: { emoji: string; user_id: string }[]; permalink: string; pinned?: boolean; pending?: boolean; error?: string; poll?: Poll | null; keyboard?: KeyboardButton[][] | null; forwarded_from?: { user_id: string; name: string | null; at: string | null } | null };
// Optional only for a cached client during rollback to the pre-metadata search API.
// Never infer a missing result title from the mutable sidebar/selected chat.
type SearchResult = Message & { conversation?: CurrentConversation };
type PollVoter = { id: string; name: string; avatar_key: string | null };
type Poll = { question: string; options: { text: string; voters: PollVoter[] }[] };
type ConversationDetails = ConversationDetail;
type Member = { id: string; display_name: string; role: string; avatar_key?: string | null; last_seen_at?: string | null; online?: boolean; is_bot?: boolean };
type RealtimeEvent = { object_id?: string; type?: string; conversation_id?: string; revision?: string; user_id?: string; active?: boolean; payload?: { object_id?: string; sender_user_id?: string; state?: string; target_user_id?: string } };

let theme: Theme = readTheme(key => localStorage.getItem(key));
applyTheme(theme);

const root = document.querySelector<HTMLDivElement>('#app') ?? (() => { throw new Error('app root missing'); })();

root.addEventListener('copy', copySelectedMessage);

const loadingStatus = new LoadingStatus();
const connectionStatus = new ConnectionStatus();
const startupBlank = (): boolean => root.childElementCount === 0 || Boolean(root.querySelector(':scope > .startup-loading'));

function renderStartupFailure(): void {
  const screen = element('main', 'login');
  const card = element('section', 'login-card');
  card.append(element('h1', '', 'Chat Ostart'), element('p', 'error', 'Не удалось запустить чат. Обновите страницу.'));
  const reload = element('button', 'ghost', 'Обновить'); reload.type = 'button';
  reload.addEventListener('click', () => location.reload());
  card.append(reload); screen.append(card); root.replaceChildren(screen);
}
// Startup is over the moment anything is on screen -- the login card, the shell, anything. After
// that this must keep quiet: a chat that is running throws and rejects for ordinary reasons (an
// aborted request while switching accounts, a cancelled upload), and painting "the chat would not
// start" over a working session because of one of those is worse than the white page it is meant to
// prevent. It also hung the whole privacy suite, whose tests provoke exactly such rejections.
let startupSettled = false;
function startupFailure(error: unknown): void {
  if (startupSettled) return;
  reportStartupFailure(error, {
    isBlank: startupBlank, showFailure: renderStartupFailure,
    store: (key, value) => sessionStorage.setItem(key, value), log: (message, detail) => console.error(message, detail),
  });
}
// Registered before the rest of the module runs, so a throw anywhere before the first render is
// reported too; once something is on screen these are left to the browser's own logging.
window.addEventListener('error', (event) => { if (startupBlank()) startupFailure(event.error ?? event.message); });
window.addEventListener('unhandledrejection', (event) => { if (startupBlank()) startupFailure(event.reason); });

const logoutFence = new LogoutFence({
  get length() { return localStorage.length; },
  key: index => localStorage.key(index),
  getItem: key => localStorage.getItem(key),
  setItem: (key, value) => localStorage.setItem(key, value),
  removeItem: key => localStorage.removeItem(key),
});
let logoutStatus = 'Выход не подтверждён сервером. Личные данные и новый вход заблокированы.';
let logoutBusy = false;
let bootstrap: Bootstrap | null = null;
let restoreNow: (()=>number)|null=null;
let restoreDeadlineTimer:number|null=null;
const restoringMessages=new Set<string>();
let outboxResetOwner: Bootstrap | null = null;
let legacyPending: { account: Bootstrap; valid: () => boolean; items: Record<string, unknown>[] } | null = null;
// Commands awaiting persistence need a projection independent of server history.
const savingPending = new Map<string, { account: Bootstrap; valid: () => boolean; item: Record<string, unknown> }>();
const legacyPendingError = 'Старая очередь без версии участия: отправка заблокирована. Текст сохранён. Проверьте историю и отправьте новое сообщение вручную; прежний запрос мог уже сохраниться на сервере.';
function requirePendingEpoch(item: Record<string, unknown>): void {
  if (typeof item.membership_epoch_id !== 'string' || !item.membership_epoch_id.trim()) throw new Error(legacyPendingError);
}
let conversations: Conversation[] = [];
let current: CurrentConversation | null = null;
let selectionGeneration = 0;
let navigationState: { generation: number; error?: string } | null = null;
// Snapshot ownership is separate from latest-wins older-page arbitration.
let historyGeneration = 0;
let olderHistoryGeneration = 0;
let historyReplacementPending: number | null = null;
// The access boundary that authorized these rows, independent of later sidebar details.
let historyAccess: Pick<ConversationDetail, 'id' | 'membership_epoch_id' | 'visible_from_seq'> | null = null;
let newerHistoryGeneration = 0;
let messageSearchGeneration = 0;
let sessionGeneration = 0;
type SidebarOwner = { key: string; account: Bootstrap; session: number; epoch: string; csrf: string };
let sidebarOwner: SidebarOwner | null = null;
let sidebarError: string | null = null;
let sidebarFavoriteBusy: string | null = null;
let sidebarSearchValue = '';
// Search scope is explicit and expires when leaving the selected chat.
let messageSearchScope: CurrentConversation | null = null;
let messageSearchController: AbortController | null = null;
let messageSearch: { query: string; scopeId: string | null; results: SearchResult[]; error?: string } | null = null;
// "New chat" mode of the sidebar: replaces the chat list with colleagues (filtered by the search
// box) plus group creation, instead of prompt() dialogs.
type Colleague = { id: string; display_name: string; avatar_key: string | null };
type NewChatState = { group: boolean; query: string; users: Colleague[] | null; error: string | null; selected: string[]; title: string; busy: boolean };
let newChat: NewChatState | null = null;
let newChatTimer: number | null = null;
let sidebarSearchTimer: number | null = null;
let sidebarRefreshDirty = false;
let sidebarRefreshTimer: number | null = null;
let sidebarRefreshPromise: Promise<void> | null = null;
let sidebarRefreshCurrent: { promise: Promise<boolean>; resolve: (authorized: boolean) => void; settled: boolean } | null = null;
let sidebarRefreshResolve: (() => void) | null = null;
let sidebarScrollTop = 0;
type HistoryAnchor = { conversationId: string; atBottom: boolean; scrollTop: number; messageId?: string; offset?: number };
let historyScrollAnchor: HistoryAnchor | null = null;
let historyReturnAnchor: HistoryAnchor | null = null;
let requestedHistoryAnchor: HistoryAnchor | null = null;
let initialUnreadPosition: number | null = null;
let historyWindowAfter: string | null = null;
// Belongs to the loaded message window, not the independently refreshed sidebar detail.
let historyWindowAtTail = false;
let historyHasEarlier = false;
let newerHistoryPending: number | null = null;
let historyResizeObserver: ResizeObserver | null = null;
let historyRenderCleanup: (() => void) | null = null;

function captureHistoryAnchor(history: HTMLElement, conversationId: string): HistoryAnchor {
  const box = history.getBoundingClientRect();
  const first = [...history.querySelectorAll<HTMLElement>('.message[data-message-id]')]
    .find(node => node.getBoundingClientRect().bottom > box.top + 1 && node.getBoundingClientRect().top < box.bottom);
  return { conversationId, atBottom: history.dataset.atTail === 'true' && history.scrollHeight-history.scrollTop-history.clientHeight < 48,
    scrollTop: history.scrollTop, messageId: first?.dataset.messageId,
    offset: first ? (first.getBoundingClientRect().top-box.top)/pageZoom() : undefined };
}
function restoreHistoryAnchor(history: HTMLElement, anchor: HistoryAnchor): void {
  const node = anchor.messageId ? [...history.querySelectorAll<HTMLElement>('.message')].find(item => item.dataset.messageId === anchor.messageId) : null;
  if (node && anchor.offset !== undefined) history.scrollTop += (node.getBoundingClientRect().top-history.getBoundingClientRect().top)/pageZoom()-anchor.offset;
  else history.scrollTop = anchor.scrollTop;
}
// A background refresh preserves the reader's bounded context, including a window
// whose first unread message is far before the latest100. It does not force a jump.
async function refreshVisibleHistory(conversationId: string): Promise<void> {
  const history = root.querySelector<HTMLElement>('#messages');
  const anchor = history?.dataset.conversationId === conversationId ? captureHistoryAnchor(history, conversationId) : null;
  if (anchor?.atBottom) { contextTargetId = null; historyWindowAfter = null; }
  const target = anchor?.atBottom ? undefined : contextTargetId ?? anchor?.messageId;
  await loadMessages(conversationId, target, target || anchor?.atBottom ? undefined : historyWindowAfter ?? undefined);
}
async function jumpHistory(latest: boolean): Promise<void> {
  if (!current) return;
  const valid = selectionGuard(); const id = current.id;
  const history = root.querySelector<HTMLElement>('#messages');
  const returning = historyReturnAnchor?.conversationId === id ? historyReturnAnchor : null;
  if (!latest && !returning?.messageId) return;
  if (latest && history) {
    const reading = captureHistoryAnchor(history, id);
    historyReturnAnchor = reading.messageId && !reading.atBottom ? reading : null;
  }
  contextTargetId = null; historyWindowAfter = null;
  await loadingStatus.run('Загружаем сообщения…', valid, () => loadMessages(id, latest ? undefined : returning!.messageId));
  if (!valid()) return;
  requestedHistoryAnchor = latest ? { conversationId:id, atBottom:true, scrollTop:0 } : returning;
  if (!latest) historyReturnAnchor = null;
  initialUnreadPosition = null; renderShell();
}
// Where the "Новые сообщения" line goes: the reader's own last_read_seq as it was when this visit
// started. Frozen for the visit -- messages arriving while the chat is open stay below the line,
// and the read receipt this visit sends must not move it.
let unreadBoundary: { generation: number; seq: bigint; tail: bigint | null } | null = null;
// Switching back to a recently open conversation must not look like a cold start: its history,
// read cursors and detail are kept in memory and rendered immediately, while the usual
// authorization + snapshot round trips run in the background and replace them (mountShell keeps
// the DOM when the refresh matches, so the swap is invisible). Never a substitute for an access
// check: an entry is bound to the account, dropped after CONVERSATION_CACHE_TTL_MS and discarded
// the moment the server says the conversation is gone or this client is removed from it.
const CONVERSATION_CACHE_TTL_MS = 300_000;
const CONVERSATION_CACHE_LIMIT = 12;
type ConversationCacheEntry = {
  account: Bootstrap; session: number; at: number;
  messages: Message[]; readCursors: ReadCursor[]; details: ConversationDetail; cursor: string;
  scrollTop: number | null; atBottom: boolean; atTail: boolean; hasEarlier: boolean;
  images: HTMLImageElement[];
};
const conversationCache = new Map<string, ConversationCacheEntry>();
function rememberConversation(): void {
  if (!bootstrap || !current || !currentDetails || currentDetails.id !== current.id) return;
  if (historyAccess?.id !== current.id || historyAccess.membership_epoch_id !== currentDetails.membership_epoch_id
    || historyAccess.visible_from_seq !== currentDetails.visible_from_seq) return;
  const cursor = revisionCursors.get(current.id);
  // Never freeze an incomplete view: a message still being sent, or a quote whose source failed
  // or has not been resolved yet, must be re-fetched on the next open rather than shown again.
  const unsettled = (message: Message): boolean => Boolean(message.pending)
    || Boolean(message.reply_to_message_id) && (!message.replySource || message.replySource.state === 'error');
  if (cursor === undefined || messages.some(unsettled)) return;
  const history = root.querySelector<HTMLElement>('#messages');
  conversationCache.delete(current.id);
  conversationCache.set(current.id, {
    account: bootstrap, session: sessionGeneration, at: Date.now(),
    messages: [...messages], readCursors: [...readCursors], details: currentDetails, cursor: String(cursor),
    images: [...root.querySelectorAll<HTMLImageElement>('#messages img.attachment-preview, #messages img.reply-image')].filter(image => image.complete && image.naturalWidth > 0),
    scrollTop: history ? history.scrollTop : null,
    atBottom: history ? captureHistoryAnchor(history, current.id).atBottom : historyWindowAtTail,
    atTail: historyWindowAtTail, hasEarlier: historyHasEarlier,
  });
  while (conversationCache.size > CONVERSATION_CACHE_LIMIT) conversationCache.delete(conversationCache.keys().next().value as string);
  // Keep only a bounded set of decoded previews, newest chats first. No blobs or disk cache.
  let bytes = 16 * 1024 * 1024; let count = 32;
  for (const entry of [...conversationCache.values()].reverse()) entry.images = entry.images.filter(image => {
    const size = image.naturalWidth * image.naturalHeight * 4;
    if (count === 0 || size > bytes) return false;
    bytes -= size; count -= 1; return true;
  });
}
function cachedConversation(id: string): ConversationCacheEntry | null {
  const entry = conversationCache.get(id);
  if (!entry) return null;
  if (entry.account !== bootstrap || entry.session !== sessionGeneration || Date.now() - entry.at > CONVERSATION_CACHE_TTL_MS) {
    conversationCache.delete(id); return null;
  }
  return entry;
}
function forgetConversation(id: string): void { conversationCache.delete(id); }
// A new img.src can trigger private-image revalidation/decoding over LTE. Reuse an
// already-decoded element only within the existing account/TTL/ACL-bounded chat cache.
// The surrounding message and its event ownership are always rebuilt for this selection.
function attachmentImage(attachment: Attachment, className: 'attachment-preview' | 'reply-image' | 'attachment-preview sticker-preview'): HTMLImageElement {
  const src = attachmentPath(attachment,className.includes('sticker-preview')?'download':'preview',bootstrap?.managed_files_enabled===true);
  if(!src){
    const image=element('img',className);image.alt=attachment.name;
    if(attachment.managed&&bootstrap?.managed_files_enabled)watchManagedAttachment(attachment,image,ready=>{const path=attachmentPath(ready,'preview',true);if(path)image.src=path;});
    return image;
  }
  const images = current ? cachedConversation(current.id)?.images : undefined;
  const index = images?.findIndex(image => !image.isConnected && image.className === className && image.getAttribute('src') === src) ?? -1;
  const image = index >= 0 ? images!.splice(index, 1)[0]! : element('img', className);
  if (image.getAttribute('src') !== src) image.src = src;
  image.alt = attachment.name;
  return image;
}
const managedAttachmentLoads=new Set<AbortController>();
function clearManagedAttachmentLoads(){for(const controller of managedAttachmentLoads)controller.abort();managedAttachmentLoads.clear();}
function watchManagedAttachment(attachment:Attachment,owner:HTMLElement,ready:(value:ChatAttachment)=>void,failed?:(error:unknown)=>void){
 if(!bootstrap?.managed_files_enabled)return;
 const valid=selectionGuard(),controller=new AbortController();managedAttachmentLoads.add(controller);
 queueMicrotask(()=>{
  if(!valid()||!owner.isConnected){managedAttachmentLoads.delete(controller);return;}
  void waitForManagedAttachment(attachment,api,controller.signal,()=>valid()&&owner.isConnected).then(value=>{
   if(!controller.signal.aborted&&valid()&&owner.isConnected){
    Object.assign(attachment,value);
    for(const message of messages)for(const candidate of [...message.attachments??[],...message.replySource?.images??[],...message.reply_source?.images??[]])if(candidate.id===value.id&&candidate.managed?.roomId===value.managed?.roomId&&candidate.managed?.membershipEpoch===value.managed?.membershipEpoch)Object.assign(candidate,value);
    ready(value);
   }
  },error=>{if(!controller.signal.aborted&&valid()&&owner.isConnected)failed?.(error);}).finally(()=>managedAttachmentLoads.delete(controller));
 });
}
function messageAttachmentNodes(attachment:Attachment,message:Message):HTMLElement[]{
 if(attachment.upload){
  const label=element('span','attachment-link',`${attachment.name} · ${Math.ceil(attachment.size/1024)} КБ`),preview=attachmentPreviews.get(attachment.id);
  if(!preview||!isAttachmentImage(attachment.mime))return [label];
  const image=element('img','attachment-preview');image.src=preview;image.alt=attachment.name;image.width=420;image.height=280;return [label,image];
 }
 if(attachment.managed&&(!bootstrap?.managed_files_enabled||attachment.managed.state!=='bound')){
  const pending=element('div','attachment-pending');pending.append(element('span','attachment-link',`${attachment.name} · ${Math.ceil(attachment.size/1024)} КБ`));
  const status=element('span','attachment-pending-status',bootstrap?.managed_files_enabled?'Готовим файл…':'Файл временно недоступен');status.setAttribute('role','status');
  if(isAttachmentImage(attachment.mime)||attachment.mime==='video/mp4'){status.classList.add('attachment-pending-preview');if(attachment.mime==='video/mp4')status.classList.add('attachment-pending-video');}
  pending.append(status);
  if(bootstrap?.managed_files_enabled){
   const load=()=>{status.textContent='Готовим файл…';watchManagedAttachment(attachment,pending,value=>pending.replaceWith(...messageAttachmentNodes(value,message)),()=>{
    status.textContent='Не удалось подготовить файл. ';const retry=element('button','ghost','Повторить');retry.type='button';retry.onclick=load;status.append(retry);
   });};load();
  }
  return [pending];
 }
 const result:HTMLElement[]=[],isSticker=isStickerAttachment(attachment,message.entities);
 const link=element('a','attachment-link',`${attachment.name} · ${Math.ceil(attachment.size/1024)} КБ`),download=attachmentPath(attachment,'download',bootstrap?.managed_files_enabled===true);
 if(download)link.href=download;link.rel='nofollow noopener';link.target='_blank';link.download=attachment.name;if(!isSticker)result.push(link);
 if(isAttachmentImage(attachment.mime)){
  const preview=attachmentImage(attachment,isSticker?'attachment-preview sticker-preview':'attachment-preview');preview.width=420;preview.height=280;preview.loading='lazy';preview.decoding='async';preview.tabIndex=0;preview.setAttribute('role','button');preview.setAttribute('aria-label',`Открыть ${attachment.name}`);preview.onclick=()=>openImageViewer(attachment);preview.onkeydown=event=>{if(event.key==='Enter'||event.key===' '){event.preventDefault();openImageViewer(attachment);}};result.push(preview);
 }else if(attachment.mime==='audio/mpeg'){
  const audio=element('audio','attachment-audio');audio.controls=true;audio.preload='none';
  if(download)audio.src=download;audio.setAttribute('aria-label',attachment.name);result.push(audio);
 }else if(attachment.mime==='video/webm'||attachment.mime==='video/mp4'){
  const video=element('video','attachment-preview');video.controls=true;video.preload='metadata';video.playsInline=true;
  const path=attachmentPath(attachment,attachment.managed?'preview':'download',bootstrap?.managed_files_enabled===true);if(path)video.src=path;video.setAttribute('aria-label',attachment.name);result.push(video);
 }
 return result;
}

let sidebarCurrentValidation: { generation: number; error?: string; startedAt: number } | null = null;
// Below this, a background revalidation (any live WS event routes through loadConversations()
// with revalidatesCurrent) must never blank the already-loaded message history: on a normal
// fast round trip it would resolve before the placeholder could mean anything to the user.
const SIDEBAR_VALIDATION_REVEAL_MS = 300;
let sidebarValidationFocus: { generation: number; start: number | null; end: number | null } | null = null;
let mobileListVisible = false;
let refreshAfterNavigation = false;
let lastReadSent: { conversationId: string; seq: string; session: number; epoch: string | null } | null = null;
function accountEpoch(user: Bootstrap['user']): string {
  try { return localStorage.getItem(`pm-chat:logout:${user.workspace_id}:${user.id}`) ?? ''; } catch { return 'storage-unavailable'; }
}
function sessionGuard(): () => boolean {
  const account = bootstrap; const generation = sessionGeneration;
  const epoch = account ? accountEpoch(account.user) : '';
  return () => Boolean(!logoutFence.read() && account && bootstrap === account && generation === sessionGeneration && epoch === accountEpoch(account.user));
}

function sidebarKey(account: Bootstrap, session = sessionGeneration): string {
  return `${account.user.workspace_id}\u0000${account.user.id}\u0000${session}\u0000${accountEpoch(account.user)}`;
}

function sidebarOwnerValid(owner: SidebarOwner): boolean {
  return !logoutFence.read() && sidebarOwner === owner && bootstrap === owner.account && sessionGeneration === owner.session && accountEpoch(owner.account.user) === owner.epoch;
}

function staleSidebarRequest(signal?: AbortSignal | null): never {
  signal?.throwIfAborted();
  throw new DOMException('Obsolete conversation owner', 'AbortError');
}

const conversationRequest: ConversationRequest = async <T>(accountKey: string, path: string, options: RequestInit = {}): Promise<T> => {
  const owner = sidebarOwner;
  if (!owner || owner.key !== accountKey || !sidebarOwnerValid(owner)) staleSidebarRequest(options.signal);
  options.signal?.throwIfAborted();
  const headers = new Headers(options.headers);
  if (options.body && !(options.body instanceof Blob) && !(options.body instanceof ArrayBuffer) && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  if (!['GET', 'HEAD'].includes(options.method ?? 'GET')) headers.set('X-CSRF-Token', owner.csrf);
  const measurement=connectionStatus.begin(path,options.signal);
  try {
    const response = await fetch(path, { ...options, headers, credentials: 'same-origin', signal: options.signal });
    measurement.headers(response);
    options.signal?.throwIfAborted();
    if (!sidebarOwnerValid(owner)) staleSidebarRequest(options.signal);
    const payload = await readApiResponse<T>(response);
    options.signal?.throwIfAborted();
    if (!sidebarOwnerValid(owner)) staleSidebarRequest(options.signal);
    measurement.finish();return payload as T;
  } catch (error) {
    measurement.finish(error);
    options.signal?.throwIfAborted();
    if (!sidebarOwnerValid(owner)) staleSidebarRequest(options.signal);
    throw error;
  }
};

// Exactly one bounded controller exists for the whole application lifecycle.
const conversationPages = new ConversationPages(conversationRequest, {
  limit: 50, maxRecords: 200,
  onDenied(id) {
    filesCards.forgetConversation(id);pmTaskPreviews.forgetConversation(id);
    forgetConversation(id);
    conversations = conversationPages.snapshot().rows;
    if (current?.id !== id) return;
    // Invalidate selection-bound continuations before a late response can restore the refused view.
    clearConversationSelection();
    renderShell(); toast('Доступ к чату закрыт');
  },
});

// UI continuations are bound to both the login epoch and the selection, including A→B→A.
function selectionGuard(): () => boolean {
  const valid = sessionGuard(); const selection = selectionGeneration; const id = current?.id;
  return () => valid() && selection === selectionGeneration && id === current?.id;
}

// Only obsolete actions are silently discarded. Current-context failures remain visible.
class StaleUiAction extends Error {}
async function runUiAction(action: (request: typeof api, valid: () => boolean, prompt: typeof window.prompt, confirm: typeof window.confirm) => Promise<void>): Promise<void> {
  const valid = selectionGuard();
  const check = (): void => { if (!valid()) throw new StaleUiAction(); };
  const request = async <T>(path: string, options?: RequestInit & {onUploadProgress?:(progress:UploadTransfer)=>void}): Promise<T> => {
    check();
    try { const result = await api<T>(path, options); check(); return result; }
    catch (error) { check(); throw error; }
  };
  const ask: typeof window.prompt = (...args) => { check(); const answer = window.prompt(...args); check(); return answer; };
  const confirm: typeof window.confirm = (...args) => { check(); const answer = window.confirm(...args); check(); return answer; };
  try { check(); await action(request, valid, ask, confirm); }
  catch (error) { if (valid() && !(error instanceof StaleUiAction)) toast(error instanceof Error ? error.message : 'Ошибка действия'); }
}

let currentDetails: ConversationDetails | null = null;
// Message attribution in a shared room is its public participant alias. Queue
// ownership and HOME credentials remain bound to the original account above.
function ownConversationAuthorId(conversationId:string):string|null {
  if (!bootstrap || current?.id !== conversationId) return null;
  if (currentDetails?.id === conversationId && currentDetails.shared_room) return currentDetails.own_participant_id ?? null;
  return bootstrap.user.id;
}

let currentMembers: Member[] = [];
type ReadCursor = { id: string; display_name: string; avatar_key: string | null; last_read_seq: string };
let readCursors: ReadCursor[] = [];
let chatInfoOpen = false;
let chatInfoMembersLoading = false;
let chatInfoAvailableUsers: { id: string; display_name: string }[] | null = null;
let conversationPins: Message[] | null = null;
let pinnedBarPage = 0;
type Scheduled = { id: string; conversation_id: string; body: string; send_at: string; state: string; last_error: string | null };
let scheduledMessages: Scheduled[] | null = null;
type PeerProfile = { id: string; display_name: string; avatar_key: string | null; status_text: string | null; email: string | null; last_seen_at: string | null; online: boolean; is_bot?: boolean };
let chatInfoPeer: PeerProfile | null = null;   // the other person of the open direct chat
let chatInfoAddUserId = '';
// Right-panel member list view: role filter and name filter (reset when another chat opens).
let chatInfoMemberFilter: 'all' | 'admins' = 'all';
let chatInfoMemberQuery = '';
let messages: Message[] = [];
let socket: WebSocket | null = null;
let connectionLabel = 'Подключение…';
let typingSentAt = 0;
// Who is typing where: conversationId → userId → expiry timer. Kept for every chat (the sidebar
// shows it too), cleared only on logout; the server also sends an explicit stop after 7 s.
const typingByConversation = new Map<string, Map<string, number>>();
function setTyping(conversationId: string, userId: string, active: boolean): void {
  const users = typingByConversation.get(conversationId) ?? new Map<string, number>();
  const timer = users.get(userId); if (timer !== undefined) window.clearTimeout(timer);
  if (active) users.set(userId, window.setTimeout(() => setTyping(conversationId, userId, false), 8000));
  else users.delete(userId);
  if (users.size) typingByConversation.set(conversationId, users); else typingByConversation.delete(conversationId);
  renderShell();
}
function clearAllTyping(): void {
  for (const users of typingByConversation.values()) for (const timer of users.values()) window.clearTimeout(timer);
  typingByConversation.clear();
}
// Best-effort display name of a member, from what this client already holds (no request).
function userNameInConversation(conversationId: string, userId: string): string | null {
  if (userId === bootstrap?.user.id) return 'Вы';
  const direct = conversations.find((item) => item.id === conversationId && item.type === 'direct');
  if (direct) return direct.display_title;
  if (current?.id === conversationId) {
    return messages.find((item) => item.sender?.id === userId)?.sender?.name
      ?? currentMembers.find((member) => member.id === userId)?.display_name
      ?? readCursors.find((reader) => reader.id === userId)?.display_name ?? null;
  }
  return null;
}
// "Борис печатает…", "Борис и Анна печатают…", "Борис и ещё 2 печатают…"; null when nobody is.
function typingLabel(conversationId: string): string | null {
  const users = typingByConversation.get(conversationId); if (!users?.size) return null;
  const names = [...users.keys()].map((id) => userNameInConversation(conversationId, id));
  const known = names.filter((name): name is string => Boolean(name));
  if (!known.length) return names.length > 1 ? 'Несколько человек печатают…' : 'Кто-то печатает…';
  if (names.length === 1) return `${known[0]} печатает…`;
  if (names.length === 2 && known.length === 2) return `${known[0]} и ${known[1]} печатают…`;
  return `${known[0]} и ещё ${names.length - 1} печатают…`;
}
let reconnectEnabled = true;
let reconnectTimer: number | null = null;
let reconnectDelay = 1500;
let realtimeSettleTimer: number | null = null;
let realtimeGeneration = 0;
let contextTargetId: string | null = null;
// Scoped to a single navigation (selectionGeneration + target pair): once the initial jump
// to a quoted/permalinked message has scrolled it into view, a later background refresh
// (e.g. catchUp() re-fetching the same context window while contextTargetId is still set)
// must not force the reader back to it again after they have already scrolled away to read
// more -- only the very first render of that navigation performs the scroll.
let revealedTargetKey: string | null = null;
const revisionCursors = new Map<string, bigint>();
const bufferedEvents = new Map<string, RealtimeEvent[]>();
const snapshotLoading = new Map<string, number>();
const catchups = new Map<string, Promise<void>>();
let catchupController: AbortController | null = null;
let pendingCurrentRevision: bigint | null = null;
let heartbeatTimer: number | null = null;
let foregroundPollTimer: number | null = null;
let replyToMessageId: string | null = null;
let composerReplyTarget: Message | null = null;
let composerMentionIds: string[] = [];
// @mentions: "@Name" token inserted in the text → the user ids it stands for ("@all" → every member).
// On send only tokens still present in the text count, so deleting "@Имя" un-mentions that person.
const composerMentionTokens = new Map<string, string[]>();
type MentionCandidate = { id: string; display_name: string };
let mentionCandidates: { conversationId: string; users: MentionCandidate[] } | null = null;
let mentionPicker: { start: number; query: string; active: number } | null = null;
let composerAttachmentIds: string[] = [];
const composerStickerAttachmentIds = new Set<string>();
let composerAttachments: Attachment[] = [];
// Files still uploading (shown with transfer progress) and local previews of attached images.
let composerUploads: ComposerUpload[] = [];
const uploadControllers=new Map<string,AbortController>();
const attachmentPreviews = new Map<string, string>();

function deviceId(): string {
  const key = 'pm-chat:device-id';
  let id = localStorage.getItem(key);
  if (!id) { id = crypto.randomUUID(); localStorage.setItem(key, id); }
  return id;
}

function element<K extends keyof HTMLElementTagNameMap>(name: K, className?: string, text?: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(name);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

async function api<T>(path: string, options: RequestInit & {onUploadProgress?:(progress:UploadTransfer)=>void} = {}): Promise<T> {
  const headers = new Headers(options.headers);
  if (options.body && !(options.body instanceof Blob) && !(options.body instanceof ArrayBuffer) && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  if (bootstrap && !['GET', 'HEAD'].includes(options.method ?? 'GET')) headers.set('X-CSRF-Token', bootstrap.csrf_token);
  const measurement=connectionStatus.begin(path,options.signal);
  try {
    const response = options.onUploadProgress&&options.body instanceof Blob
      ? await uploadRequest(path,options.body,headers,options.onUploadProgress,options.signal)
      : await fetch(path, { ...options, headers, credentials: 'same-origin' });
    measurement.headers(response);const value=await readApiResponse<T>(response);measurement.finish();return value;
  } catch(error){measurement.finish(error);throw error;}
}

function toast(message: string): void {
  const node = element('div', 'toast', message);
  root.append(node);
  window.setTimeout(() => node.remove(), 2400);
}

function faviconLink(): HTMLLinkElement {
  return document.querySelector('link[rel="icon"]')
    ?? (() => { const link = document.createElement('link'); link.rel = 'icon'; document.head.append(link); return link; })();
}
let faviconShown: string | null = null;
const faviconBadgeCache = new Map<string, string>();
function showFavicon(href: string, type: string): void {
  // Re-assigning the same href still makes the browser reload the icon; renderShell() calls this
  // on every render, so only touch the <link> when the picture actually changes.
  if (faviconShown === href) return;
  const link = faviconLink(); link.href = href; link.type = type; faviconShown = href;
}
// Unread: the number takes the whole tile instead of sitting in a disc on top of the mark. A disc
// is about 40% of the tile, so in a 16-pixel favicon its digit comes out three or four pixels high
// and cannot be read at all; filling the tile gives the digit nearly the full height, which is
// legible at 16 and comfortable at 32. The mark steps aside while there is something unread -- the
// number is what the tab is for. Drawn in the mark's own 512x512 coordinates and scaled once, so
// these figures are the ones in web/icon.svg and cannot drift away from it.
function updateFaviconBadge(count: number): void {
  if (count <= 0) { showFavicon('/icon.svg', 'image/svg+xml'); return; }
  const label = count > 99 ? '99+' : String(count);
  const cached = faviconBadgeCache.get(label);
  if (cached) { showFavicon(cached, 'image/png'); return; }
  // 64, not 32: browsers ask for 16 or 32 and downscale, and downscaling from 64 looks markedly
  // better. Larger buys nothing, the tab is still 16 pixels.
  const size = 64;
  const canvas = document.createElement('canvas'); canvas.width = size; canvas.height = size;
  const ctx = canvas.getContext('2d'); if (!ctx) return;
  ctx.scale(size / 512, size / 512);
  ctx.fillStyle = '#c23b30';
  // roundRect is recent enough that an older browser must still get a tile rather than nothing.
  if (typeof ctx.roundRect === 'function') { ctx.beginPath(); ctx.roundRect(0, 0, 512, 512, 108); ctx.fill(); }
  else ctx.fillRect(0, 0, 512, 512);
  // Sized by the room the characters need side to side, not by height: one digit can be huge,
  // "99+" cannot.
  const fontSize = label.length === 1 ? 460 : label.length === 2 ? 380 : 250;
  ctx.fillStyle = '#ffffff';
  ctx.font = `700 ${fontSize}px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`;
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  // Digits sit a little above the middle of the em box, so nudge down proportionally.
  ctx.fillText(label, 256, 256 + fontSize * 0.035);
  const href = canvas.toDataURL('image/png');
  faviconBadgeCache.set(label, href); showFavicon(href, 'image/png');
}

// Incoming-message click: per account, on by default, toggled in the profile dialog. A device
// preference, so localStorage (a failure just means the default).
function enterSends(): boolean {
  if (!bootstrap) return true;
  try { return localStorage.getItem(`pm-chat:enter-sends:${bootstrap.user.workspace_id}:${bootstrap.user.id}`) !== 'off'; } catch { return true; }
}

function soundKey(user: Bootstrap['user']): string { return `pm-chat:sound:${user.workspace_id}:${user.id}`; }
function incomingSoundEnabled(): boolean {
  if (!bootstrap) return false;
  try { return localStorage.getItem(soundKey(bootstrap.user)) !== 'off'; } catch { return true; }
}
function setIncomingSound(enabled: boolean): void {
  if (!bootstrap) return;
  try { localStorage.setItem(soundKey(bootstrap.user), enabled ? 'on' : 'off'); } catch { /* keep default */ }
}
// A file from our own origin, not a `data:` URI. The data URI is what broke the sound for everyone:
// the page's CSP has no `media-src`, so media falls back to `default-src 'self'`, a data: source is
// refused, and the element reports it as "no supported source was found" -- which the old
// `.catch(() => undefined)` swallowed. A same-origin file needs no CSP exception at all.
// Still an <audio> element and not Web Audio: constructing an AudioContext blocks the main thread
// until the output device is open (seconds on some systems), and this runs on the realtime path.
function soundPreferences() {
  return readSoundPreferences(key => localStorage.getItem(key), bootstrap ? soundKey(bootstrap.user) : 'pm-chat:sound:anonymous');
}
let incomingSoundUrl = '';
let audioVolumeSupported: boolean | null = null;
let incomingSound: HTMLAudioElement | null = null;
let lastSoundAt = 0;
let soundAttemptVersion = 0;
// Why the last attempt did or did not make a noise. Without this the only symptom of a refusal is
// silence, indistinguishable from "the sound is switched off".
let lastSoundOutcome: { ok: boolean; error: string; at: number } | null = null;
let soundHintDismissed = false;

// Created and fetched once, on the first interaction rather than on the realtime path: the element
// and its bytes are then ready long before a message arrives.
function primeIncomingSound(): void { soundElement(); }

function soundElement(): HTMLAudioElement | null {
  const preference = soundPreferences();
  const baseUrl = MESSAGE_SOUNDS.find(sound => sound.id === preference.tone)!.url;
  try {
    // iOS may leave media.volume fixed at 1. Use quieter PCM files on those devices.
    if (audioVolumeSupported === null) { const probe = new Audio(); probe.volume = 0.5; audioVolumeSupported = probe.volume === 0.5; }
    let url = !audioVolumeSupported && preference.volume > 0 && preference.volume < 3
      ? baseUrl.replace('.wav', `-level-${preference.volume}.wav`) : baseUrl;
    // Audio has a one-hour HTTP cache: revision also refreshes the quieter iOS variants.
    if (preference.tone === 'coin') url += '?v=damped-1';
    if (preference.tone === 'kitten') url += '?v=short-1';
    if (incomingSound && incomingSoundUrl !== url) { incomingSound.pause(); incomingSound = null; }
    if (!incomingSound) {
      incomingSound = new Audio(url); incomingSound.preload = 'auto'; incomingSoundUrl = url;
    }
    incomingSound.volume = audioVolumeSupported ? SOUND_LEVELS[preference.volume] : 1;
    return incomingSound;
  } catch { return null; }
}

/** Plays the incoming-message sound and reports honestly what happened. `force` is for the
 *  "Проверить звук" button: it plays even when the throttle would have skipped it. */
async function playIncomingSound(force = false): Promise<{ ok: boolean; error: string }> {
  const now = Date.now();
  if (soundPreferences().volume === 0) {
    soundAttemptVersion += 1; incomingSound?.pause();
    lastSoundOutcome = { ok: true, error: 'Muted', at: now };
    return { ok: true, error: 'Muted' };
  }
  if (!force && now - lastSoundAt < 400) return { ok: true, error: '' };
  lastSoundAt = now;
  const version = ++soundAttemptVersion;
  const audio = soundElement();
  if (!audio) { lastSoundOutcome = { ok: false, error: 'NoAudio', at: now }; return { ok: false, error: 'NoAudio' }; }
  try {
    audio.currentTime = 0;
    const volume = audio.volume;
    await audio.play();
    if (version !== soundAttemptVersion || incomingSound !== audio || audio.volume !== volume) return { ok: true, error: '' };
    lastSoundOutcome = { ok: true, error: '', at: now };
    return { ok: true, error: '' };
  } catch (error) {
    if (version !== soundAttemptVersion || incomingSound !== audio || soundPreferences().volume === 0) return { ok: true, error: '' };
    const name = error instanceof Error ? error.name || 'Error' : 'Error';
    lastSoundOutcome = { ok: false, error: name, at: now };
    return { ok: false, error: name };
  }
}

/** What to tell the person about the last attempt. */
function soundOutcomeText(outcome: { ok: boolean; error: string }): string {
  if (outcome.error === 'Muted') return 'Громкость 0 — звук выключен.';
  if (outcome.ok) return 'Звук воспроизведён.';
  if (outcome.error === 'NotAllowedError') return 'Браузер заблокировал звук: нажмите «Включить звук» или разрешите звук для этого сайта.';
  if (outcome.error === 'NotSupportedError') return 'Браузер не смог загрузить звук — сообщите нам об этом.';
  if (outcome.error === 'NoAudio') return 'В этом браузере звук недоступен.';
  return `Браузер не проиграл звук (${outcome.error}).`;
}

/** Only a refusal the person can lift by clicking is worth a banner. */
function soundBlockedByGesture(): boolean {
  return incomingSoundEnabled() && soundPreferences().volume > 0 && lastSoundOutcome?.ok === false && lastSoundOutcome.error === 'NotAllowedError' && !soundHintDismissed;
}

const pendingSoundChecks = new Set<string>();
async function playMessageSound(message: RealtimeEvent): Promise<void> {
  const id = message.payload?.object_id;
  if (!id || !incomingSoundEnabled() || soundPreferences().volume === 0 || pendingSoundChecks.has(id) || pendingSoundChecks.size >= 4) return;
  if (current?.id === message.conversation_id && !mobileListVisible && tabAttended()) return;
  const valid = sessionGuard(); pendingSoundChecks.add(id);
  try {
    const state = await api<{ notify: boolean }>(`/v1/messages/${encodeURIComponent(id)}/notification-state`);
    if (!valid() || !state.notify || (current?.id === message.conversation_id && !mobileListVisible && tabAttended())) return;
    playIncomingClick();
  } catch { /* No sound without a current, authorized notification decision. */ }
  finally { pendingSoundChecks.delete(id); }
}

function playIncomingClick(): void {
  if (!incomingSoundEnabled()) return;
  // Nothing is rendered from here. A render on the realtime path -- even a conditional one -- lands
  // in the middle of the handler that is still applying the message, and left the open chat empty.
  // The refusal is remembered instead, and the message's own render puts the banner up a moment
  // later, which is soon enough for something the person reads, not reacts to.
  void playIncomingSound();
}

// Title/favicon blink for eligible unread notifications when the tab is not attended.
let titleBase = 'Chat Ostart';
let notificationTotal = 0;
let blinkTimer: number | null = null;
let blinkOn = false;
function tabAttended(): boolean { return document.visibilityState === 'visible' && document.hasFocus(); }
function backgroundPushPreferenceKey(): string | null {
  return bootstrap ? `pm-chat:background-push:${bootstrap.user.workspace_id}:${bootstrap.user.id}` : null;
}
function suppressBackgroundPush(): boolean {
  const key = backgroundPushPreferenceKey();
  try { return key !== null && localStorage.getItem(key) === 'on'; } catch { return false; }
}

// The installed app and tab share the authoritative notification count.
// Unsupported browsers simply have no badge; a rejected promise is never a user-facing failure.
function updateAppBadge(count: number): void {
  const badging = navigator as Navigator & { setAppBadge?: (n?: number) => Promise<void>; clearAppBadge?: () => Promise<void> };
  try {
    if (count > 0) void badging.setAppBadge?.(count).catch(() => undefined);
    else void badging.clearAppBadge?.().catch(() => undefined);
  } catch { /* badging unavailable */ }
}
function applyTitle(): void {
  const alert = blinkTimer !== null && blinkOn;
  document.title = alert ? `✉ Новое сообщение · ${titleBase}` : titleBase;
  updateFaviconBadge(alert || blinkTimer === null ? notificationTotal : 0);
  updateAppBadge(notificationTotal);
}
function syncBlink(): void {
  const shouldBlink = notificationTotal > 0 && !tabAttended();
  if (shouldBlink && blinkTimer === null) {
    blinkOn = true;
    blinkTimer = window.setInterval(() => { blinkOn = !blinkOn; applyTitle(); }, 1000);
  } else if (!shouldBlink && blinkTimer !== null) {
    window.clearInterval(blinkTimer); blinkTimer = null; blinkOn = false;
  }
  applyTitle();
}
for (const type of ['focus', 'blur', 'visibilitychange']) (type === 'visibilitychange' ? document : window).addEventListener(type, () => syncBlink());

function resetTitleAndFavicon(): void {
  titleBase = 'Chat Ostart'; notificationTotal = 0; syncBlink();
}
function updateTitleAndFavicon(): void {
  notificationTotal = conversationPages.snapshot().notificationTotal;
  const parts: string[] = [];
  if (notificationTotal > 0) parts.push(`(${notificationTotal > 99 ? '99+' : notificationTotal})`);
  if (current?.display_title) parts.push(current.display_title);
  parts.push('Chat Ostart');
  titleBase = parts.join(' · ');
  syncBlink();
}

function initials(name: string): string {
  return name.split(/\s+/).slice(0, 2).map((part) => part[0] ?? '').join('').toUpperCase();
}

// A line drawing next to every menu item, so a menu is scanned by shape instead of read word by
// word. The paths are constants in this file -- the only thing that ever comes from outside is the
// label, and it is appended as text, never as markup.
const MENU_ICONS: Record<string, string> = {
  smile: '<circle cx="12" cy="12" r="9"/><path d="M8 14a4 4 0 0 0 8 0M8 9h.01M16 9h.01"/>',
  bug: '<path d="M8 2l1.9 1.9M16 2l-1.9 1.9M9 7.1V6a3 3 0 0 1 6 0v1.1"/><path d="M12 20c-3.3 0-6-2.7-6-6v-3a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v3c0 3.3-2.7 6-6 6Z"/><path d="M12 20v-9M6.5 9C4.6 8.8 3 7.1 3 5M6 13H2M3 21c0-2.1 1.7-3.9 3.8-4M20.97 5c0 2.1-1.6 3.8-3.5 4M22 13h-4M17.2 17c2.1.1 3.8 1.9 3.8 4"/>',
  link: '<path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.5 1.5"/><path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7L12 19"/>',
  reply: '<path d="M9 17 4 12l5-5"/><path d="M20 18v-2a4 4 0 0 0-4-4H4"/>',
  forward: '<path d="m15 17 5-5-5-5"/><path d="M4 18v-2a4 4 0 0 1 4-4h12"/>',
  pin: '<path d="M12 17v5"/><path d="M9 10.8V4h6v6.8l3 3.2H6l3-3.2Z"/>',
  eye: '<path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/>',
  pencil: '<path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"/>',
  trash: '<path d="M3 6h18"/><path d="M8 6V4h8v2"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/>',
  check: '<path d="m1 13 4 4 8-8"/><path d="m11 15 2 2 10-10"/>',
  bell: '<path d="M18 8a6 6 0 1 0-12 0c0 7-3 8-3 8h18s-3-1-3-8"/><path d="M13.7 21a2 2 0 0 1-3.4 0"/>',
  download: '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="m7 10 5 5 5-5"/><path d="M12 15V3"/>',
  refresh: '<path d="M21 12a9 9 0 1 1-3-6.7L21 8"/><path d="M21 3v5h-5"/>',
  sliders: '<path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3"/><path d="M1 14h6M9 8h6M17 16h6"/>',
  star: '<path d="m12 3 2.9 5.9 6.5.9-4.7 4.6 1.1 6.5L12 17.8 6.2 20.9l1.1-6.5L2.6 9.8l6.5-.9L12 3Z"/>',
  bookmark: '<path d="M6 3h12v18l-6-4-6 4V3Z"/>',
  at: '<circle cx="12" cy="12" r="4"/><path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-4 8"/>',
  send: '<path d="m22 2-7 20-4-9-9-4 20-7Z"/><path d="M22 2 11 13"/>',
  paperclip: '<path d="M21 12.5 12.5 21a5.5 5.5 0 0 1-7.8-7.8l9-9a3.8 3.8 0 1 1 5.4 5.4l-9 9a2 2 0 0 1-2.8-2.8l8.3-8.3"/>',
  clock: '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3.5 2"/>',
  close: '<path d="m6 6 12 12M18 6 6 18"/>',
  eraser: '<circle cx="12" cy="12" r="9"/><path d="m9 9 6 6M15 9l-6 6"/>',
  shield: '<path d="M12 3l8 3v6c0 5-3.4 8.4-8 9.9C7.4 20.4 4 17 4 12V6l8-3Z"/><path d="m9 12 2 2 4-4"/>',
  userPlus: '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M19 8v6M22 11h-6"/>',
  userMinus: '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 11h-6"/>',
  crown: '<path d="m3 8 4 4 5-7 5 7 4-4v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8Z"/>',
  quote: '<path d="M10 11H5a1 1 0 0 1-1-1V7a3 3 0 0 1 3-3"/><path d="M10 11v3a4 4 0 0 1-4 4"/><path d="M21 11h-5a1 1 0 0 1-1-1V7a3 3 0 0 1 3-3"/><path d="M21 11v3a4 4 0 0 1-4 4"/>',
};

/** A menu item: icon, then label. The label is set as text, so a chat title or a person's name in
 *  it can never be markup. */
function menuItem(label: string, icon: keyof typeof MENU_ICONS, className = ''): HTMLButtonElement {
  const button = element('button', className) as HTMLButtonElement;
  button.type = 'button';
  button.innerHTML = `<svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${MENU_ICONS[icon]}</svg>`;
  button.append(document.createTextNode(label));
  return button;
}

function previewSenderName(name:string):string {
  const parts=name.trim().split(/\s+/u);return parts.length>1?`${parts[0]} ${Array.from(parts.at(-1)!)[0]}.`:parts[0]??'';
}

function avatarNode(name: string, avatarUrl: string | null, className = 'avatar'): HTMLElement {
  if (avatarUrl) { const img = element('img', className); img.src = avatarUrl; img.alt = ''; return img; }
  return element('span', className, initials(name));
}

function chatInfoAvatarUrl(id: string, avatarKey: string): string {
  return `/v1/conversations/${id}/avatar?v=${encodeURIComponent(avatarKey)}`;
}

function userAvatarUrl(id: string, avatarKey: string): string {
  return `/v1/users/${id}/avatar?v=${encodeURIComponent(avatarKey)}`;
}

// Directs show the peer's user image; groups retain their own conversation image.
function conversationAvatarUrl(row: Pick<ConversationRow, 'id' | 'type' | 'avatar_key' | 'peer_user_id' | 'peer_avatar_key'>): string | null {
  if (row.type === 'direct') return row.peer_user_id && row.peer_avatar_key ? userAvatarUrl(row.peer_user_id, row.peer_avatar_key) : null;
  return row.avatar_key ? chatInfoAvatarUrl(row.id, row.avatar_key) : null;
}

function formatConversationTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diffDays = Math.round((startOfDay(now) - startOfDay(date)) / 86_400_000);
  if (diffDays === 0) return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  if (diffDays === 1) return 'Вчера';
  if (diffDays > 1 && diffDays < 7) return date.toLocaleDateString('ru-RU', { weekday: 'short' });
  return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
}

function errorMessage(error: unknown, fallback = 'Не удалось загрузить чаты'): string {
  return error instanceof Error ? error.message : fallback;
}

function currentFromDetail(detail: ConversationDetail, row?: Conversation, previous = current): CurrentConversation {
  const knownTitle = row?.display_title || (previous?.id === detail.id ? previous.display_title : '');
  return {
    id: detail.id,
    type: detail.type,
    display_title: detail.display_title?.trim() || knownTitle || detail.title?.trim() || (detail.type === 'direct' ? 'Диалог' : 'Чат'),
    archived_at: detail.archived_at,
  };
}

// The address the app has actually acted on. Back/Forward compare against this rather than against
// `current`: while a chat is being opened, applyConversationSnapshot() re-derives `current` from
// ConversationPages, whose selection is still the chat being left, so `current` briefly names the
// wrong chat. A Back/Forward step that landed in that window decided it had nothing to do and left
// the previous chat on screen under the new address.
let appliedPath = location.pathname;

function clearConversationSelection(replaceRoute = true): void {
  pmTaskPreviews.detach(); filesCards.detach();
  if (current && socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify({ type:'visibility', conversation_id:current.id, visible:false, suppress_push:false }));
  reportedVisible = null; reportedSuppressed = false;
  if (messageSearchScope) { messageSearchScope = null; queueConversationQuery('', 0); }
  else if (messageSearchController) queueConversationQuery(sidebarSearchValue);
  selectionGeneration += 1; historyGeneration += 1; replyWork.cancel();clearManagedAttachmentLoads(); messageNodes.clear();
  current = null; currentDetails = null; historyAccess = null; messages = []; readCursors = []; contextTargetId = null; navigationState = null; sidebarCurrentValidation = null; sidebarValidationFocus = null;
  replyToMessageId = null; composerReplyTarget = null; composerMentionIds = []; composerMentionTokens.clear(); mentionPicker = null; composerAttachmentIds = []; composerStickerAttachmentIds.clear(); composerAttachments = [];
  if (replaceRoute && /^\/(?:c\/|w\/)/.test(location.pathname)) history.replaceState({}, '', '/');
  appliedPath = location.pathname;
}

function applyConversationSnapshot(final = false): void {
  const snapshot = conversationPages.snapshot();
  conversations = snapshot.rows;
  // Only the detail of the conversation that is actually selected. select() deliberately keeps the
  // previous `current` until the new one is authorized (see its comment), so between a switch and
  // its answer the snapshot still describes the chat being left. Publishing that put the app in a
  // state where `current` named one chat while another's history was on screen: openConversation()
  // then aborted its own load on `current?.id !== id`, and Back/Forward decided they had nothing to
  // do. What the snapshot has not confirmed yet is simply not applied.
  if (snapshot.current && snapshot.current.id === snapshot.activeId) {
    const row = snapshot.rows.find((item) => item.id === snapshot.current?.id);
    current = currentFromDetail(snapshot.current, row);
    currentDetails = snapshot.current;
    if (sidebarCurrentValidation?.generation === selectionGeneration) sidebarCurrentValidation = null;
  } else if (final && current && !snapshot.activeId) {
    clearConversationSelection();
  }
}

function beginSidebarValidation(): void {
  if (!current) return;
  const generation = selectionGeneration;
  sidebarCurrentValidation = { generation, startedAt: Date.now() };
  window.setTimeout(() => {
    if (sidebarCurrentValidation?.generation === generation) renderShell();
  }, SIDEBAR_VALIDATION_REVEAL_MS);
  const focused = document.activeElement;
  if (focused instanceof HTMLElement && focused.matches('.message-input') && focused.getAttribute('aria-label') === 'Сообщение' && root.contains(focused)) {
    const field=focused as MessageInput;
    sidebarValidationFocus = { generation: selectionGeneration, start: field.selectionStart, end: field.selectionEnd };
  }
}

async function runSidebarOperation(operation: () => Promise<void>, options: { revalidatesCurrent?: boolean; valid?: () => boolean; background?: boolean } = {}): Promise<void> {
  const valid = options.valid ?? sessionGuard();
  sidebarError = null;
  if (options.revalidatesCurrent) beginSidebarValidation();
  let task: Promise<void>;
  try { task = operation(); }
  catch (error) { sidebarError = errorMessage(error); renderShell(); throw error; }
  applyConversationSnapshot();
  if (!options.background) renderShell();
  try {
    await task;
    if (!valid()) return;
    applyConversationSnapshot(true);
    // refresh() re-validates the SAME already-active conversation on every live event, so
    // many overlapping calls are normal. If THIS call's own validation was superseded by a
    // newer one before it resolved, conversationPages.select() intentionally leaves `current`
    // untouched rather than guess -- that is not a denial, it is "ask again shortly". Only
    // report "access denied" once nothing newer is still in flight to correct it: otherwise a
    // real user saw this false error on every single message under real network latency.
    const detailWorkload = conversationPages.workload().detail;
    if (sidebarCurrentValidation?.generation === selectionGeneration && !conversationPages.snapshot().current
        && detailWorkload.inflight === 0 && detailWorkload.queued === 0) {
      sidebarCurrentValidation = { generation: selectionGeneration, error: 'Не удалось подтвердить доступ к чату', startedAt: Date.now() };
    }
  } catch (error) {
    if (!valid()) return;
    applyConversationSnapshot(true);
    sidebarError = errorMessage(error);
    if (options.revalidatesCurrent && current && !conversationPages.snapshot().current) {
      sidebarCurrentValidation = { generation: selectionGeneration, error: sidebarError, startedAt: Date.now() };
    }
    renderShell();
    throw error;
  }
  renderShell();
}

async function activateConversationSidebar(account: Bootstrap): Promise<void> {
  const owner: SidebarOwner = {
    key: sidebarKey(account), account, session: sessionGeneration,
    epoch: accountEpoch(account.user), csrf: account.csrf_token,
  };
  sidebarOwner = owner; sidebarError = null; sidebarSearchValue = ''; sidebarScrollTop = 0;
  const valid = sessionGuard();
  await loadingStatus.run('Загружаем список чатов…', valid, () => runSidebarOperation(() => conversationPages.setAccount(owner.key), { valid }));
}

function cancelConversationSidebar(): void {
  if (sidebarSearchTimer !== null) window.clearTimeout(sidebarSearchTimer);
  if (sidebarRefreshTimer !== null) window.clearTimeout(sidebarRefreshTimer);
  sidebarSearchTimer = null; sidebarRefreshTimer = null; sidebarRefreshDirty = false;
  sidebarRefreshResolve?.(); sidebarRefreshResolve = null; sidebarRefreshPromise = null;
  sidebarRefreshCurrent?.resolve(false); sidebarRefreshCurrent = null;
  sidebarOwner = null;
  // setAccount performs cancellation synchronously before its already-resolved await.
  void conversationPages.setAccount(null);
  cancelMessageSearch(); messageSearchScope = null;
  sidebarError = null; sidebarFavoriteBusy = null; sidebarSearchValue = ''; newChat = null; sidebarScrollTop = 0; sidebarCurrentValidation = null; sidebarValidationFocus = null; mobileListVisible = false;
}

async function setConversationQuery(value: string): Promise<void> {
  sidebarSearchValue = value.slice(0, 200);
  const query = sidebarSearchValue; const owns = sessionGuard();
  await loadingStatus.run('Ищем чаты…', () => owns() && sidebarSearchValue === query, () => runSidebarOperation(() => conversationPages.setQuery(messageSearchScope ? '' : query), { revalidatesCurrent: true }));
}

function queueConversationQuery(value: string, delay = 250): void {
  cancelMessageSearch();
  sidebarSearchValue = value.slice(0, 200);
  // A programmatic reset must not be undone by renderShell's caret/focus restoration.
  const input = root.querySelector<HTMLInputElement>('[aria-label="Поиск чатов"]');
  if (input && input.value !== sidebarSearchValue) input.value = sidebarSearchValue;
  if (sidebarSearchTimer !== null) window.clearTimeout(sidebarSearchTimer);
  const owner = sidebarOwner;
  sidebarSearchTimer = window.setTimeout(() => {
    sidebarSearchTimer = null;
    if (!owner || sidebarOwner !== owner || !sidebarOwnerValid(owner)) return;
    void setConversationQuery(sidebarSearchValue).catch(() => undefined);
    void searchMessagesFromUi(sidebarSearchValue);
  }, delay);
  renderShell();
}

async function loadMoreConversations(): Promise<void> {
  await loadingStatus.run('Загружаем список чатов…', sessionGuard(), () => runSidebarOperation(() => conversationPages.loadMore()));
}

async function previousConversations(): Promise<void> {
  sidebarScrollTop = 0;
  await loadingStatus.run('Загружаем список чатов…', sessionGuard(), () => runSidebarOperation(() => conversationPages.previous()));
}

async function toggleConversationFavorite(conversation: { id: string; pinned: boolean }): Promise<void> {
  if (sidebarFavoriteBusy) return;
  sidebarFavoriteBusy = conversation.id;
  try {
    await runSidebarOperation(() => conversationPages.setFavorite(conversation.id, !conversation.pinned), { revalidatesCurrent: true });
  } catch {
    // runSidebarOperation publishes the scoped error; do not leak a rejection.
  } finally {
    sidebarFavoriteBusy = null;
    if (bootstrap) renderShell();
  }
}

function scheduleLiveConversationRefresh(currentOnly?: false): Promise<void>;
function scheduleLiveConversationRefresh(currentOnly: true): Promise<boolean>;
function scheduleLiveConversationRefresh(currentOnly = false): Promise<void | boolean> {
  // Fixed throttle window: arrivals cannot slide dispatch indefinitely. While a
  // snapshot is active retain just one trailing invalidation, never a FIFO.
  if (sidebarRefreshPromise) {
    if (sidebarRefreshTimer === null) sidebarRefreshDirty = true;
    if (currentOnly) {
      // A later event needs its own confirmation, not an already-settled old one.
      if (sidebarRefreshCurrent && !sidebarRefreshCurrent.settled) return sidebarRefreshCurrent.promise;
      return sidebarRefreshPromise.then(() => scheduleLiveConversationRefresh(true));
    }
    return sidebarRefreshPromise;
  }
  const owner = sidebarOwner;
  const cycle = new Promise<void>((resolve) => { sidebarRefreshResolve = resolve; });
  sidebarRefreshPromise = cycle;
  let ready!: (authorized: boolean) => void;
  const currentCycle = { promise: new Promise<boolean>(resolve => { ready = resolve; }), resolve: (authorized: boolean) => { currentCycle.settled = true; ready(authorized); }, settled: false };
  sidebarRefreshCurrent = currentCycle;
  const finish = sidebarRefreshResolve;
  const dispatch = () => {
    sidebarRefreshTimer = window.setTimeout(() => {
      sidebarRefreshTimer = null;
      sidebarRefreshDirty = false;
      if (!owner || sidebarOwner !== owner || !sidebarOwnerValid(owner)) {
        if (sidebarRefreshPromise === cycle) { sidebarRefreshPromise = null; sidebarRefreshResolve = null; }
        currentCycle.resolve(false); finish?.(); return;
      }
      void loadConversations(sessionGuard(), true, authorized => currentCycle.resolve(authorized)).catch(() => undefined).finally(() => {
        // An old account's completion must not clear or schedule the new cycle.
        if (sidebarRefreshPromise !== cycle) return;
        // Awaiters depend on this finite snapshot, not global quiet. Preserve
        // the single dirty trailing refresh, but give it its own promise.
        sidebarRefreshPromise = null; sidebarRefreshResolve = null;
        currentCycle.resolve(false); if (sidebarRefreshCurrent === currentCycle) sidebarRefreshCurrent = null; finish?.();
        if (sidebarRefreshDirty) void scheduleLiveConversationRefresh();
      });
    }, 180);
  };
  dispatch();
  return currentOnly ? currentCycle.promise : cycle;
}

// Two-step email-code login rendered directly on this domain (not a redirect to ostart.ru).
// Chat never sees a password; both steps just ask Zoan on the visitor's behalf server-to-server
// (POST /v1/auth/email-code/request, /verify) -- see server.ts for that boundary.
// Zoan sends 6-digit login codes (zoan src/lib/otp.ts generateOtp()).
const LOGIN_CODE_LENGTH = 6;

// Classic one-box-per-digit code field. Typing advances, Backspace on an empty box steps back,
// and a paste or OS one-time-code autofill (which lands the whole code in one box) is spread
// across the boxes. Calls onComplete once every box holds a digit.
function codeBoxes(length: number, onComplete: () => void): { group: HTMLElement; value: () => string; clear: () => void; focus: () => void } {
  const group = element('div', 'code-boxes'); group.setAttribute('role', 'group'); group.setAttribute('aria-label', 'Код из письма');
  const boxes = Array.from({ length }, (_, index) => {
    const box = element('input'); box.type = 'text'; box.inputMode = 'numeric'; box.maxLength = length;
    box.autocomplete = index === 0 ? 'one-time-code' : 'off'; box.setAttribute('aria-label', `Цифра ${index + 1} из ${length}`);
    return box;
  });
  const value = (): string => boxes.map((box) => box.value).join('');
  const fill = (start: number, digits: string): void => {
    let index = start;
    for (const digit of digits) { if (index >= length) break; boxes[index]!.value = digit; index += 1; }
    boxes[Math.min(index, length - 1)]!.focus();
    if (value().length === length) onComplete();
  };
  boxes.forEach((box, index) => {
    box.addEventListener('input', () => {
      const digits = box.value.replace(/\D/g, '');
      box.value = '';
      if (digits) fill(index, digits);
    });
    box.addEventListener('keydown', (event) => {
      if (event.key === 'Backspace' && !box.value && index > 0) { event.preventDefault(); boxes[index - 1]!.value = ''; boxes[index - 1]!.focus(); }
      else if (event.key === 'ArrowLeft' && index > 0) { event.preventDefault(); boxes[index - 1]!.focus(); }
      else if (event.key === 'ArrowRight' && index < length - 1) { event.preventDefault(); boxes[index + 1]!.focus(); }
    });
    box.addEventListener('paste', (event) => {
      const digits = (event.clipboardData?.getData('text') ?? '').replace(/\D/g, '');
      if (!digits) return;
      event.preventDefault(); fill(index, digits);
    });
    box.addEventListener('focus', () => box.select());
    group.append(box);
  });
  return {
    group, value,
    clear: () => { for (const box of boxes) box.value = ''; },
    focus: () => { (boxes.find((box) => !box.value) ?? boxes[length - 1]!).focus(); },
  };
}

function renderEmailCodeLogin(card: HTMLElement, generation: number): void {
  const emailForm = element('form', 'login-email-form');
  emailForm.append(element('p', 'notice', 'Введите рабочий email — пришлём код для входа.'));
  const emailInput = element('input'); emailInput.type = 'email'; emailInput.name = 'email'; emailInput.required = true;
  emailInput.autocomplete = 'email'; emailInput.placeholder = 'you@company.ru'; emailInput.setAttribute('aria-label', 'Email');
  const emailError = element('p', 'error'); emailError.hidden = true;
  const emailSubmit = element('button', '', 'Отправить код'); emailSubmit.type = 'submit';
  emailForm.append(emailInput, emailError, emailSubmit);

  const codeForm = element('form', 'login-code-form'); codeForm.hidden = true;
  const codeNotice = element('p', 'notice');
  const code = codeBoxes(LOGIN_CODE_LENGTH, () => codeForm.requestSubmit());
  const codeError = element('p', 'error'); codeError.hidden = true;
  const codeSubmit = element('button', '', 'Войти'); codeSubmit.type = 'submit';
  const backButton = element('button', 'ghost', 'Другой email'); backButton.type = 'button';
  codeForm.append(codeNotice, code.group, codeError, codeSubmit, backButton);

  const recoveryPanel = element('section', 'login-email-form'); recoveryPanel.hidden = true;
  recoveryPanel.setAttribute('aria-label', 'Восстановление входа');
  const recoveryNotice = element('p', 'notice', 'Ответ входа пришёл с задержкой. Завершите потерянную сессию этого браузера, затем войдите ещё раз.');
  const recoveryError = element('p', 'error'); recoveryError.hidden = true; recoveryError.setAttribute('role', 'status');
  const recoveryButton = element('button', 'ghost', 'Завершить потерянную сессию'); recoveryButton.type = 'button';
  recoveryPanel.append(recoveryNotice, recoveryError, recoveryButton);
  card.append(emailForm, codeForm, recoveryPanel);

  let pendingEmail = '';
  const stillCurrent = (): boolean => !bootstrap && generation === sessionGeneration;
  let recoveryChallenge: string | undefined;
  const showRecovery = async (): Promise<void> => {
    recoveryPanel.hidden = false; recoveryButton.disabled = true; recoveryError.hidden = true;
    recoveryChallenge = undefined;
    try {
      const result = await api<{ challenge: string }>('/v1/auth/recovery/prepare', { method: 'POST', body: '{}' });
      if (!stillCurrent()) return;
      recoveryChallenge = result.challenge;
      recoveryButton.textContent = 'Завершить потерянную сессию';
    } catch (error) {
      if (!stillCurrent()) return;
      recoveryError.textContent = error instanceof Error ? error.message : 'Не удалось проверить вход. Попробуйте ещё раз.';
      recoveryError.hidden = false; recoveryButton.textContent = 'Проверить снова';
    } finally { if (stillCurrent()) recoveryButton.disabled = false; }
  };
  recoveryButton.addEventListener('click', async () => {
    if (!stillCurrent() || !root.contains(card) || recoveryButton.disabled) return;
    if (!recoveryChallenge) { await showRecovery(); return; }
    const challenge = recoveryChallenge; recoveryChallenge = undefined;
    recoveryButton.disabled = true; recoveryError.hidden = true;
    try {
      const response = await fetch('/v1/auth/recovery/confirm', { method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ challenge }) });
      if (response.status !== 204) await readApiResponse(response);
      if (!stillCurrent()) return;
      const url = new URL(location.href); url.searchParams.delete('login_recovery');
      history.replaceState(history.state, '', url);
      await start();
    } catch (error) {
      if (!stillCurrent()) return;
      recoveryError.textContent = error instanceof Error ? error.message : 'Не удалось завершить вход. Проверьте состояние ещё раз.';
      recoveryError.hidden = false; recoveryButton.textContent = 'Проверить снова'; recoveryButton.disabled = false;
    }
  });
  if (new URL(location.href).searchParams.get('login_recovery') === '1') void showRecovery();

  emailForm.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (!stillCurrent()) return;
    const value = emailInput.value.trim();
    if (!value) return;
    emailError.hidden = true;
    emailSubmit.disabled = true;
    try {
      await api('/v1/auth/email-code/request', { method: 'POST', body: JSON.stringify({ email: value }) });
      if (!stillCurrent()) return;
      pendingEmail = value;
      codeNotice.textContent = `Мы отправили код на ${value}.`;
      emailForm.hidden = true;
      codeForm.hidden = false;
      code.focus();
    } catch (error) {
      if (!stillCurrent()) return;
      emailError.textContent = error instanceof Error ? error.message : 'Не удалось отправить код. Попробуйте ещё раз.';
      emailError.hidden = false;
    } finally {
      if (stillCurrent()) emailSubmit.disabled = false;
    }
  });

  codeForm.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (!stillCurrent()) return;
    const value = code.value();
    if (value.length !== LOGIN_CODE_LENGTH || codeSubmit.disabled) return;
    codeError.hidden = true;
    codeSubmit.disabled = true;
    try {
      await api('/v1/auth/email-code/verify', { method: 'POST', body: JSON.stringify({ email: pendingEmail, code: value }) });
      if (!stillCurrent()) return;
      await start();
    } catch (error) {
      if (!stillCurrent()) return;
      codeSubmit.disabled = false;
      codeError.textContent = error instanceof Error ? error.message : 'Не удалось войти. Попробуйте ещё раз.';
      codeError.hidden = false;
      if (typeof error === 'object' && error !== null && 'code' in error && error.code === 'SESSION_CONFLICT') void showRecovery();
      code.clear(); code.focus();
    }
  });

  backButton.addEventListener('click', () => {
    codeForm.hidden = true;
    emailForm.hidden = false;
    code.clear(); codeError.hidden = true;
    emailInput.focus();
  });
}

async function renderLogin(): Promise<void> {
  loadingStatus.refresh();
  if (logoutFence.read()) { renderLogoutRecovery(); return; }
  if (activeEditor) { activeEditor.close(false); }
  if (activeProfileDialog) { activeProfileDialog.close(); }
  resetTitleAndFavicon();
  const generation = sessionGeneration;
  suspendMenu();
  root.replaceChildren();
  const screen = element('main', 'login');
  const card = element('section', 'login-card');
  const brand = element('div', 'brand');
  const icon = element('img'); icon.src = '/icon.svg'; icon.alt = '';
  brand.append(icon, element('h1', '', 'Chat Ostart'));
  card.append(brand);
  try {
    const data = await api<{ users: { id: string; display_name: string; workspace_id: string }[] }>('/v1/test/users');
    if (logoutFence.read() || bootstrap || generation !== sessionGeneration) return;
    card.append(element('p', 'notice', 'Только синтетические учётные записи. Доступ к этому контуру должен выполняться через защищённый SSH-туннель. Реальные push и анализирующий бот отключены.'));
    for (const user of data.users) {
      const button = element('button', 'user-choice');
      button.type = 'button';
      button.append(element('span', '', user.display_name), element('span', 'meta', user.workspace_id.endsWith('1') ? 'Основное test workspace' : 'Изолированное workspace'));
      button.addEventListener('click', async () => {
        if (logoutFence.read() || bootstrap || generation !== sessionGeneration || !root.contains(screen)) return;
        button.disabled = true;
        try {
        await api('/v1/test/session', { method: 'POST', body: JSON.stringify({ user_id: user.id }) });
        if (logoutFence.read() || bootstrap || generation !== sessionGeneration) return;
        await start();
        } catch (error) { if (!bootstrap && generation === sessionGeneration) { button.disabled = false; toast(error instanceof Error ? error.message : 'Ошибка входа'); } }
      });
      card.append(button);
    }
  } catch (error) {
    const code = typeof error === 'object' && error !== null && 'code' in error ? String((error as { code?: unknown }).code ?? '') : '';
    if (code === 'NOT_FOUND') {
      renderEmailCodeLogin(card, generation);
    } else {
      card.append(element('p', 'error', 'Не удалось загрузить страницу входа. Попробуйте ещё раз.'));
      const retry = element('button', 'ghost', 'Повторить'); retry.type = 'button'; retry.addEventListener('click', () => void renderLogin());
      card.append(retry);
      if (!code || code === 'INTERNAL_ERROR' || code === 'NETWORK_ERROR') {
        window.setTimeout(() => { if (!bootstrap && root.contains(screen)) void renderLogin(); }, 750);
      }
    }
  }
  if (logoutFence.read() || bootstrap || generation !== sessionGeneration) return;
  screen.append(card); root.append(screen);
}

let activeEditor: { dialog: HTMLDialogElement; valid: () => boolean; close: (restore?: boolean) => void } | null = null;
let activeProfileDialog: { valid: () => boolean; close: () => void } | null = null;
// renderShell() rebuilds the whole page from state on every change, and most calls (each live
// event runs a sidebar refresh, a history catch-up and a read-cursor load, each ending in a
// render) change nothing visible or only one column. Swapping the unchanged columns anyway
// detached whatever the user was pointing at: a click that landed between two renders hit a
// removed node, and hover/text selection/scroll were lost. mountShell() therefore keeps every
// top-level region (sidebar, conversation, chat info) whose freshly built replacement is
// identical (isEqualNode) to the mounted one.
//
// Keeping an old node also keeps its event listeners, so an identical DOM must imply
// equivalent listeners. Two rules make that hold:
// - Everything a listener's guard captures is stamped on each region (`data-owner`):
//   sessionGuard() compares the session generation, the `bootstrap` object identity and the
//   logout epoch; selectionGuard() adds the selection generation and the current conversation.
//   Any change there therefore replaces every region, exactly as before.
// - Everything else a listener captures must be visible in attributes/children: form state is
//   written through defaultValue/defaultChecked/defaultSelected (reflected), not the
//   property-only value/checked/selected, and hidden identifiers are stamped as data attributes
//   (message version, member id). Listeners that need live data look it up at event time.
const messageNodes = new MessageNodes();
function mutateMessageCards(mutate: () => void): void {
  const history = root.querySelector<HTMLElement>('#messages'), id = current?.id;
  if (!history || !id || history.dataset.conversationId !== id || !history.getClientRects().length) { mutate(); return; }
  // Reuse the last restored anchor at the same scroll position. Fractional CSS zoom
  // can round scrollTop and otherwise move a barely visible message across the edge
  // during a burst of card responses, replacing the reader's anchor with its sibling.
  const anchor = historyScrollAnchor?.conversationId === id && historyScrollAnchor.scrollTop === history.scrollTop
    ? historyScrollAnchor : captureHistoryAnchor(history, id);
  mutate();
  if (!history.isConnected || current?.id !== id) return;
  if (anchor.atBottom) history.scrollTop = history.scrollHeight;
  else restoreHistoryAnchor(history, anchor);
  historyScrollAnchor = { ...anchor, scrollTop: history.scrollTop };
}
const sidebarWidth = new SidebarWidth(root, () => bootstrap ? `pm-chat:sidebar-width:${bootstrap.user.workspace_id}:${bootstrap.user.id}` : null, run => mutateMessageCards(() => { run(); const input=root.querySelector<MessageInput>('.composer .message-input'); if(input)fitComposer(input); }));
const filesCards = new FilesCards(loadFileCard, mutateMessageCards);
function syncFilesCards(): void {
 const valid=selectionGuard();const allowed=()=>valid()&&Boolean(current&&currentDetails&&historyAccess&&!navigationState&&currentDetails.id===current.id&&historyAccess.id===current.id&&historyAccess.membership_epoch_id===currentDetails.membership_epoch_id&&historyAccess.visible_from_seq===currentDetails.visible_from_seq);
 filesCards.sync(root.querySelector<HTMLElement>('#messages'),`${renderOwnerStamp()}:${historyAccess?.membership_epoch_id??''}`,bootstrap?.files_preview_identity??null,allowed,()=>allowed()&&!sidebarCurrentValidation&&historyReplacementPending===null,cardCacheScope());
}
window.addEventListener('focus',syncFilesCards);
document.addEventListener('visibilitychange',syncFilesCards);
const pmTaskPreviews = new PmTaskPreviews((request, signal) => request.refresh
  ? api<unknown>(`/v1/messages/${request.messageId}/pm-task-previews/${request.taskId}/refresh`, {
    method: 'POST', body: JSON.stringify({ messageVersion: request.messageVersion, ...request.refresh }), signal, cache: 'no-store',
  })
  : api<unknown>(pmMessageSnapshotPath(request.messageId, request.taskId, request.messageVersion), { signal, cache: 'no-store' }), mutateMessageCards);
function syncPmTaskPreviews(): void {
  const valid = selectionGuard();
  const authorized = (): boolean => valid() && bootstrap?.pm_task_previews?.enabled === true && Boolean(current && currentDetails && historyAccess
    && !navigationState && currentDetails.id === current.id && historyAccess.id === current.id
    && historyAccess.membership_epoch_id === currentDetails.membership_epoch_id
    && historyAccess.visible_from_seq === currentDetails.visible_from_seq);
  if (!authorized()) { pmTaskPreviews.detach(); return; }
  // Pending ordinary Chat validation is not a new PM snapshot. Keep the authorized
  // mounted card, but do not start an initial read or explicit refresh while busy.
  const canLoad = (): boolean => authorized() && !sidebarCurrentValidation && historyReplacementPending === null;
  pmTaskPreviews.sync(root.querySelector<HTMLElement>('#messages'), `${renderOwnerStamp()}:${historyAccess!.membership_epoch_id}`, authorized,
    bootstrap?.pm_task_previews?.requires_reauthentication === true, canLoad,cardCacheScope());
}
window.addEventListener('focus', syncPmTaskPreviews);
document.addEventListener('visibilitychange', syncPmTaskPreviews);
function cardCacheScope(){return {owner:bootstrap?JSON.stringify([bootstrap.user.workspace_id,bootstrap.user.id,accountEpoch(bootstrap.user)]):'',context:JSON.stringify([current?.id,historyAccess?.membership_epoch_id,historyAccess?.visible_from_seq,currentDetails?.role])};}
const bootstrapIdentities = new WeakMap<Bootstrap, number>();
let bootstrapIdentitySerial = 0;
function renderOwnerStamp(): string {
  if (!bootstrap) return '';
  let identity = bootstrapIdentities.get(bootstrap);
  if (identity === undefined) { identity = ++bootstrapIdentitySerial; bootstrapIdentities.set(bootstrap, identity); }
  return JSON.stringify([sessionGeneration, identity, accountEpoch(bootstrap.user), selectionGeneration, current?.id ?? null]);
}

// A replaced region takes over the previous region's already-decoded avatar <img>s (by URL): a
// fresh <img> is blank for a frame even when cached, which users saw as avatars flickering.
// Avatars carry no listeners (the direct-chat link is delegated via data-open-direct), so only
// attributes are transferred; `src` stays untouched so the decoded image is kept.
function adoptAvatars(from: Element, into: Element): void {
  const pool = new Map<string, HTMLImageElement[]>();
  for (const img of from.querySelectorAll<HTMLImageElement>('img[class*="avatar"]')) {
    // A retained article owns its descendants; parent fallback must not steal them.
    if (messageNodes.isRetained(img.closest('.message') ?? img)) continue;
    const src = img.getAttribute('src') ?? ''; pool.set(src, [...pool.get(src) ?? [], img]);
  }
  for (const fresh of into.querySelectorAll<HTMLImageElement>('img[class*="avatar"]')) {
    const reused = pool.get(fresh.getAttribute('src') ?? '')?.pop(); if (!reused) continue;
    for (const attribute of [...reused.attributes]) if (attribute.name !== 'src') reused.removeAttribute(attribute.name);
    for (const attribute of [...fresh.attributes]) if (attribute.name !== 'src') reused.setAttribute(attribute.name, attribute.value);
    fresh.replaceWith(reused);
  }
}

// A copy of a mounted region with the attribute changes that interaction (not state) made undone,
// so it compares equal to a fresh render of the same state: tooltip.ts moves a hovered element's
// `title` to `data-tip`, and a closed action menu's emptied inline position can still serialize as
// style="". The live nodes are left as they are.
function asRendered(region: Element): Element {
  const copy = region.cloneNode(true) as Element;
  for (const node of copy.querySelectorAll<HTMLElement>('[data-tip]')) {
    if (!node.hasAttribute('title')) node.title = node.dataset.tip!;
    delete node.dataset.tip;
  }
  // Scroll owns this button's visibility; its fresh render starts hidden.
  copy.querySelector('.history-jump')?.setAttribute('hidden', '');
  // fitComposer() sizes the message field to the text typed into it.
  for (const node of copy.querySelectorAll<HTMLElement>('.composer .message-input')) node.style.removeProperty('height');
  for (const node of copy.querySelectorAll('[style=""]')) node.removeAttribute('style');
  for (const field of copy.querySelectorAll<HTMLElement>('.message-input')) normalizeMessageInput(field);
  if (copy instanceof HTMLElement && copy.matches('.message-input')) normalizeMessageInput(copy);
  // The message box's height is set to fit its text after mounting; it alone must not force a remount.
  copy.querySelector('.composer .message-input')?.removeAttribute('style');
  return copy;
}

function fitComposer(input: MessageInput): void {
  if (!input.isConnected) return;
  const style = getComputedStyle(input);
  fitComposerInput(input, document.querySelector<HTMLElement>('#messages'), parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth));
}
// A narrower or wider window rewraps the draft, so its height changes too.
window.addEventListener('resize', () => { const input = document.querySelector<MessageInput>('.composer .message-input'); if (input) fitComposer(input); });

// The sidebar changes on nearly every background refresh (loading state of its buttons, one chat's
// preview/time/unread, typing), and swapping the whole column each time made the idle chat list
// visibly blink. Inside it only the changed children are swapped: containers matching this selector
// are kept and reconciled child by child (rows keyed by `data-key`, the conversation id). Only
// Containers have no child-capturing listeners, except history, whose listeners are explicitly
// rebound after every mount. Controls and message articles retain the existing equal-DOM rule
// and are replaced as units when their content changes.
const RECONCILED_CONTAINERS = 'aside.sidebar, nav.chat-list, div.chat-row, section.conversation, div.history-frame, div.history-navigation, div.messages, div.message-row, div.message-avatar-rail';

function childSignature(node: Node): string {
  if (!(node instanceof HTMLElement)) return `#${node.nodeName}`;
  return node.dataset.key !== undefined ? `key:${node.dataset.key}` : `${node.nodeName}.${node.className}`;
}

// Returns the node to mount: the old one when unchanged (or reconciled in place), else the fresh one.
function reconcileNode(old: Node, fresh: Node): Node {
  if (fresh instanceof HTMLElement && fresh.hasAttribute('data-retained-message')) return messageNodes.resolve(fresh);
  if (messageNodes.isFresh(fresh)) { if (old instanceof Element && fresh instanceof Element) adoptAvatars(old, fresh); return messageNodes.resolve(fresh); }
  if (!(old instanceof Element) || !(fresh instanceof Element)) return old.isEqualNode(fresh) ? old : fresh;
  // Measured rail height is live layout state, not a render dependency. Preserve
  // the rail/avatar while its existing message rows reconcile; resize updates it.
  if (old instanceof HTMLElement && fresh instanceof HTMLElement && old.matches('.message-avatar-rail') && fresh.matches('.message-avatar-rail')) fresh.style.height=old.style.height;
  // History has render-owned listeners rebound after reconciliation; atTail is live state.
  if (old.matches('#messages') && fresh.matches('#messages') && old.getAttribute('data-conversation-id') === fresh.getAttribute('data-conversation-id')) {
    old.setAttribute('data-at-tail', fresh.getAttribute('data-at-tail') ?? 'false');
  }
  if (old.matches(RECONCILED_CONTAINERS) && fresh.matches(RECONCILED_CONTAINERS) && old.cloneNode(false).isEqualNode(fresh.cloneNode(false))) {
    const pool = new Map<string, ChildNode[]>();
    for (const child of old.childNodes) { const signature = childSignature(child); pool.set(signature, [...pool.get(signature) ?? [], child]); }
    const next = [...fresh.childNodes].map((child) => { const match = pool.get(childSignature(child))?.shift(); return match ? reconcileNode(match, child) : messageNodes.resolve(child); });
    next.forEach((child, index) => { const at = old.childNodes[index] ?? null; if (at !== child) old.insertBefore(child, at); });
    while (old.childNodes.length > next.length) old.lastChild!.remove();
    return old;
  }
  // Compare interactive leaves only: cloning a structural parent duplicates its entire history.
  if (old.isEqualNode(fresh) || asRendered(old).isEqualNode(fresh)) return old;
  adoptAvatars(old, fresh);
  return messageNodes.resolve(fresh);
}

function mountShell(shell: HTMLElement): void {
  const owner = renderOwnerStamp();
  shell.dataset.owner = owner;
  const regions = [...shell.children];
  for (const region of regions) (region as HTMLElement).dataset.owner = owner;
  const mounted = root.querySelector<HTMLElement>(':scope > main.shell');
  // A different owner (or no shell yet) replaces everything, toasts included: nothing
  // rendered for a previous account/selection may survive. Within the same owner a toast
  // (appended to root beside the shell) now outlives unrelated renders for its 2.4 s instead of
  // vanishing with the next live event.
  if (!mounted || mounted.dataset.owner !== owner) {
    if (mounted) adoptAvatars(mounted, shell);
    root.replaceChildren(messageNodes.resolve(shell));
    // A retained img must not keep its former message/history/shell alive through parentNode.
    for (const entry of conversationCache.values()) for (const image of entry.images) if (!image.isConnected) {
      image.remove(); image.onclick = null; image.onkeydown = null;
    }
    return;
  }
  mounted.className = shell.className;
  regions.forEach((region, index) => {
    const old = mounted.children[index];
    if (!old) { mounted.append(messageNodes.resolve(region)); return; }
    const next = reconcileNode(old, region);
    if (next !== old) old.replaceWith(next);
  });
  while (mounted.children.length > regions.length) mounted.lastElementChild!.remove();
}

function renderShell(): void {
  if(restoreDeadlineTimer!==null)window.clearTimeout(restoreDeadlineTimer);restoreDeadlineTimer=null;
  loadingStatus.refresh();connectionStatus.refresh();
  historyRenderCleanup?.(); historyRenderCleanup = null;
  historyResizeObserver?.disconnect(); historyResizeObserver = null;
  if (activeEditor && !activeEditor.valid()) { activeEditor.close(false); }
  // The dialog is appended to document.body, outside root, so rendering the shell below
  // never removes it on its own -- without this, logging out (or switching accounts) left it
  // open on top of the fresh login screen, blocking every click on the new account choices.
  closeStaleStickerPicker();
  if (activeProfileDialog && !activeProfileDialog.valid()) { activeProfileDialog.close(); }
  if (activeBugDialog && !activeBugDialog.valid()) { activeBugDialog.close(); }
  if (activeForwardDialog && !activeForwardDialog.valid()) { activeForwardDialog.close(); }
  if (activeTodoDialog && (!activeTodoDialog.valid() || (activeTodoDialog.mode()==='inline' && activeTodoConversationId!==current?.id))) { activeTodoDialog.close(); }
  if (activeAddMembersDialog && !activeAddMembersDialog.valid()) { activeAddMembersDialog.close(); }
  if (logoutFence.read()) { connectionStatus.reset();pmTaskPreviews.clear(); filesCards.clear(); messageNodes.clear(); renderLogoutRecovery(); return; }
  if (!bootstrap) { connectionStatus.reset();pmTaskPreviews.clear(); filesCards.clear(); messageNodes.clear(); return; }
  messageNodes.begin(JSON.stringify([renderOwnerStamp(), currentDetails?.membership_epoch_id, currentDetails?.role, current?.type, current?.archived_at]));
  const previousComposer = root.querySelector<MessageInput>('.composer .message-input');
  const composerScroll = previousComposer ? {owner:previousComposer.closest('form')?.dataset.composerOwner,value:previousComposer.value,top:previousComposer.scrollTop,left:previousComposer.scrollLeft} : null;
  const previousList = root.querySelector<HTMLElement>('.chat-list');
  if (previousList) sidebarScrollTop = previousList.scrollTop;
  const previousHistory = root.querySelector<HTMLElement>('#messages');
  if (previousHistory?.getClientRects().length && current && previousHistory.dataset.conversationId === current.id) {
    historyScrollAnchor = captureHistoryAnchor(previousHistory, current.id);
  } else if (historyScrollAnchor?.conversationId !== current?.id) historyScrollAnchor = null;
  const retainedBusyMenu = sidebarFavoriteBusy ? root.querySelector<HTMLElement>('.conversation-head .action-menu:has([aria-busy="true"])') : null;
  const savedMenu = suspendMenu();
  const focused = document.activeElement as HTMLInputElement | HTMLTextAreaElement | MessageInput | HTMLSelectElement | null;
  const focusLabel = focused?.getAttribute('aria-label');
  // The chat-info panel's own async loads (members/pins/available users) trigger a second
  // renderShell() shortly after opening; without restoring these fields' in-progress
  // text/focus the same way the search box already is, a render landing mid-keystroke silently
  // wipes whatever the user just typed (the panel is torn down and rebuilt whenever anything
  // in it changed, see mountShell()). The add-member select has no text/selection to preserve, only
  // focus itself -- losing it right after the header shortcut opens the panel and focuses it
  // would make that shortcut pointless the moment any other live event triggers a re-render.
  const restoreFocus = focused && root.contains(focused) && (focusLabel === 'Сообщение' || focusLabel === 'Поиск чатов' || focusLabel === 'Поиск коллег' || focusLabel === 'Название группы' || focusLabel === 'Название чата' || focusLabel === 'Описание чата' || focusLabel === 'Добавить участника' || focusLabel === 'Фильтр участников');
  const isSelectFocus = restoreFocus && focused instanceof HTMLSelectElement;
  const focusValue = focused?.value; const start = restoreFocus && !isSelectFocus ? (focused as HTMLInputElement | HTMLTextAreaElement | MessageInput).selectionStart : null; const end = restoreFocus && !isSelectFocus ? (focused as HTMLInputElement | HTMLTextAreaElement | MessageInput).selectionEnd : null;
  if (sidebarValidationFocus?.generation === selectionGeneration) {
    const composer = root.querySelector<MessageInput>('.composer .message-input');
    if (composer && focused === composer) {
      sidebarValidationFocus = { generation: selectionGeneration, start: composer.selectionStart, end: composer.selectionEnd };
    } else if (composer || (focused && focused !== document.body)) {
      // Respect focus deliberately moved elsewhere, including while validation
      // temporarily hides the composer. Text always comes from the owned draft.
      sidebarValidationFocus = null;
    }
  }
  const snapshot = conversationPages.snapshot();
  const shell = element('main', `shell${current ? ' chat-open' : ''}${mobileListVisible ? ' mobile-list-visible' : ''}`);
  const sidebar = element('aside', 'sidebar');
  const head = element('div', 'sidebar-head');
  // The "Chat Ostart" title, the account name, and the bot-analysis notice used to sit here as
  // plain text -- removed as visual clutter; the account name now lives only in the
  // .sidebar-account footer button (which opens the profile dialog), and the notice text
  // itself was never actionable.
  // Only while the choice has not been made yet: once notifications are on (or refused), the
  // control lives in the profile dialog instead of taking room in every render.
  if (bootstrap.push.enabled && pushSupport() === 'ready' && Notification.permission === 'default') {
    const notifications = element('button', 'ghost', 'Включить уведомления');
    notifications.type = 'button'; notifications.addEventListener('click', () => void bindBrowserPush(true));
    head.append(notifications);
  }
  const utilities = element('div', 'toolbar');
  const selfMessages = menuItem('Сообщения себе', 'bookmark', 'ghost');
  const ownsSelfMessages = sessionGuard();
  selfMessages.addEventListener('click', () => {
    if (!ownsSelfMessages()) return;
    void runUiAction(async (request, valid) => {
      const userId = bootstrap!.user.id;
      const saved = await loadingStatus.run('Открываем сообщения себе…', valid, () => request<{id:string}>(`/v1/direct/${encodeURIComponent(userId)}`, {method:'POST',body:'{}'}));
      await loadConversations(valid); if (!valid()) return;
      await openConversation(conversations.find(row=>row.id===saved.id) ?? saved.id);
    });
  });
  const readAll = menuItem('Прочитать все', 'check', 'ghost'); readAll.addEventListener('click', () => void markReadAllFromUi());
  const settings = menuItem('Уведомления', 'bell', 'ghost'); settings.addEventListener('click', () => void notificationSettingsFromUi());
  const refresh = menuItem(sidebarError ? 'Повторить' : 'Обновить с начала', 'refresh', 'ghost'); refresh.disabled = snapshot.loading;
  refresh.addEventListener('click', () => void loadingStatus.run('Обновляем список чатов…', sessionGuard(), () => loadConversations()).catch(() => undefined));
  const myTasks = menuItem('Мои задачи', 'check', 'ghost');
  const ownsMyTasks = sessionGuard(); myTasks.addEventListener('click', () => { if (ownsMyTasks()) showTasks(true); });
  utilities.append(selfMessages, myTasks, readAll, settings);
  if(!current)utilities.append(connectionStatus.button());
  // Export is offered only to group owners/admins; the server enforces the same rule.
  if (bootstrap.can_export) {
    const exportData = menuItem('Экспорт', 'download', 'ghost'); exportData.addEventListener('click', () => void requestExportFromUi());
    utilities.append(exportData);
  }
  if (!sidebarError) utilities.append(refresh);
  const toolbar = element('div', 'toolbar');
  const filter = element('input'); filter.maxLength = 200;
  if (newChat) { filter.placeholder = 'Поиск коллег'; filter.setAttribute('aria-label', 'Поиск коллег'); filter.defaultValue = newChat.query; }
  else { filter.placeholder = messageSearchScope ? 'Поиск в этом чате' : 'Поиск чатов и сообщений'; filter.setAttribute('aria-label', 'Поиск чатов'); filter.defaultValue = sidebarSearchValue; }
  const create = element('button', `icon-button${newChat ? ' active' : ''}`); create.type = 'button'; create.setAttribute('aria-label', 'Создать диалог'); create.title = 'Создать диалог';
  create.setAttribute('aria-expanded', String(Boolean(newChat)));
  create.innerHTML = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>';
  create.addEventListener('click', () => { if (newChat) closeNewChat(); else void openNewChat(); });
  const searchField = element('div', 'search-field');
  const clearSearch = element('button', 'search-clear'); clearSearch.type = 'button';
  clearSearch.innerHTML = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" aria-hidden="true"><path d="m3 3 10 10M13 3 3 13"/></svg>';
  clearSearch.setAttribute('aria-label', 'Очистить поиск'); clearSearch.title = 'Очистить поиск';
  clearSearch.hidden = !filter.value;
  clearSearch.addEventListener('click', () => {
    cancelMessageSearch();
    filter.value = ''; clearSearch.hidden = true;
    // Focus before the synchronous render so the replacement field inherits it.
    filter.focus({ preventScroll: true });
    if (newChat) queueColleagueQuery('', 0); else queueConversationQuery('', 0);
  });
  searchField.append(filter, clearSearch);
  toolbar.append(searchField, create); head.append(toolbar);
  if (messageSearchScope && !newChat) {
    const scope = element('div', 'search-scope');
    const label = element('span', '', `В чате: ${messageSearchScope.display_title}`); label.title = label.textContent ?? '';
    const all = element('button', 'search-scope-clear', '✕'); all.type = 'button';
    all.setAttribute('aria-label', 'Искать во всех чатах'); all.title = 'Искать во всех чатах';
    all.addEventListener('click', () => { messageSearchScope = null; queueConversationQuery(sidebarSearchValue, 0); focusSidebarSearch(); });
    scope.append(label, all); head.append(scope);
  }
  sidebar.append(head);
  const list = element('nav', 'chat-list'); list.setAttribute('aria-label', 'Чаты');
  const appendConversation = (conversation: Conversation): void => {
    const active = snapshot.activeId === conversation.id;
    const row = element('div', `chat-row${active ? ' active' : ''}`);
    if (active) row.setAttribute('aria-current', 'page');
    row.dataset.key = conversation.id;
    const button = element('button', 'chat-item'); button.type = 'button';
    button.append(avatarNode(conversation.display_title, conversationAvatarUrl(conversation)));
    const text = element('span');
    const typing = typingLabel(conversation.id);
    const preview = element('span', typing ? 'preview typing-preview' : 'preview');
    if (typing) {
      preview.textContent = conversation.type === 'direct' ? 'печатает…' : typing;
    } else {
      const sender = conversation.last_message_sender_name;
      if (sender && conversation.last_message_preview) {
        const author = element('span', 'preview-author', `${conversation.last_message_from_self ? 'Вы' : previewSenderName(sender)}:`);
        author.title = sender;
        preview.append(author);
      }
      preview.append(element('span', 'preview-text', conversation.last_message_preview || 'Пока нет сообщений'));
    }
    text.append(element('span', `chat-title${conversation.unread ? ' unread' : ''}`, conversation.display_title), preview);
    const side = element('span', 'chat-side');
    side.append(element('span', 'chat-time', formatConversationTime(conversation.last_activity_at)));
    const badges = element('span', 'chat-badges');
    if (conversation.unread) {
      badges.append(element('span', 'badge', String(conversation.unread)));
      if (conversation.mentions) {
        const attention = element('span', 'mention', '@');
        attention.title = `Упоминания и ответы вам: ${conversation.mentions}`; attention.setAttribute('aria-label', attention.title);
        badges.append(attention);
      }
    } else if (conversation.archived_at) {
      badges.append(element('span', 'meta', 'Архив'));
    }
    side.append(badges);
    button.append(text, side);
    button.addEventListener('click', () => void openConversation(conversations.find((item) => item.id === conversation.id) ?? conversation));
    row.append(button); list.append(row);
  };
  if (!messageSearchScope) for (const conversation of conversations) appendConversation(conversation);
  if (!messageSearchScope && snapshot.currentOutsideWindow && current) {
    const selectedConversation = current;
    const outside = element('div', 'chat-row active current-outside-window'); outside.setAttribute('aria-current', 'page');
    const button = element('button', 'chat-item'); button.type = 'button';
    const outsideAvatar = currentDetails?.id === selectedConversation.id ? conversationAvatarUrl(currentDetails) : null;
    button.append(avatarNode(selectedConversation.display_title, outsideAvatar));
    // An empty direct is not listed by the server until its first message; it shows here only while open.
    const emptyDirect = selectedConversation.type === 'direct' && currentDetails?.id === selectedConversation.id && currentDetails.last_message_seq === '0';
    const text = element('span'); text.append(element('span', 'chat-title', selectedConversation.display_title), element('span', 'preview', emptyDirect ? 'Нет сообщений' : 'Открытый чат вне текущей страницы'));
    const side = element('span', 'chat-side');
    side.append(element('span', 'chat-time', ''));
    const badges = element('span', 'chat-badges'); badges.append(element('span', 'meta', 'Текущий')); side.append(badges);
    button.append(text, side);
    button.addEventListener('click', () => void openConversation(selectedConversation)); outside.append(button); list.prepend(outside);
  }
  if (messageSearch && messageSearch.query === sidebarSearchValue.trim() && messageSearch.scopeId === (messageSearchScope?.id ?? null)) {
    const found = element('section', 'message-search'); found.setAttribute('aria-label', 'Найдено в сообщениях');
    found.append(element('h4', 'message-search-heading', messageSearch.error ?? (messageSearch.results.length ? 'Найдено в сообщениях' : 'В сообщениях ничего не найдено')));
    for (const result of messageSearch.results) {
      const hit = element('button', 'message-search-result'); hit.type = 'button';
      hit.append(
        element('span', 'chat-title', result.conversation?.display_title ?? 'Чат'),
        searchExcerpt(result, messageSearch.query),
      );
      hit.addEventListener('click', () => void openConversation(result.conversation ?? result.conversation_id, result.id));
      found.append(hit);
    }
    list.append(found);
  }
  filter.addEventListener('input', () => { clearSearch.hidden = !filter.value; if (newChat) queueColleagueQuery(filter.value); else queueConversationQuery(filter.value); });
  filter.addEventListener('keydown', (event) => {
    if (newChat) {
      if (event.key === 'Escape') { event.preventDefault(); closeNewChat(); }
      else if (event.key === 'Enter') { event.preventDefault(); queueColleagueQuery(filter.value, 0); }
      return;
    }
    if (event.key !== 'Enter') return;
    event.preventDefault();
    if (sidebarSearchTimer !== null) window.clearTimeout(sidebarSearchTimer);
    sidebarSearchTimer = null;
    void setConversationQuery(filter.value).catch(() => undefined);
    void searchMessagesFromUi(filter.value);
  });
  list.addEventListener('scroll', () => {
    // renderShell() detaches this list on every re-render; Chromium then still delivers a
    // queued scroll event to the detached node, whose geometry reads 0/0/0 and so looks
    // "scrolled to the bottom". Acting on it auto-loaded pages nobody scrolled to and each
    // resulting render detached the next list, cascading into a burst of re-renders.
    if (!list.isConnected || messageSearchScope) return;
    sidebarScrollTop = list.scrollTop;
    const latest = conversationPages.snapshot();
    if (list.scrollHeight - list.scrollTop - list.clientHeight < 160 && latest.hasMore && !latest.loading) void loadMoreConversations().catch(() => undefined);
  });
  sidebar.append(newChat ? renderNewChat(newChat) : list);
  const pagination = element('div', 'sidebar-pagination');
  // Only when there is nothing to show yet: a background refresh of a visible list (after every sent
  // message) must not flash a status row and shift the column.
  if (snapshot.loading && !conversations.length) { const loading = element('span', 'sidebar-status', 'Загрузка чатов…'); loading.setAttribute('role', 'status'); pagination.append(loading); }
  if (sidebarError) { const error = element('span', 'sidebar-error', sidebarError); error.setAttribute('role', 'alert'); pagination.append(error); }
  if (snapshot.canPrevious) {
    const previous = element('button', 'ghost', 'Показать предыдущие'); previous.type = 'button'; previous.disabled = snapshot.loading;
    previous.addEventListener('click', () => void previousConversations().catch(() => undefined)); pagination.append(previous);
  }
  if (snapshot.hasMore) {
    const more = element('button', 'ghost', 'Загрузить ещё'); more.type = 'button'; more.disabled = snapshot.loading;
    more.addEventListener('click', () => void loadMoreConversations().catch(() => undefined)); pagination.append(more);
  }
  if (sidebarError) pagination.append(refresh);
  if (pagination.childNodes.length && !newChat && !messageSearchScope) sidebar.append(pagination);
  {
    const durable = legacyPending?.account === bootstrap && legacyPending.valid() ? legacyPending.items : [];
    const saving = [...savingPending.values()].filter(entry => entry.account === bootstrap && entry.valid()).map(entry => entry.item);
    for (const item of [...durable, ...saving.filter(item => !durable.some(row => row.client_message_id === item.client_message_id))]) {
      const legacy = typeof item.membership_epoch_id !== 'string' || !item.membership_epoch_id.trim();
      if (!legacy && !item.quarantined && !item.managed_cancel_requested && messages.some(message => message.pending && message.id === item.client_message_id)) continue;
      const notice = element('div', 'error'); notice.setAttribute('role', 'alert'); notice.dataset.pendingId = String(item.client_message_id);
      const managed = Object.hasOwn(item,'managed_files');
      notice.append(element('p', '', item.managed_cancel_requested ? 'Повторная отправка остановлена. Подтверждение отмены ещё не получено.' : item.quarantined ? 'Автоматическая отправка остановлена: доступ запрещён. Текст сохранён; проверьте историю перед новой отправкой.' : legacy ? legacyPendingError : String(item.send_error ?? 'Ожидает отправки. При ошибке можно повторить с тем же ID.')), element('pre', '', String(item.body ?? '')+(Array.isArray(item.managed_file_names)?'\n'+item.managed_file_names.slice(0,10).filter(name=>typeof name==='string').map(name=>String(name).slice(0,240)).join('\n'):'')));
      if (!legacy) {
        const retry = element('button', '', item.managed_cancel_requested ? 'Повторить отмену' : 'Повторить отправку'); retry.type = 'button'; retry.disabled = Boolean(item.quarantined) && !item.managed_cancel_requested;
        const valid = sessionGuard(); retry.addEventListener('click', () => { if (valid()) void retryPending(String(item.client_message_id)); }); notice.append(retry);
      }
      notice.append(element('p', 'meta', managed ? 'Отмена проверяется сервером. Уже отправленное сообщение останется в чате.' : 'Отмена удаляет только локальную очередь и не отменяет сообщение, уже сохранённое сервером.'));
      const remove = element('button', '', !managed && (legacy || item.quarantined) ? 'Удалить из локальной очереди' : 'Отменить'); remove.type = 'button';
      const valid = sessionGuard();
      remove.addEventListener('click', () => {
        if (!valid() || (!managed && (legacy || item.quarantined) && !confirm('Удалить только локальную запись? Это не отменяет сообщение, уже сохранённое сервером.'))) return;
        void cancelPending(String(item.client_message_id));
      });
      notice.append(remove); sidebar.append(notice);
    }
  }
  const accountFooter = element('button', 'sidebar-account'); accountFooter.type = 'button';
  accountFooter.setAttribute('aria-label', 'Настройки профиля');
  accountFooter.append(avatarNode(bootstrap.user.display_name, bootstrap.user.avatar_key ? userAvatarUrl(bootstrap.user.id, bootstrap.user.avatar_key) : null));
  const accountText = element('span');
  accountText.append(element('span', '', bootstrap.user.display_name), element('span', 'meta', bootstrap.user.status_text || 'Настройки профиля'));
  accountFooter.append(accountText);
  accountFooter.addEventListener('click', () => showProfileDialog());
  const accountRow = element('div', 'sidebar-account-row');
  const report = menuItem('Сообщить об ошибке', 'bug', 'ghost');
  const ownsReport=sessionGuard();report.addEventListener('click',()=>{if(ownsReport())showBugReportDialog();});utilities.append(report);
  const sound = element('button', 'bug-button sound-toggle'); sound.type = 'button';
  sound.setAttribute('aria-label', 'Звук сообщений'); sound.setAttribute('aria-pressed', String(incomingSoundEnabled()));
  sound.title = incomingSoundEnabled() ? 'Выключить звук сообщений' : 'Включить звук сообщений';
  sound.innerHTML = `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M11 5 6 9H3v6h3l5 4V5Z"/>${incomingSoundEnabled() ? '<path d="M15 8a6 6 0 0 1 0 8m3-11a10 10 0 0 1 0 14"/>' : '<path d="m16 9 5 6m0-6-5 6"/>'}</svg>`;
  sound.addEventListener('click', () => { setIncomingSound(!incomingSoundEnabled()); renderShell(); });
  accountRow.append(accountFooter, sound, actionMenu('Действия со списком чатов', `sidebar:${sessionGeneration}`, utilities));
  if (installPrompt && !standaloneApp() && !installOfferDismissed()) {
    const offer = element('div', 'install-offer');
    offer.append(element('span', '', 'Открывать чат как программу?'));
    const yes = element('button', '', 'Установить'); yes.type = 'button';
    yes.addEventListener('click', () => {
      const prompt = installPrompt; if (!prompt) return;
      installPrompt = null; rememberInstallChoice();
      void prompt.prompt().catch(() => undefined).then(() => renderShell());
    });
    const later = element('button', 'ghost', 'Не сейчас'); later.type = 'button';
    later.addEventListener('click', () => { rememberInstallChoice(); renderShell(); });
    offer.append(yes, later);
    sidebar.append(offer);
  }
  // One click is all a blocked sound needs: the click itself is the gesture the browser waits for.
  if (soundBlockedByGesture()) {
    const offer = element('div', 'install-offer sound-offer');
    offer.append(element('span', '', 'Браузер заглушил звук сообщений'));
    const on = element('button', '', 'Включить звук'); on.type = 'button';
    on.addEventListener('click', () => void playIncomingSound(true).then(() => renderShell()));
    const later = element('button', 'ghost', 'Не сейчас'); later.type = 'button';
    later.addEventListener('click', () => { soundHintDismissed = true; renderShell(); });
    offer.append(on, later);
    sidebar.append(offer);
  }
  sidebar.append(accountRow);
  const panel = element('section', 'conversation');
  // Detached/queued events belong to the rendered owner, never mutable current.
  const ownsPanel = selectionGuard();
  for (const type of ['click', 'input', 'change', 'keydown', 'submit']) panel.addEventListener(type, (event) => {
    if (!ownsPanel()) { event.preventDefault(); event.stopImmediatePropagation(); }
  }, true);
  let afterConversationAttach: (() => void) | null = null;
  if (!current) panel.append(element('div', 'empty', 'Выберите диалог или создайте новый.'));
  else { const conversation = renderConversation(); panel.append(conversation.fragment); afterConversationAttach = conversation.afterAttach; }
  const resizer=element('div','sidebar-resizer');resizer.tabIndex=0;resizer.setAttribute('role','separator');resizer.setAttribute('aria-orientation','vertical');resizer.setAttribute('aria-label','Ширина списка чатов');resizer.title='Потяните, чтобы изменить ширину списка чатов';sidebar.append(resizer);
  shell.append(serviceNavigation(() => { mobileListVisible = true; renderShell(); }), sidebar, panel);
  if (current && chatInfoOpen) shell.append(renderChatInfo());
  shell.classList.toggle('with-info', Boolean(current && chatInfoOpen));
  mountShell(shell);
  sidebarWidth.sync();
  messageNodes.remember(root);
  refreshRestoreButtons();
  syncPmTaskPreviews();
  syncFilesCards();
  afterConversationAttach?.();
  list.scrollTop = sidebarScrollTop;
  const newSettingsMenu = root.querySelector('.conversation-head .action-menu');
  if (retainedBusyMenu && newSettingsMenu && newSettingsMenu !== retainedBusyMenu && retainedBusyMenu.querySelector('[data-menu-key]')?.getAttribute('data-menu-key') === newSettingsMenu.querySelector('[data-menu-key]')?.getAttribute('data-menu-key')) newSettingsMenu.replaceWith(retainedBusyMenu);
  if (!activeEditor) restoreMenu(savedMenu);
  if (restoreFocus && !activeEditor) {
    const replacement = root.querySelector<HTMLInputElement | HTMLTextAreaElement | MessageInput | HTMLSelectElement>(`[aria-label="${focusLabel}"]`);
    if (replacement) {
      if (focusLabel === 'Поиск чатов') (replacement as HTMLInputElement).value = focusValue ?? sidebarSearchValue;
      else if (focusLabel === 'Название чата' || focusLabel === 'Описание чата') (replacement as HTMLInputElement).value = focusValue ?? replacement.value;
      // Recreating a selection in a retained, focused contenteditable clears
      // native typing marks at an empty caret. Its existing selection is current.
      if (!replacement.matches('.message-input') || replacement !== focused || document.activeElement !== replacement) {
        replacement.focus({preventScroll:true});
        if (!(replacement instanceof HTMLSelectElement)) replacement.setSelectionRange(start ?? 0, end ?? 0);
      }
    }
  }
  if (sidebarValidationFocus && !sidebarCurrentValidation && sidebarValidationFocus.generation === selectionGeneration && !activeEditor) {
    const saved = sidebarValidationFocus; sidebarValidationFocus = null;
    const replacement = root.querySelector<MessageInput>('[aria-label="Сообщение"]');
    if (replacement && document.activeElement !== replacement) { replacement.focus({ preventScroll: true }); replacement.setSelectionRange(saved.start ?? 0, saved.end ?? saved.start ?? 0); }
  }
  const mountedComposer = root.querySelector<MessageInput>('.composer .message-input');
  if (composerScroll && mountedComposer && mountedComposer.closest('form')?.dataset.composerOwner === composerScroll.owner && mountedComposer.value === composerScroll.value) {
    mountedComposer.scrollTop = composerScroll.top; mountedComposer.scrollLeft = composerScroll.left;
  }
  updateTitleAndFavicon();
}

// The first message of this visit's unread tail: everything at or below the reader's last_read_seq
// was seen before, so the line goes right before the oldest newer one. Nothing is marked when the
// whole loaded window is new (the line would sit at the very top and say nothing), when the tail is
// only the reader's own messages sent elsewhere, or while a jump to a permalink is showing a slice
// of old history.
function firstUnreadMessageId(): string | null {
  if (!bootstrap || !unreadBoundary || unreadBoundary.generation !== selectionGeneration || contextTargetId) return null;
  const boundary = unreadBoundary.seq;
  for (const message of messages) {
    if (message.pending) continue;
    let seq: bigint;
    try { seq = BigInt(message.seq); } catch { continue; }
    if (seq <= boundary) continue;
    if (unreadBoundary.tail !== null && seq > unreadBoundary.tail) return null;
    if (message.sender?.id === ownConversationAuthorId(message.conversation_id) || message.kind !== 'user' || message.deleted_at) continue;
    return message.id;
  }
  return null;
}

function renderConversation(): { fragment: DocumentFragment; afterAttach: () => void } {
  const fragment = document.createDocumentFragment();
  const noop = (): void => {};
  if (!current || !bootstrap) return { fragment, afterAttach: noop };
  const head = element('header', 'conversation-head');
  const left = element('div');
  const back = element('button', 'back', '‹'); back.type = 'button'; back.setAttribute('aria-label', 'К списку чатов'); back.addEventListener('click', closeConversationFromUi);
  // A real <button> inside the heading keeps native keyboard/AT semantics without
  // overriding the h2's own "heading" role (role="button" directly on the h2 did,
  // breaking every getByRole('heading', ...) query elsewhere in the test suite).
  const heading = element('h2');
  const headingButton = element('button', 'chat-title-button', current.display_title);
  headingButton.type = 'button'; headingButton.title = 'Информация о чате';
  headingButton.addEventListener('click', () => void toggleChatInfo());
  heading.append(headingButton);
  const typing = typingLabel(current.id);
  left.append(heading);
  // Two separate elements sharing one flex row (not one joined string) so the member-count
  // and connection-status text stay independently exact-matchable in tests while still
  // reading as a single line.
  const stats = element('div', 'connection-row'); stats.setAttribute('role', 'status');
  if (current.type === 'group' && currentDetails?.member_count !== undefined) stats.append(element('span', 'connection', pluralizeMembers(currentDetails.member_count)));
  // While someone types, their name replaces the connection status in the header.
  if (typing) stats.append(element('span', 'connection typing-status', typing));
  else stats.append(element('span', 'connection', `${connectionLabel}${current.archived_at ? ' · Архив: только чтение' : ''}`));
  stats.prepend(connectionStatus.button());
  left.append(stats);
  const conversationActions = element('div', 'message-actions');
  const perChat = menuItem('Настройки', 'sliders'); perChat.addEventListener('click', () => void conversationSettingsFromUi());
  const selected = current;
  const pinned = currentDetails?.pinned;
  const favorite = menuItem(pinned ? 'Убрать из избранного' : 'Добавить в избранное', 'star');
  favorite.disabled = Boolean(sidebarFavoriteBusy) || pinned === undefined;
  favorite.setAttribute('aria-busy', String(Boolean(sidebarFavoriteBusy)));
  favorite.addEventListener('click', () => { if (pinned !== undefined) void toggleConversationFavorite({ id: selected.id, pinned }); });
  conversationActions.append(favorite, perChat);
  if (sidebarFavoriteBusy) for (const button of conversationActions.querySelectorAll('button')) button.disabled = true;
  // The chat's avatar next to the title opens the same info panel (for a direct chat: the person's card).
  const headAvatar = element('button', 'head-avatar'); headAvatar.type = 'button'; headAvatar.setAttribute('aria-label', 'Открыть информацию о чате');
  const avatarSource = conversations.find(item => item.id === current!.id) ?? currentDetails;
  headAvatar.append(avatarNode(current.display_title, avatarSource ? conversationAvatarUrl(avatarSource) : null));
  headAvatar.addEventListener('click', () => void toggleChatInfo());
  const ident = element('div', 'head-ident'); ident.append(headAvatar, left);
  head.append(back, ident);
  if (current.type === 'group' && ['owner', 'admin'].includes(currentDetails?.role ?? '')) {
    const addMember = element('button', 'icon-button add-member-button'); addMember.type = 'button';
    addMember.setAttribute('aria-label', 'Добавить участника'); addMember.title = 'Добавить участников';
    addMember.innerHTML = `<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${MENU_ICONS.userPlus}</svg>`;
    addMember.addEventListener('click', () => void showAddMembersDialog());
    head.append(addMember);
    const addMemberAction = menuItem('Добавить участника', 'userPlus');
    addMemberAction.addEventListener('click', () => void showAddMembersDialog()); conversationActions.append(addMemberAction);
  }
  const search = element('button', 'icon-button chat-search-button'); search.type = 'button';
  search.setAttribute('aria-label', 'Поиск в этом чате'); search.title = 'Поиск в этом чате';
  search.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5"/><path d="m16 16 5 5"/></svg>';
  search.addEventListener('click', () => {
    if (newChatTimer !== null) window.clearTimeout(newChatTimer);
    newChatTimer = null; newChat = null; messageSearchScope = { ...selected };
    mobileListVisible = true; chatInfoOpen = false;
    queueConversationQuery(sidebarSearchValue, 0); focusSidebarSearch();
  });
  head.append(search);
  {
    const tasks=menuItem('', 'check', 'ghost chat-tasks-button');tasks.setAttribute('aria-label',isPersonalConversation()?'Мои задачи':'Открыть задачи чата');paintTodoHeader(tasks);
    const valid=selectionGuard();tasks.addEventListener('click',()=>{if(valid())showTasks(isPersonalConversation());});head.append(tasks);
  }
  head.append(actionMenu('Настройки чата', `chat:${sessionGeneration}:${selectionGeneration}`, conversationActions));
  const closeChat = element('button', 'icon-button close-chat-button', '✕'); closeChat.type = 'button';
  closeChat.setAttribute('aria-label', 'Закрыть чат'); closeChat.title = 'Закрыть чат (Esc)';
  closeChat.addEventListener('click', closeConversationFromUi);
  head.append(closeChat);
  fragment.append(head);
  const pins = conversationPins ?? [];
  if (pins.length) {
    const pageSize = 5;
    const lastPage = Math.floor((pins.length - 1) / pageSize);
    pinnedBarPage = Math.min(pinnedBarPage, lastPage);
    const destination = current;
    const valid = selectionGuard();
    const bar = element('section', 'pinned-bar');
    // Reconciliation must invalidate handlers when an off-page pin changes too.
    bar.dataset.pinSnapshot = JSON.stringify([sessionGeneration, selectionGeneration, pins.map(pin => [pin.id, pinPreview(pin)])]);
    bar.setAttribute('aria-label', 'Закреплённые сообщения');
    const heading = element('div', 'pinned-bar-heading');
    const label = element('span', 'pinned-bar-label');
    label.setAttribute('aria-live', 'polite');
    heading.append(element('span', 'pinned-bar-icon', '📌'), label);
    const rows = element('div', 'pinned-bar-rows');
    const previous = element('button', 'ghost pinned-bar-page', '‹'); previous.type = 'button';
    previous.setAttribute('aria-label', 'Предыдущие закреплённые сообщения'); previous.title = 'Предыдущие пять';
    const next = element('button', 'ghost pinned-bar-page', '›'); next.type = 'button';
    next.setAttribute('aria-label', 'Следующие закреплённые сообщения'); next.title = 'Следующие пять';
    const paintPage = () => {
      const start = pinnedBarPage * pageSize;
      label.textContent = `Закреплено ${start + 1}–${Math.min(start + pageSize, pins.length)} из ${pins.length}`;
      previous.disabled = pinnedBarPage === 0; next.disabled = pinnedBarPage === lastPage;
      rows.replaceChildren();
      for (const [offset, shown] of pins.slice(start, start + pageSize).entries()) {
        const row = element('div', 'pinned-bar-row');
        const preview = pinPreview(shown);
        const jump = element('button', 'pinned-bar-open'); jump.type = 'button';
        jump.setAttribute('aria-label', `Перейти к закреплённому сообщению ${start + offset + 1}: ${preview}`);
        jump.title = preview;
        jump.append(element('span', 'pinned-bar-text', preview));
        jump.addEventListener('click', () => { if (valid()) void openConversation(destination, shown.id); });
        row.append(jump);
        if (canPinHere()) {
          const unpin = element('button', 'ghost pinned-bar-unpin', '✕'); unpin.type = 'button';
          unpin.setAttribute('aria-label', `Открепить сообщение ${start + offset + 1}`); unpin.title = 'Открепить';
          unpin.addEventListener('click', () => {
            if (!valid()) return;
            unpin.disabled = true;
            void togglePin(shown.id, true).finally(() => { unpin.disabled = false; });
          });
          row.append(unpin);
        }
        rows.append(row);
      }
    };
    previous.addEventListener('click', () => { if (valid() && pinnedBarPage > 0) { pinnedBarPage--; paintPage(); } });
    next.addEventListener('click', () => { if (valid() && pinnedBarPage < lastPage) { pinnedBarPage++; paintPage(); } });
    if (pins.length > pageSize) heading.append(previous, next);
    bar.append(heading, rows);
    paintPage();
    fragment.append(bar);
  }
  if (bootstrap.files_requires_reauthentication && messages.some(message => message.attachments?.length || message.replySource?.images?.length)) {
    const notice = element('div', 'file-access-recovery'); notice.setAttribute('role', 'status');
    const owner = bootstrap.user, valid = selectionGuard();
    notice.append(element('span', '', 'Для открытия файлов нужно обновить вход.'));
    const login = element('button', '', 'Восстановить доступ'); login.type = 'button';
    login.addEventListener('click', event => {
      if (!valid()) { event.preventDefault(); return; }
      if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      if (navigationWouldLoseAttachmentWork()) { toast('Дождитесь отправки сообщения или уберите вложения из черновика перед повторным входом.'); return; }
      const route = parseChatRoute(location.pathname, owner.workspace_id);
      try { rememberFileAccessReturn(sessionStorage, owner, route?.conversationId === current?.id ? location.pathname : chatPath(current!.id)); } catch { /* Browser can deny the storage getter itself. */ }
      location.assign('/v1/auth/zoan/start');
    });
    notice.append(login); fragment.append(notice);
  }
  if (sidebarCurrentValidation?.generation === selectionGeneration
      && (sidebarCurrentValidation.error || Date.now() - sidebarCurrentValidation.startedAt >= SIDEBAR_VALIDATION_REVEAL_MS)) {
    if (!sidebarFavoriteBusy) conversationActions.replaceChildren();
    fragment.append(element('div', 'empty', sidebarCurrentValidation.error ?? 'Проверяем доступ к чату…'));
    if (sidebarCurrentValidation.error) {
      const destination = current!; const target = contextTargetId;
      const retry = element('button', '', 'Повторить проверку доступа'); retry.type = 'button';
      retry.addEventListener('click', () => void openConversation(destination, target ?? undefined)); fragment.append(retry);
    }
    return { fragment, afterAttach: noop };
  }
  if (navigationState?.generation === selectionGeneration) {
    if (!sidebarFavoriteBusy) conversationActions.replaceChildren();
    const placeholder = element('div', 'empty navigation-loading', navigationState.error ?? '');
    placeholder.setAttribute('aria-busy', String(!navigationState.error)); fragment.append(placeholder);
    if (navigationState.error) {
      const destination = current!; const target = contextTargetId;
      const retry = element('button', '', 'Повторить загрузку'); retry.type = 'button';
      retry.addEventListener('click', () => void openConversation(destination, target ?? undefined)); fragment.append(retry);
    }
    return { fragment, afterAttach: noop };
  }
  if (!navigator.onLine) fragment.append(element('div', 'offline', 'Нет сети. Сообщение останется в очереди до восстановления.'));
  let history = element('div', 'messages'); history.id = 'messages'; history.tabIndex = 0; history.dataset.conversationId = current.id; history.dataset.atTail = String(historyWindowAtTail);
  // Anchor restoration is synchronous; a CSS scroll animation would race the next update.
  history.style.scrollBehavior = 'auto';
  if (messages.length && historyHasEarlier) {
    const older = element('button', 'load-older', 'Загрузить более ранние'); older.type = 'button';
    older.addEventListener('click', () => void loadOlderMessagesFromUi());
    history.append(older);
  }
  const readReceiptGroups = groupReadReceipts(messages, readCursors);
  const senderAvatarByUserId = new Map(readCursors.map((reader) => [reader.id, reader.avatar_key]));
  const dividerBefore = firstUnreadMessageId();
  // A day line wherever the day changes, so a bare "15:47" always has its date above it -- older
  // pages loaded by scrolling back get theirs from the same pass.
  let previousDay: Date | null = null;
  let previousMessage: Message | null = null;
  for (const message of messages) {
    const day = new Date(message.created_at);
    // Group presentation belongs to the row, not the memoized article: prepending
    // history or inserting a separator must not remount its image/video descendants.
    const grouped = Boolean(message.sender && previousMessage?.sender
      && message.sender.id === previousMessage.sender.id && message.kind === previousMessage.kind
      && message.kind !== 'system' && !message.deleted_at && !previousMessage.deleted_at
      && sameDay(new Date(previousMessage.created_at), day) && message.id !== dividerBefore);
    if (!previousDay || !sameDay(previousDay, day)) {
      const separator = element('div', 'day-divider');
      separator.append(element('span', '', dayLabel(day)));
      history.append(separator);
    }
    previousDay = day;
    if (message.id === dividerBefore) {
      const divider = element('div', 'unread-divider');
      divider.append(element('span', '', 'Новые сообщения'));
      history.append(divider);
    }
    const readers = readReceiptGroups.get(message.id) ?? [];
    const today = new Date();
    const signature = JSON.stringify([message, readers, ownConversationAuthorId(message.conversation_id),
      (message.reactions ?? []).map(reaction => userNameInConversation(message.conversation_id, reaction.user_id)),
      [...pressedButtons].filter(key => key.startsWith(`${message.id}:`)),
      [...openPollVoterLists].filter(key => key.startsWith(`${message.id}:`)),
      today.getFullYear(), today.getMonth(), today.getDate()]);
    const rendered = message.pending ? renderMessage(message, readers)
      : messageNodes.render(message.id, signature, () => renderMessage(message, readers));
    rendered.dataset.key = `message:${message.id}`;
    const mine = Boolean(bootstrap && message.sender?.id === ownConversationAuthorId(message.conversation_id));
    const ordinary = message.kind === 'user' && !message.deleted_at;
    const withAvatar = !mine && message.kind !== 'system' && message.sender && !message.deleted_at;
    if (withAvatar || ordinary) {
      const row = element('div', `message-row${mine ? ' mine' : ''}${grouped ? ' grouped' : ''}`); row.dataset.key = `message:${message.id}`;
      if (withAvatar && grouped) {
        const spacer = element('span', 'message-avatar-spacer'); spacer.setAttribute('aria-hidden', 'true'); row.append(spacer);
      } else if (withAvatar) {
        const avatarKey = senderAvatarByUserId.get(message.sender!.id) ?? null;
        row.classList.add('has-author-avatar');
        const rail=element('div','message-avatar-rail');
        rail.append(linkAvatarToDirect(avatarNode(message.sender!.name, avatarKey ? userAvatarUrl(message.sender!.id, avatarKey) : null, 'avatar message-avatar'), message.sender!.id, message.sender!.name));
        row.append(rail);
      }
      row.append(rendered);
      // A message still being sent cannot be reacted to, but it keeps the slot: without it the
      // bubble would jump sideways the moment the send completes.
      if (ordinary) row.append(message.pending ? element('div', 'reaction-slot') : reactionPicker(message));
      history.append(row);
    } else history.append(rendered);
    previousMessage = message;
  }
  if (activeTodoDialog?.mode()==='inline' && activeTodoConversationId===current.id) history.append(activeTodoDialog.element);
  const navigation = element('div', 'history-navigation');
  const returning = historyReturnAnchor?.conversationId === current.id;
  let navigationButton = element('button', 'history-jump'); navigationButton.type = 'button';
  const navigationLabel = returning ? 'Вернуться к месту чтения' : 'К последним сообщениям';
  navigationButton.title = navigationLabel; navigationButton.setAttribute('aria-label', navigationLabel);
  navigationButton.innerHTML = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${returning ? '<path d="m7 10-4-4 4-4M3 6h10a7 7 0 0 1 0 14h-2"/>' : '<path d="M12 4v16m-6-6 6 6 6-6"/>'}</svg>`;
  navigationButton.hidden = true;
  navigationButton.addEventListener('click', () => {
    navigationButton.disabled = true;
    void jumpHistory(!returning).catch(error => toast(error.message)).finally(() => { navigationButton.disabled = false; });
  });
  navigation.append(navigationButton);
  const historyFrame = element('div', 'history-frame');
  historyFrame.append(history, navigation); fragment.append(historyFrame);
  if (!current.archived_at) {
    const composer = element('form', 'composer');
    composer.dataset.composerOwner = JSON.stringify([sessionGeneration,selectionGeneration,current.id,currentDetails?.membership_epoch_id]);
    // Own messages waiting for their time, above the composer: they are invisible in the history
    // until sent, so this strip is the only place their author can see and change them.
    const waiting = (scheduledMessages ?? []).filter((item) => item.conversation_id === current!.id);
    if (waiting.length) {
      const strip = element('div', 'scheduled-strip');
      strip.append(element('span', 'scheduled-title', waiting.length === 1 ? 'Отложенное сообщение' : `Отложенных сообщений: ${waiting.length}`));
      for (const item of waiting) {
        const line = element('div', `scheduled-item${item.state === 'failed' ? ' failed' : ''}`);
        const when = element('span', 'scheduled-when', item.state === 'failed' ? 'не отправлено' : scheduleLabel(item.send_at));
        if (item.state === 'failed' && item.last_error) when.title = item.last_error;
        const text = element('span', 'scheduled-text', item.body.replace(/\s+/g, ' ').slice(0, 120) || 'Вложение');
        const edit = element('button', 'ghost', 'Изменить'); edit.type = 'button';
        edit.addEventListener('click', () => showScheduleDialog(item));
        const drop = element('button', 'ghost', 'Отменить'); drop.type = 'button';
        drop.addEventListener('click', () => { drop.disabled = true; void cancelScheduled(item.id).finally(() => { drop.disabled = false; }); });
        line.append(when, text, edit, drop);
        strip.append(line);
      }
      composer.append(strip);
    }
    const row = element('div', 'composer-row');
    const ownsComposer = selectionGuard(); const key = draftKey(current.id);
    const input = createMessageInput('Сообщение',ownsComposer); input.enterKeyHint = 'enter';
    input.value = localStorage.getItem(key) ?? '';
    const picker = element('div', 'mention-picker'); picker.hidden = true; picker.setAttribute('role', 'listbox'); picker.setAttribute('aria-label', 'Кого упомянуть');
    const syncPicker = (): void => { updateMentionPicker(input); renderMentionPicker(picker, input); };
    input.addEventListener('input', () => {
      fitComposer(input); if (!ownsComposer()) return;
      localStorage.setItem(key, input.value); sendTyping(); syncPicker();
    });
    input.addEventListener('click', syncPicker);
    input.addEventListener('paste', (event) => {
      // File clipboard entries take precedence over the OS filename text fallback.
      const files = [...(event.clipboardData?.files ?? [])];
      if (!files.length || !ownsComposer()) return;
      event.preventDefault();
      const stamp = new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit' }).replace(/:/g, '-');
      void uploadFiles(files.map((file, index) => file.type.startsWith('image/') && file.name === 'image.png'
        ? new File([file], `Скриншот ${stamp}${files.length > 1 ? `-${index + 1}` : ''}.${file.type.split('/')[1] || 'png'}`, { type: file.type })
        : file));
    });
    input.addEventListener('keydown', (event) => {
      if (event.isComposing) return;
      if (event.defaultPrevented) return;
      if (event.key === 'ArrowUp' && !event.ctrlKey && !event.metaKey && !event.altKey && !event.shiftKey
          && !input.value && !mentionPicker && !activeEditor && ownsComposer()) {
        event.preventDefault(); void editLatestOwnMessage(input); return;
      }
      if (mentionPicker && !isSoftEnter(event, navigator.maxTouchPoints > 0) && handleMentionKey(event, input)) { renderMentionPicker(picker, input); return; }
      // requestSubmit() on a form detached by a concurrent renderShell() (e.g. an incoming
      // peer message) silently does nothing: no submit event, no error. Call the same send
      // path the submit listener uses directly instead of relying on form submission.
      if (event.key === 'Enter' && !event.shiftKey && !isSoftEnter(event, navigator.maxTouchPoints > 0) && (enterSends() || event.ctrlKey || event.metaKey)) { event.preventDefault(); if (ownsComposer()) void queueMessage(input.value); }
    });
    if (replyToMessageId) composer.append(composerReplyState());
    if (composerAttachments.length || composerUploads.length) {
      // Each attached file is a chip with its own ✕ (before, only "Очистить" in the + menu removed them, all at once);
      // files show actual transfer progress; images also show a small preview.
      const files = element('div', 'composer-files'); files.setAttribute('aria-label', 'Прикреплённые файлы');
      for (const upload of composerUploads) files.append(uploadProgressChip(upload,()=>{
        if(!ownsComposer())return;
        sendAfterUploads=null;
        uploadControllers.get(upload.key)?.abort();
        composerUploads=composerUploads.filter(item=>item.key!==upload.key);
        loadingStatus.refresh();renderShell();
      }));
      for (const attachment of composerAttachments) {
        const chip = element('span', 'composer-file');
        const preview = attachmentPreviews.get(attachment.id);
        if (preview) { const thumb = element('img', 'composer-thumb'); thumb.src = preview; thumb.alt = ''; chip.append(thumb); }
        chip.append(element('span', '', attachment.name));
        const remove = element('button', '', '✕'); remove.type = 'button'; remove.setAttribute('aria-label', `Убрать файл ${attachment.name}`);
        remove.addEventListener('click', () => {
          if (!ownsComposer()) return;
          void runUiAction(async(request,valid)=>{
            if(attachment.upload)await cancelChatManagedUpload(request,attachment.upload);
            else if(currentDetails?.shared_room)await request(`/v1/attachments/${attachment.id}`,{method:'DELETE'});
            if(!valid()||!ownsComposer())return;
            composerAttachments = composerAttachments.filter((item) => item.id !== attachment.id); composerStickerAttachmentIds.delete(attachment.id);
            composerAttachmentIds = composerAttachmentIds.filter((id) => id !== attachment.id);
            renderShell(); root.querySelector<MessageInput>('[aria-label="Сообщение"]')?.focus({ preventScroll: true });
          });
        });
        chip.append(remove); files.append(chip);
      }
      composer.append(files);
    }
    const tools = element('div', 'composer-tools');
    const mention = menuItem('Упомянуть', 'at', 'ghost'); mention.addEventListener('click', () => {
      // Types "@" at the caret and opens the same picker as typing it.
      const field = root.querySelector<MessageInput>('[aria-label="Сообщение"]'); if (!field || !ownsComposer()) return;
      const at = field.selectionStart ?? field.value.length; const prefix = at > 0 && !/\s$/.test(field.value.slice(0, at)) ? ' @' : '@';
      field.setRangeText(prefix, at, field.selectionEnd ?? at, 'end'); field.focus(); field.dispatchEvent(new Event('input'));
    });
    const attach = menuItem('Прикрепить файл', 'paperclip');
    const file = element('input'); file.type = 'file'; file.multiple = true; file.setAttribute('aria-label', 'Прикрепить файл');
    file.hidden = true;
    file.addEventListener('change', () => { if (ownsComposer()) void uploadFiles([...file.files ?? []]); });
    // Keep the native picker in the trusted click stack (no await/render first).
    attach.addEventListener('click', () => { if (ownsComposer()) file.click(); });
    const clear = menuItem('Очистить', 'eraser', 'ghost'); clear.addEventListener('click', () => {
      if (!ownsComposer()) return;
      clearStoredReply(); replyToMessageId = null; composerReplyTarget = null; composerMentionIds = []; composerMentionTokens.clear(); mentionPicker = null; composerAttachmentIds = []; composerStickerAttachmentIds.clear(); composerAttachments = []; renderShell();
      root.querySelector<MessageInput>('[aria-label="Сообщение"]')?.focus({ preventScroll: true });
    });
    const later = menuItem('Отправить позже', 'clock');
    later.addEventListener('click', () => { if (ownsComposer()) showScheduleDialog(); });
    tools.append(mention, attach, later, clear);
    const send = menuItem('', 'send', 'primary composer-send'); send.type = 'submit';
    send.append(element('span', 'composer-send-label', 'Отправить'));
    send.setAttribute('aria-label', 'Отправить'); send.title = 'Отправить';
    const actions = actionMenu('Действия с сообщением перед отправкой', `composer:${sessionGeneration}:${selectionGeneration}`, tools);
    actions.querySelector('button')!.textContent = '+';
    const emoji = menuItem('', 'smile', 'composer-emoji'); emoji.title = 'Эмодзи'; emoji.setAttribute('aria-label','Эмодзи');
    emoji.addEventListener('click', () => {
      if (!ownsComposer()) return;
      const start=input.selectionStart, end=input.selectionEnd;
      const restore=():void => { root.querySelector<MessageInput>('.composer .message-input')?.focus({preventScroll:true}); };
      showEmojiPicker(ownsComposer, value => {
        const field=root.querySelector<MessageInput>('.composer .message-input'); if (!field) return;
        field.setRangeText(value, Math.min(start,field.value.length), Math.min(end,field.value.length), 'end');
        field.dispatchEvent(new Event('input'));
      }, restore, () => showStickerPicker({
        valid:ownsComposer,request:api,restore,attach:async file=>{if(ownsComposer())await uploadFiles([file], true);},
        image:async url=>{
          const response=await fetch(url,{credentials:'same-origin',signal:AbortSignal.timeout(10_000)});
          if(!response.ok||response.headers.get('Content-Type')?.split(';')[0]!=='image/webp')throw new Error('Не удалось загрузить стикер');
          const blob=await response.blob();if(blob.size>262144)throw new Error('Стикер слишком большой');return blob;
        },
      }));
    });
    row.append(actions, input, send, emoji); composer.append(file, picker, row);
    if (!enterSends()) composer.append(element('span', 'editor-hint', 'Enter — новая строка · Ctrl/⌘ + Enter — отправить'));
    if (mentionPicker) renderMentionPicker(picker, input);
    composer.addEventListener('submit', (event) => { event.preventDefault(); if (ownsComposer()) void queueMessage(input.value); });
    fragment.append(composer);
  }
  const visible = selectionGuard();
  const anchor = historyScrollAnchor && historyScrollAnchor.conversationId === current.id ? historyScrollAnchor : null;
  const conversationId = current.id;
  // Applied synchronously by the caller right after `history` is attached to the live
  // document (not deferred via queueMicrotask): a deferred fix races the very next
  // renderShell() when several renders happen back-to-back (e.g. on first opening a
  // conversation), which could read this render's pre-fix scroll position as its own
  // "previous" state and misjudge whether the reader was at the bottom.
  const positionHistory = (viewport: HTMLElement): void => {
    // A mobile list hides the mounted chat; zero layout is not a reading position.
    if (!viewport.getClientRects().length) return;
    // A navigation highlight is transient decoration, not new message content. Apply it to
    // mounted nodes so a loaded permalink never rebuilds its bubble or decoded images.
    for (const node of viewport.querySelectorAll<HTMLElement>('.message[data-message-id]')) {
      const id = node.dataset.messageId!;
      node.classList.toggle('target', contextTargetId === id || location.pathname.endsWith(`/m/${id}`));
    }
    // The permalink may remain in the URL after an explicit jump to the tail.
    // Its visual highlight must not override the reader's new scroll request.
    const target = contextTargetId ? viewport.querySelector<HTMLElement>(`.message[data-message-id="${CSS.escape(contextTargetId)}"]`) : null;
    // A quote click or permalink jump reveals its target once per navigation. contextTargetId
    // otherwise stays set for the rest of the visit (catchUp() keeps reloading the same
    // context window around it), so without this key a later background refresh would find
    // the same `.message.target` and force the reader back to it even after they had already
    // scrolled away to read on.
    const revealKey = contextTargetId ? `${selectionGeneration}:${contextTargetId}` : null;
    if (target && revealedTargetKey !== revealKey) {
      target.scrollIntoView({ block: 'center' });
      revealedTargetKey = revealKey;
    } else if (requestedHistoryAnchor?.conversationId === conversationId) {
      const requested = requestedHistoryAnchor; requestedHistoryAnchor = null;
      if (requested.atBottom) viewport.scrollTop = viewport.scrollHeight;
      else restoreHistoryAnchor(viewport, requested);
    } else if (initialUnreadPosition === selectionGeneration && dividerBefore) {
      const divider = viewport.querySelector<HTMLElement>('.unread-divider');
      if (divider) viewport.scrollTop += (divider.getBoundingClientRect().top-viewport.getBoundingClientRect().top)/pageZoom();
      initialUnreadPosition = null;
    } else if (anchor && !anchor.atBottom) restoreHistoryAnchor(viewport, anchor);
    else viewport.scrollTop = viewport.scrollHeight;
  };
  // Reading the newest message clears the chat's unread counter. A permalink jump does NOT count
  // as reading -- the reader was sent to old history and everything after it is still unseen --
  // with one exception: when the jump target IS the newest message, which is exactly where a
  // notification click lands. Without that exception the badge kept sitting in the sidebar after
  // the reader had opened the very message it was counting.
  const sendReadReceipt = (): void => {
    const liveHistory = root.querySelector<HTMLElement>('#messages');
    if (!visible() || !liveHistory || !tabAttended() || mobileListVisible) return;
    const box = liveHistory.getBoundingClientRect();
    const lastNode = [...liveHistory.querySelectorAll<HTMLElement>('.message[data-message-id]')].filter(node => {
      const rect = node.getBoundingClientRect(); return rect.top < box.bottom && rect.bottom > box.top;
    }).at(-1);
    const last = messages.find(message => message.id === lastNode?.dataset.messageId);
    if (!last || last.pending) return;
    if (contextTargetId && (contextTargetId !== last.id || last.seq !== currentDetails?.last_message_seq)) return;
    const receipt = { conversationId, seq: last.seq, session: sessionGeneration, epoch: currentDetails?.membership_epoch_id ?? null };
    if (lastReadSent && lastReadSent.conversationId === receipt.conversationId && lastReadSent.seq === receipt.seq && lastReadSent.session === receipt.session && lastReadSent.epoch === receipt.epoch) return;
    lastReadSent = receipt;
    void api(`/v1/conversations/${receipt.conversationId}/read`, { method: 'POST', body: JSON.stringify({ seq: receipt.seq }) }).then(() => {
      // The server has the cursor; clear the row's badge now instead of leaving it until some
      // later event happens to refresh the sidebar.
      if (!visible()) return;
      if (currentDetails && currentDetails.membership_epoch_id === receipt.epoch && BigInt(receipt.seq) >= BigInt(currentDetails.last_message_seq ?? '0')) {
        const changed = conversations.some(row => row.id === receipt.conversationId && (row.unread > 0 || row.mentions > 0));
        conversationPages.markRead(receipt.conversationId, receipt.seq);
        conversations = conversationPages.snapshot().rows; // Only counters, never replace authorized detail.
        if (changed) renderShell();
        // Revalidate the all-chat count after the committed read; an off-page row is not a total basis.
        void loadConversations(visible, true).catch(() => undefined);
      }
    }).catch(() => {
      if (lastReadSent?.conversationId === receipt.conversationId && lastReadSent.seq === receipt.seq && lastReadSent.session === receipt.session && lastReadSent.epoch === receipt.epoch) lastReadSent = null;
    });
  };
  let previousHistoryScrollTop = history.scrollTop;
  const updateHistoryNavigation = (): void => {
    const distance = history.scrollHeight-history.scrollTop-history.clientHeight;
    // A bounded context can end before the conversation tail, even at its DOM bottom.
    navigationButton.hidden = !returning && history.dataset.atTail === 'true' && distance <= Math.max(240, history.clientHeight * 0.75);
    // Positioning/restoring a rendered history establishes a new baseline, not a user scroll.
    previousHistoryScrollTop = history.scrollTop;
  };
  let receiptTimer: number | undefined;
  const onHistoryScroll = (): void => {
    if (!visible() || !history.isConnected || !history.getClientRects().length) return;
    // A synchronous restore already recorded its anchor and actual rounded scrollTop.
    // Only a new scroll position establishes new reading intent.
    if (historyScrollAnchor?.conversationId !== conversationId || historyScrollAnchor.scrollTop !== history.scrollTop)
      historyScrollAnchor = captureHistoryAnchor(history, conversationId);
    const saved = historyReturnAnchor?.conversationId === conversationId ? historyReturnAnchor : null;
    if (saved?.messageId && saved.offset !== undefined && history.scrollTop < previousHistoryScrollTop) {
      const node = [...history.querySelectorAll<HTMLElement>('.message[data-message-id]')].find(item => item.dataset.messageId === saved.messageId);
      // Wheel/touch/scrollbar movement can cross the saved position between events.
      // Use its message's current geometry so prepends and resized rows do not shift the target.
      if (node && (node.getBoundingClientRect().top-history.getBoundingClientRect().top)/pageZoom() >= saved.offset - 2) {
        historyReturnAnchor = null;
        renderShell(); return;
      }
    }
    updateHistoryNavigation();
    if (bootstrap?.pm_task_previews?.requires_reauthentication) syncPmTaskPreviews();
    if (!historyWindowAtTail && history.scrollHeight-history.scrollTop-history.clientHeight < 48) {
      void loadNewerMessages().catch(error => toast(error.message));
    }
    window.clearTimeout(receiptTimer); receiptTimer = window.setTimeout(sendReadReceipt, 150);
  };
  const afterAttach = (): void => {
    if (!visible()) return;
    const mountedHistory = root.querySelector<HTMLDivElement>('#messages');
    if (!mountedHistory || mountedHistory.dataset.conversationId !== conversationId) return;
    history = mountedHistory;
    navigationButton = root.querySelector<HTMLButtonElement>('.history-jump')!;
    // Rebind to retained DOM with this render's guards, anchor and navigation state.
    const events = new AbortController();
    history.addEventListener('scroll', onHistoryScroll, { signal: events.signal });
    historyRenderCleanup = () => { events.abort(); window.clearTimeout(receiptTimer); };
    syncMessageAvatarRails(history);
    positionHistory(history);
    if (bootstrap?.pm_task_previews?.requires_reauthentication) syncPmTaskPreviews();
    if (history.getClientRects().length) historyScrollAnchor = captureHistoryAnchor(history, conversationId);
    historyResizeObserver = new ResizeObserver(() => {
      if (events.signal.aborted || !visible() || !history.isConnected || !history.getClientRects().length) return;
      syncMessageAvatarRails(history);
      if (!historyScrollAnchor || historyScrollAnchor.conversationId !== conversationId) return;
      if (historyScrollAnchor.atBottom) history.scrollTop = history.scrollHeight;
      else restoreHistoryAnchor(history, historyScrollAnchor);
      updateHistoryNavigation();
    });
    for (const row of history.children) historyResizeObserver.observe(row);
    historyResizeObserver.observe(history);
    updateHistoryNavigation();
    // A freshly mounted message box (restored draft, or empty again after sending) starts at its text's height.
    const input = document.querySelector<MessageInput>('.composer .message-input');
    if (input && !input.style.height) fitComposer(input);
    sendReadReceipt();
    refreshTodoSummary(isPersonalConversation());
  };
  return { fragment, afterAttach };
}

// Reacting shows up at once and is confirmed afterwards. The generic mutate() path waited for
// three round trips before anything moved (the write, a full history refetch, a sidebar refresh),
// which on a real network read as a laggy button; a reaction is a one-bit change we can draw
// immediately and take back if the server refuses.
// Local time in the input, ISO on the wire.
function scheduleInputValue(at: Date): string {
  const local = new Date(at.getTime() - at.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}
function scheduleLabel(iso: string): string {
  const at = new Date(iso);
  const today = new Date(); const tomorrow = new Date(today.getTime() + 24 * 3_600_000);
  const time = at.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  if (at.toDateString() === today.toDateString()) return `сегодня в ${time}`;
  if (at.toDateString() === tomorrow.toDateString()) return `завтра в ${time}`;
  return `${at.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })} в ${time}`;
}

async function scheduleCurrentMessage(at: Date, existing?: Scheduled): Promise<void> {
  const conversationId = current?.id;
  const input = root.querySelector<MessageInput>('[aria-label="Сообщение"]');
  const body = existing ? existing.body : (input?.value ?? '');
  if(!existing&&composerAttachments.some(attachment=>attachment.upload)){toast('Файлы из хранилища пока можно отправить только сразу.');return;}
  const lengthError = messageLengthError(body);
  if (lengthError) { toast(lengthError); return; }
  if (!conversationId || (!body.trim() && !composerAttachmentIds.length)) { toast('Сначала напишите сообщение'); return; }
  return runUiAction(async (request, valid) => {
    if (existing) {
      await request(`/v1/scheduled/${existing.id}`, { method: 'PATCH', body: JSON.stringify({ body, send_at: at.toISOString() }) });
    } else {
      await request(`/v1/conversations/${conversationId}/scheduled`, { method: 'POST', body: JSON.stringify({
        client_message_id: crypto.randomUUID(), body,
        reply_to_message_id: replyToMessageId, mentions: mentionsInText(body), attachment_ids: [...composerAttachmentIds],
        entities: composerAttachmentIds.filter(id=>composerStickerAttachmentIds.has(id)).map(id=>({type:'sticker',attachment_id:id})),
        send_at: at.toISOString(),
      }) });
      // The composer is now empty: what was in it is waiting instead.
      clearStoredReply(conversationId);
      if (input) { input.value = ''; try { localStorage.removeItem(draftKey(conversationId)); } catch { /* ignore */ } }
      replyToMessageId = null; composerReplyTarget = null; composerMentionIds = []; composerMentionTokens.clear(); composerAttachmentIds = []; composerStickerAttachmentIds.clear(); composerAttachments = [];
    }
    await loadScheduledMessages(valid);
    if (valid()) { renderShell(); toast(`Отправим ${scheduleLabel(at.toISOString())}`); }
  });
}

async function cancelScheduled(id: string): Promise<void> {
  return runUiAction(async (request, valid) => {
    await request(`/v1/scheduled/${id}`, { method: 'DELETE' });
    await loadScheduledMessages(valid);
    if (valid()) { renderShell(); toast('Отложенное сообщение отменено'); }
  });
}

// One dialog for "send later" and for changing the time of something already waiting.
function showScheduleDialog(existing?: Scheduled): void {
  if (activeEditor) return;
  const dialog = element('dialog', 'message-editor schedule-dialog');
  dialog.setAttribute('aria-label', existing ? 'Изменить время отправки' : 'Отправить позже');
  const valid = selectionGuard();
  const close = (): void => { if (activeEditor?.close === close) activeEditor = null; if (dialog.open) dialog.close(); dialog.remove(); };
  dialog.addEventListener('close', () => { if (activeEditor?.close === close) activeEditor = null; dialog.remove(); });
  activeEditor = { dialog, valid, close };
  const header = element('div', 'chat-info-head');
  header.append(element('h3', '', existing ? 'Изменить время отправки' : 'Отправить позже'));
  const closeButton = element('button', 'ghost', '✕'); closeButton.type = 'button';
  closeButton.setAttribute('aria-label', 'Закрыть'); closeButton.addEventListener('click', close);
  header.append(closeButton);
  const form = element('form', 'schedule-form');
  const quick = element('div', 'schedule-quick');
  const now = new Date();
  const evening = new Date(now); evening.setHours(18, 0, 0, 0);
  const morning = new Date(now.getTime() + 24 * 3_600_000); morning.setHours(9, 0, 0, 0);
  const field = element('input'); field.type = 'datetime-local'; field.setAttribute('aria-label', 'Дата и время отправки');
  field.value = scheduleInputValue(existing ? new Date(existing.send_at) : new Date(now.getTime() + 3_600_000));
  field.min = scheduleInputValue(new Date(now.getTime() + 60_000));
  for (const [label, at] of [['Через час', new Date(now.getTime() + 3_600_000)], ['Сегодня в 18:00', evening], ['Завтра в 9:00', morning]] as const) {
    if (at.getTime() <= now.getTime()) continue;
    const button = element('button', 'ghost', label); button.type = 'button';
    button.addEventListener('click', () => { field.value = scheduleInputValue(at); });
    quick.append(button);
  }
  const actions = element('div', 'notification-actions');
  const submit = element('button', '', existing ? 'Сохранить время' : 'Отправить позже'); submit.type = 'submit';
  const cancel = element('button', 'ghost', 'Отмена'); cancel.type = 'button'; cancel.addEventListener('click', close);
  actions.append(submit, cancel);
  form.append(element('p', 'meta', 'Сообщение подождёт и уйдёт само. До этого его видите только вы.'), quick, field, actions);
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    const at = new Date(field.value);
    if (Number.isNaN(at.getTime())) { toast('Укажите дату и время'); return; }
    close();
    void scheduleCurrentMessage(at, existing);
  });
  dialog.append(header, form);
  document.body.append(dialog);
  dialog.showModal();
}

function navigationWouldLoseAttachmentWork(): boolean {
  return Boolean(composerUploads.length || composerAttachments.length || savingPending.size);
}

function pinPreview(message: Message): string {
  const author = message.sender?.name ? `${message.sender.name}: ` : '';
  const body = message.body?.trim() || (message.attachments?.length ? 'Вложение' : 'Сообщение');
  return `${author}${body.replace(/\s+/g, ' ').slice(0, 120)}`;
}

function canPinHere(): boolean {
  return !current?.archived_at && (current?.type === 'direct' || ['owner', 'admin'].includes(currentDetails?.role ?? ''));
}

// Pinning has to refresh the bar as well as the message, so it does not go through mutate().
async function togglePin(messageId: string, pinned: boolean): Promise<void> {
  const command = currentDetails?.shared_room ? {attempt_id:crypto.randomUUID(),membership_epoch_id:currentDetails.membership_epoch_id} : {};
  return runUiAction(async (request, valid) => {
    await request(`/v1/messages/${messageId}/pin`, { method: pinned ? 'DELETE' : 'PUT', body: JSON.stringify(command) });
    if (!valid() || !current) return;
    pinnedBarPage = 0;
    await Promise.all([loadMessages(current.id), loadConversationPins(valid)]);
    if (valid()) renderShell();
  });
}

async function toggleReaction(messageId: string, emoji: string): Promise<void> {
  // Looked up at click time, never captured: a retained DOM node keeps the listener from an
  // earlier render, and by now the history has been replaced by fresh objects.
  const message = messages.find((item) => item.id === messageId);
  if (!bootstrap || !message || message.pending) return;
  const me = ownConversationAuthorId(message.conversation_id);
  if (!me) return;
  const before = message.reactions ?? [];
  const had = before.some((item) => item.user_id === me && item.emoji === emoji);
  message.reactions = had
    ? before.filter((item) => !(item.user_id === me && item.emoji === emoji))
    : [...before, { emoji, user_id: me }];
  const command = currentDetails?.shared_room ? {emoji,attempt_id:crypto.randomUUID(),membership_epoch_id:currentDetails.membership_epoch_id} : {emoji};
  renderShell();
  return runUiAction(async (request, valid) => {
    try {
      await request(`/v1/messages/${message.id}/reactions`, { method: had ? 'DELETE' : 'PUT', body: JSON.stringify(command) });
    } catch (error) {
      message.reactions = before;
      if (valid()) renderShell();
      throw error;   // runUiAction turns it into the toast
    }
    // The conversation's own event carries the authoritative list; no full history refetch here.
    if (valid() && current) void catchUp(current.id);
  });
}

// Reactions the chat offers, in the order people reach for them.
const REACTIONS = ['👍', '👎', '❤️', '😠', '😮', '😂'] as const;
const REACTION_LABELS: Record<string, string> = { '👍': 'Нравится', '👎': 'Не нравится', '❤️': 'Сердечко', '😠': 'Злюсь', '😮': 'Удивлён', '😂': 'Смеюсь' };

// Sits next to the bubble rather than inside the "⋯" menu: reacting is a one-glance action, and
// burying it two clicks deep is what the menu is for.
function reactionPicker(message: Message): HTMLElement {
  const choices = element('div');
  for (const emoji of REACTIONS) {
    const own = message.reactions?.some((item) => item.user_id === ownConversationAuthorId(message.conversation_id) && item.emoji === emoji) ?? false;
    const choice = element('button', `reaction-choice${own ? ' own' : ''}`, emoji);
    choice.type = 'button';
    choice.setAttribute('aria-label', `${own ? 'Убрать' : 'Поставить'}: ${REACTION_LABELS[emoji] ?? emoji}`);
    choice.title = REACTION_LABELS[emoji] ?? emoji;
    choice.addEventListener('click', () => void toggleReaction(message.id, emoji));
    choices.append(choice);
  }
  const more = element('button', 'reaction-choice', '…'); more.type = 'button';
  more.setAttribute('aria-label', 'Другие реакции'); more.title = 'Другие реакции';
  const ownsSelection = selectionGuard();
  more.addEventListener('click', () => {
    const valid = (): boolean => ownsSelection() && messages.some(item => item.id === message.id && !item.deleted_at && !item.pending);
    showEmojiPicker(valid, emoji => void toggleReaction(message.id, emoji), () => {
      root.querySelector<HTMLButtonElement>(`[data-menu-key="reaction:${sessionGeneration}:${selectionGeneration}:${message.id}"]`)?.focus({ preventScroll: true });
    });
  });
  choices.append(more);
  const host = actionMenu('Реакция на сообщение', `reaction:${sessionGeneration}:${selectionGeneration}:${message.id}`, choices, 'reaction-trigger', '');
  host.classList.add('reaction-menu');
  // An outlined thumb rather than a yellow face: the button offers reactions, it is not one.
  const trigger = host.querySelector('.reaction-trigger');
  if (trigger) trigger.innerHTML = '<svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M7 10.5V21"/><path d="M14.5 5.9 13.6 9.8h5.6a1.9 1.9 0 0 1 1.85 2.45l-2.2 7.6A1.9 1.9 0 0 1 17 21H4.3A1.3 1.3 0 0 1 3 19.7v-8a1.3 1.3 0 0 1 1.3-1.3h2.4a1.9 1.9 0 0 0 1.7-1.05L11.6 3a2.9 2.9 0 0 1 2.9 2.9Z"/></svg>';
  return host;
}

// Buttons a bot hung under its message. A link button is a link; a callback button asks the bot and
// waits for its answer, because that is the whole point of pressing it -- the person is standing in
// front of the screen. The press carries where the button is, not what it says: the payload belongs
// to the bot, and the server reads it from the stored keyboard.
function renderKeyboard(message: Message): HTMLElement {
  const keyboard = element('div', 'keyboard');
  (message.keyboard ?? []).forEach((buttons, row) => {
    const line = element('div', 'keyboard-row');
    buttons.forEach((button, index) => {
      const style = `keyboard-button${button.style && button.style !== 'base' ? ` keyboard-${button.style}` : ''}`;
      if (button.url) {
        const link = element('a', style, button.text);
        link.href = button.url; link.target = '_blank'; link.rel = 'noopener noreferrer';
        line.append(link);
        return;
      }
      const press = element('button', style, button.text); press.type = 'button';
      press.dataset.pressKey = pressKey(message.id, row, index);
      // A render during the wait must not hand back an enabled button.
      if (pressedButtons.has(press.dataset.pressKey)) { press.disabled = true; press.classList.add('waiting'); }
      press.addEventListener('click', () => void pressKeyboardButton(message.id, row, index, press));
      line.append(press);
    });
    keyboard.append(line);
  });
  return keyboard;
}

// Which button is waiting for its bot, kept here rather than on the node: any render replaces the
// node, and a waiting state living on it came back enabled mid-wait -- so a second press could ask
// the bot the same question twice.
const pressedButtons = new Set<string>();
const pressKey = (messageId: string, row: number, index: number): string => `${messageId}:${row}:${index}`;

async function pressKeyboardButton(messageId: string, row: number, index: number, button: HTMLButtonElement): Promise<void> {
  const valid = selectionGuard();
  const key = pressKey(messageId, row, index);
  if (pressedButtons.has(key)) return;
  pressedButtons.add(key);
  button.disabled = true; button.classList.add('waiting');
  try {
    const { query_id } = await api<{ query_id: string }>(`/v1/messages/${encodeURIComponent(messageId)}/callback`,
      { method: 'POST', body: JSON.stringify({ row, index }) });
    const answer = await api<{ answered: boolean; text?: string; show_alert?: boolean; url?: string | null }>(
      `/v1/callbacks/${encodeURIComponent(query_id)}`);
    if (!valid()) return;
    if (!answer.answered) { toast('Бот не ответил'); return; }
    if (answer.url) { window.open(answer.url, '_blank', 'noopener,noreferrer'); return; }
    if (!answer.text) return;
    if (answer.show_alert) alert(answer.text); else toast(answer.text);
  } catch (error) {
    if (valid()) toast(error instanceof Error ? error.message : 'Не удалось нажать кнопку');
  } finally {
    pressedButtons.delete(key);
    // The node this started on may be long gone; the current one is found by where the button is.
    for (const node of root.querySelectorAll<HTMLButtonElement>(`.message[data-message-id="${CSS.escape(messageId)}"] .keyboard-button`)) {
      if (node.dataset.pressKey === key) { node.disabled = false; node.classList.remove('waiting'); }
    }
  }
}

function renderMessage(message: Message, readers: ReadCursor[] = []): HTMLElement {
  if (!bootstrap) return element('article');
  const mine = message.sender?.id === ownConversationAuthorId(message.conversation_id);
  if (message.deleted_at && message.kind !== 'system') {
    // A deleted message reads like a system notice: centred, muted, no bubble/author/menu.
    const gone = element('article', 'message system deleted', 'Сообщение удалено');
    gone.dataset.messageId = message.id; gone.dataset.selection = String(selectionGeneration); gone.dataset.version = String(message.version);
    if(mine&&!message.retained_native&&!current?.archived_at&&restoreNow&&restoreTimeLeft(message.restore_until,restoreNow())>0){
      const restore=element('button','ghost message-restore','Восстановить');restore.type='button';
      restore.dataset.restoreUntil=message.restore_until!;restore.dataset.restoreKey=restoreKey(message);
      restore.title=`Восстановить до ${fullStamp(new Date(message.restore_until!))} — в течение часа после удаления`;
      restore.disabled=restoringMessages.has(restoreKey(message));
      const valid=selectionGuard();restore.addEventListener('click',()=>{if(valid())void restoreMessageFromUi(message);});gone.append(restore);
    }
    gone.title = `${message.sender?.name ?? 'Сообщение'} · ${new Date(message.created_at).toLocaleString([], { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}`;
    return gone;
  }
  const node = element('article', `message ${message.kind === 'system' ? 'system' : mine ? 'mine' : ''}${message.pending ? ' pending' : ''}`);
  if (isStickerOnlyMessage(message)) node.classList.add('sticker-only');
  node.dataset.messageId = message.id; node.dataset.selection = String(selectionGeneration); node.dataset.version = String(message.version);
  node.setAttribute('aria-label', message.sender ? `Сообщение от ${message.sender.name}` : 'Системное сообщение');
  if (message.attachments?.some(attachment => (attachment.mime.startsWith('video/')||attachment.mime==='audio/mpeg'))) node.dataset.mediaOwner = JSON.stringify([message.version, message.body, message.body_html, message.attachments]);
  const top = element('div', 'message-top');
  const sentAt = new Date(message.created_at);
  const time = element('time', '', messageTime(sentAt));
  time.dateTime = message.created_at; time.title = fullStamp(sentAt);
  // User-message timestamps share the action menu; other timestamps retain date details.
  if (message.kind !== 'user' || message.pending)
    time.addEventListener('contextmenu', (event) => { event.preventDefault(); toast(fullStamp(sentAt)); });
  top.append(element('span', 'message-author', message.sender?.name ?? 'Системное сообщение'));
  // Keep metadata under the same memoized article owner; only its presentation
  // follows the last content line, so media and existing action handlers stay mounted.
  const metadata = message.kind === 'system' ? top : element('div', 'message-side');
  metadata.append(time);
  const indicators = message.kind === 'system' ? node : element('div', 'message-indicators');
  const content = element('div', 'message-body');
  if (message.deleted_at) content.textContent = 'Сообщение удалено';
  else if (message.body_html && !message.pending) {
    content.innerHTML = message.body_html;
    // The server marks every mention with the person's id but does not know who is reading, so being
    // addressed yourself is marked here.
    const me = bootstrap?.user.id;
    if (me) for (const ref of content.querySelectorAll(`.mention-ref[data-mention="${CSS.escape(me)}"]`)) ref.classList.add('mention-ref-self');
  } else content.textContent = message.body;
  node.append(top);
  // Rendered above the message body (not below): a reply reads top-to-bottom as "quoting
  // this, then saying this", matching how the user reads a quote-reply.
  if (message.reply_to_message_id) {
    const source = message.replySource;
    const preview = element('div', 'meta reply-source');
    // The server renders each quoted line separately. Join this run into one card,
    // preserving the sanitized nodes and the author's name as written at quote time.
    let quote = content.querySelector('blockquote');
    let linkedQuoteTarget: string | null = null;
    if (quote) {
      quote.classList.add('reply-quote');
      if (quote.textContent?.endsWith(':')) {
        const author = element('span', 'quote-author');
        author.append(...quote.childNodes); quote.append(author);
      }
      while (quote.nextSibling?.nodeName === 'BR' && quote.nextSibling.nextSibling?.nodeName === 'BLOCKQUOTE') {
        const separator = quote.nextSibling;
        const line = separator.nextSibling!;
        quote.append(document.createElement('br'), ...line.childNodes);
        separator.remove(); line.parentNode?.removeChild(line);
      }
      const quoteLink = quote.querySelector<HTMLAnchorElement>('a[href*="/m/"]');
      if (quoteLink) { linkedQuoteTarget = parseChatRoute(quoteLink.pathname, bootstrap.user.workspace_id)?.targetId ?? null; quote = null; }
      else preview.classList.add('reply-source-quote');
    }
    if (source?.state === 'available' || source?.state === 'deleted') {
      const jump = element('button', '', source.state === 'deleted' ? 'Сообщение удалено' : quote ? '↗ К исходному сообщению' : `Ответ: ${source.author ? `${source.author}: ` : ''}${source.text}`);
      jump.type = 'button'; jump.setAttribute('aria-label', `Перейти к исходному сообщению: ${jump.textContent}`);
      const valid = selectionGuard();
      jump.addEventListener('click', () => {
        if (valid() && jump.isConnected && current?.id === message.conversation_id)
          void openConversation(message.conversation_id, message.reply_to_message_id!);
      });
      if (!quote && !message.deleted_at && source.state === 'available') appendReplyImages(jump, source.images);
      preview.append(jump);
    } else preview.textContent = source?.state === 'unavailable' ? 'Сообщение недоступно' : 'Не удалось загрузить исходное сообщение';
    // The API carries one reply target. With several separate quotation blocks,
    // keep its link at message level rather than attributing it to the wrong block.
    if (linkedQuoteTarget !== message.reply_to_message_id) (quote && content.querySelectorAll('blockquote').length === 1 ? quote : node).append(preview);
  }
  if (message.forwarded_from && !message.deleted_at) {
    // Above the words, because that is the order they are read in: whose they are, then what they
    // say. The time is the original's -- when it was said, not when it was passed on.
    const from = message.forwarded_from;
    const when = from.at ? new Date(from.at) : null;
    const line = element('div', 'meta forwarded-from',
      `Переслано от ${from.name ?? 'участника'}${when ? ` · ${editedLabel(when)}` : ''}`);
    node.append(line);
  }
  node.append(message.poll && !message.deleted_at ? renderPoll(message, message.poll) : content);
  if (!message.pending && !message.deleted_at && !message.poll && bootstrap.pm_task_previews?.enabled) {
    const tasks = pmTaskLinks(content);
    if (tasks.length) {
      const cards = element('div', 'pm-task-cards');
      for (const task of tasks) {
        const slot = element('div', 'pm-task-slot'); slot.dataset.taskId = task.id;
        slot.dataset.messageId = message.id; slot.dataset.messageVersion = String(message.version); cards.append(slot);
      }
      node.append(cards);
    }
  }
  if (!message.pending && !message.deleted_at && !message.poll && bootstrap.files_preview_identity) {
    const ids=fileLinks(content);
    if(ids.length){const cards=element('div','files-cards');for(const id of ids){const slot=element('div','files-card-slot');slot.dataset.fileId=id;slot.dataset.messageId=message.id;slot.dataset.messageVersion=String(message.version);cards.append(slot);}node.append(cards);}
  }
  if (message.keyboard?.length && !message.deleted_at) node.append(renderKeyboard(message));
  if (message.pinned) {
    const pin = element('span', 'pin-state', message.kind === 'system' ? 'Закреплено' : '📌');
    pin.title = 'Закреплено'; pin.setAttribute('aria-label', 'Закреплено'); indicators.append(pin);
  }
  // No "Упомянуты: …" line any more: the address is highlighted in the text itself, where it says who
  // is meant at the place they are meant (owner's decision, 27.09.2026). The list under the message
  // repeated the same names a second time, away from the sentence they belonged to.
  for(const attachment of message.attachments??[])node.append(...messageAttachmentNodes(attachment,message));
  if (message.edited_at && !message.deleted_at) {
    const edited = new Date(message.edited_at);
    const mark = element('span', 'meta edited-mark', 'изменено');
    mark.title = `Изменено ${edited.toLocaleString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`;
    mark.setAttribute('aria-label', mark.title);
    indicators.append(mark);
  }
  // Each reaction retains its own action and title in the external left strip.
  const reactionUsers = new Map<string, string[]>();
  for (const reaction of message.reactions ?? []) reactionUsers.set(reaction.emoji, [...(reactionUsers.get(reaction.emoji) ?? []), reaction.user_id]);
  if (reactionUsers.size) {
    const row = element('div', 'message-reactions');
    row.tabIndex = 0; row.setAttribute('role', 'group'); row.setAttribute('aria-label', 'Реакции сообщения');
    for (const [emoji, userIds] of reactionUsers) {
      const own = userIds.includes(ownConversationAuthorId(message.conversation_id) ?? '');
      const chip = element('button', `reaction${own ? ' own' : ''}`);
      chip.type = 'button';
      chip.append(element('span', 'reaction-emoji', emoji), element('span', 'reaction-count', String(userIds.length)));
      const names = userIds.map((id) => userNameInConversation(message.conversation_id, id) ?? 'Участник')
        .sort((a, b) => Number(b === 'Вы') - Number(a === 'Вы') || a.localeCompare(b, 'ru'));
      chip.title = names.join(', ');
      chip.setAttribute('aria-label', `${own ? 'Убрать реакцию' : 'Поставить реакцию'} ${emoji}: ${names.join(', ')}`);
      if (!message.pending) chip.addEventListener('click', () => void toggleReaction(message.id, emoji));
      row.append(chip);
    }
    node.append(row);
  }
  if (readers.length) {
    const row = element('div', 'read-receipts');
    const shown = readers.slice(0, 5); const overflow = readers.slice(5);
    for (const reader of shown) {
      const avatar = avatarNode(reader.display_name, reader.avatar_key ? userAvatarUrl(reader.id, reader.avatar_key) : null, 'avatar read-receipt-avatar');
      linkAvatarToDirect(avatar, reader.id, reader.display_name);
      row.append(avatar);
    }
    if (overflow.length) {
      const more = element('span', 'read-receipt-more', `+${overflow.length}`);
      more.title = overflow.map((reader) => reader.display_name).join(', ');
      row.append(more);
    }
    // Readers stay beneath the right edge; reactions use the left edge.
    node.append(row);
  }
  if (message.kind === 'user' && !message.pending) {
    const actions = element('div', 'message-actions');
    const copy = menuItem('Ссылка', 'link'); copy.addEventListener('click', () => void copyLink(message.permalink));
    const reply = menuItem('Ответить', 'reply'); reply.addEventListener('click', () => {
      setReplyTarget(message); renderShell();
      const input = root.querySelector<MessageInput>('.composer .message-input');
      input?.focus({ preventScroll: true });
      input?.setSelectionRange(input.value.length, input.value.length);
    });
    const forward = menuItem('Переслать', 'forward'); forward.addEventListener('click', () => showForwardDialog(message));
    const quote = menuItem('Цитировать', 'quote'); quote.addEventListener('click', () => quoteMessage(message));
    const pin = menuItem(message.pinned ? 'Открепить' : 'Закрепить', 'pin');
    pin.addEventListener('click', () => { pin.disabled = true; void togglePin(message.id, Boolean(message.pinned)).finally(() => { pin.disabled = false; }); });
    const readers = menuItem('Прочитали', 'eye'); readers.addEventListener('click', () => void showReaders(message.id));
    const sharedMutation = currentDetails?.id === message.conversation_id && currentDetails.shared_room;
    const moderator = currentDetails?.id===message.conversation_id && canModerateMessages(currentDetails);
    if (!message.retained_native && !message.deleted_at && (mine || moderator)) {
      if (mine && !message.poll) {
        const edit = menuItem('Изменить', 'pencil'); edit.addEventListener('click', () => void editMessageFromUi(message)); actions.append(edit);
      }
      if(bootstrap.managed_files_enabled && message.attachments?.some(file=>file.managed)) {
        const unlink=menuItem('Убрать вложение','trash');unlink.addEventListener('click',()=>showManagedAttachmentRemoval(message));actions.append(unlink);
      }
      const remove = menuItem('Удалить', 'trash');
      const path = sharedMutation ? `/v1/shared-rooms/${message.conversation_id}/messages/${message.id}` : `/v1/messages/${message.id}`;
      const command = {version:message.version,...(sharedMutation?{attempt_id:crypto.randomUUID(),membership_epoch_id:currentDetails!.membership_epoch_id}:{})};
      remove.addEventListener('click', () => void mutate(path, 'DELETE', command)); actions.append(remove);
    }
    if (!message.deleted_at) {
      const personalTask = menuItem('В мои задачи', 'check'); personalTask.addEventListener('click', () => void addMessageTask(message, true));
      actions.append(personalTask);
      if (!current?.archived_at) {
        const chatTask = menuItem('В задачи чата', 'check'); chatTask.addEventListener('click', () => void addMessageTask(message, false)); actions.append(chatTask);
      }
    }
    actions.append(copy);
    if (!current?.archived_at && (current?.type === 'direct' || ['owner', 'admin'].includes(currentDetails?.role ?? ''))) actions.append(pin);
    actions.append(readers, forward, quote, reply);
    const menu = actionMenu('Действия с сообщением', `message:${sessionGeneration}:${selectionGeneration}:${message.id}`, actions, 'message-more', '⋯');
    // One native trigger makes both the timestamp and ellipsis open the same
    // menu, with its existing keyboard, focus and per-message ownership rules.
    const trigger = menu.querySelector<HTMLButtonElement>('.menu-trigger')!;
    trigger.replaceChildren(element('span', 'message-menu-dots', '⋯'), time);
    trigger.setAttribute('aria-description', fullStamp(sentAt));
    metadata.append(menu);
  }
  if (message.pending) {
    // A sending message keeps its separate cancel action; its timestamp cannot cancel a send.
    const cancel = element('button', 'pending-cancel', '✕'); cancel.type = 'button';
    cancel.setAttribute('aria-label', 'Отменить');
    cancel.title = 'Отменить отправку. Удаляет только локальную очередь и не отменяет сообщение, уже сохранённое сервером.';
    cancel.addEventListener('click', () => void cancelPending(message.id));
    metadata.insertBefore(cancel, time);
    if (message.error) {
      // A failed send is a real problem the user must see, so only this state adds a row. No live
      // region here: the sidebar's queue notice is the one role=alert for pending failures.
      const failed = element('div', 'error pending-error');
      const retry = element('button', 'ghost', 'Повторить отправку'); retry.type = 'button';
      retry.disabled = Boolean(legacyPending?.account === bootstrap && legacyPending.valid() && legacyPending.items.some(item => item.client_message_id === message.id && item.quarantined));
      retry.addEventListener('click', () => void retryPending(message.id));
      failed.append(element('span', '', message.error), retry);
      node.append(failed);
    }
  }
  if (metadata !== top) {
    if (indicators.childElementCount) {
      indicators.tabIndex = 0;
      indicators.setAttribute('role', 'group');
      indicators.setAttribute('aria-label', 'Отметки сообщения');
      metadata.prepend(indicators);
    }
    // Reply/forward headers precede the body and must not force an extra footer.
    // Only actual following content (cards/media/errors) needs its own footer;
    // external reactions/readers do not participate in the text layout.
    let trailingContent = false;
    for (let sibling = content.nextElementSibling; sibling; sibling = sibling.nextElementSibling) {
      if (!sibling.matches('.message-reactions,.read-receipts')) { trailingContent = true; break; }
    }
    if (!message.poll && !trailingContent) {
      const lastBlock = content.lastElementChild;
      (lastBlock?.matches('.md-list-item') ? lastBlock : content).append(metadata);
    }
    else { metadata.classList.add('message-footer'); node.append(metadata); }
  }
  return node;
}

function draftKey(conversationId: string): string {
  return `pm-chat:draft:${bootstrap?.user.workspace_id}:${bootstrap?.user.id}:${conversationId}`;
}

// Store only the reference, never another author's text. The existing draft prefix
// also makes logout remove these entries. The membership epoch prevents old grants
// from restoring an answer after rejoining a conversation.
function clearStoredReply(conversationId = current?.id): void {
  if (!conversationId) return;
  try { localStorage.removeItem(`${draftKey(conversationId)}:reply`); } catch { /* browser storage unavailable */ }
}
function setReplyTarget(message: Message): void {
  if (!current || message.conversation_id !== current.id || message.deleted_at) return;
  replyToMessageId = message.id; composerReplyTarget = message;
  try { localStorage.setItem(`${draftKey(current.id)}:reply`, JSON.stringify({ id: message.id, epoch: currentDetails?.membership_epoch_id })); } catch { /* browser storage unavailable */ }
}
async function restoreDraftReply(conversationId: string, valid: () => boolean): Promise<void> {
  if (!valid() || current?.id !== conversationId || !currentDetails) return;
  const key = `${draftKey(conversationId)}:reply`; let saved: string | null;
  try { saved = localStorage.getItem(key); } catch { return; }
  if (!saved) return;
  let ref: { id?: unknown; epoch?: unknown };
  try { ref = JSON.parse(saved); } catch { clearStoredReply(conversationId); return; }
  if (!ref || typeof ref.id !== 'string' || !ref.id || ref.id.length > 100 || ref.epoch !== currentDetails.membership_epoch_id) { clearStoredReply(conversationId); return; }
  const epoch = currentDetails.membership_epoch_id;
  const owns = (): boolean => {
    if (!valid() || current?.id !== conversationId || currentDetails?.membership_epoch_id !== epoch) return false;
    try { return localStorage.getItem(key) === saved; } catch { return false; }
  };
  // Even a warm cached row is not proof that the reply source still exists. Fetch
  // a bounded context under the freshly authorized membership before binding it.
  try {
    const { target } = await api<{ target: Message }>(`/v1/conversations/${conversationId}/messages/context?message_id=${encodeURIComponent(ref.id)}&before=0&after=0`);
    if (!owns()) return;
    if (!target || target.id !== ref.id || target.conversation_id !== conversationId || target.deleted_at) { clearStoredReply(conversationId); return; }
    replyToMessageId = target.id; composerReplyTarget = target;
  } catch (error) {
    if (!owns()) return;
    if ([403,404].includes((error as { status?: number })?.status ?? 0)) clearStoredReply(conversationId);
    // Ambiguous network/server errors leave the reference for a later retry. Own
    // draft text is never removed by a failed reply validation.
  }
}

async function copyLink(link: string): Promise<void> {
  const valid = selectionGuard();
  try { await navigator.clipboard.writeText(link); if (!valid()) return; toast('Ссылка скопирована'); }
  catch {
    if (!valid()) return;
    const input = element('textarea'); input.value = link; root.append(input); input.select();
    const copied = document.execCommand('copy'); input.remove(); toast(copied ? 'Ссылка скопирована' : 'Скопируйте ссылку вручную');
  }
}

// Poll card: question, then per option a radio, votes and percent, a bar, and the voters as mini
// avatars under it (newest 5, then "…" opening the full list). Everyone sees who voted for what;
// picking another option changes the vote.
const POLL_AVATARS = 5;
// Which "…" voter lists are open: renderShell() rebuilds the history on every live event (typing,
// votes…), which would otherwise snap an open list shut under the reader.
const openPollVoterLists = new Set<string>();
function renderPoll(message: Message, poll: Poll): HTMLElement {
  const card = element('div', 'poll'); card.setAttribute('role', 'group'); card.setAttribute('aria-label', `Голосование: ${poll.question}`);
  const total = poll.options.reduce((sum, option) => sum + option.voters.length, 0);
  const mine = poll.options.findIndex((option) => option.voters.some((voter) => voter.id === bootstrap?.user.id));
  card.append(element('div', 'poll-question', poll.question));
  poll.options.forEach((option, index) => {
    const count = option.voters.length; const percent = total ? Math.round((count / total) * 100) : 0;
    const row = element('div', `poll-option${index === mine ? ' mine' : ''}`);
    const label = element('label', 'poll-choice');
    const radio = element('input'); radio.type = 'radio'; radio.name = `poll-${message.id}`; radio.defaultChecked = index === mine;
    radio.disabled = Boolean(message.pending || current?.archived_at);
    radio.addEventListener('change', () => { if (radio.checked) void mutate(`/v1/messages/${message.id}/vote`, 'PUT', { option: index }); });
    label.append(radio, element('span', 'poll-text', option.text), element('span', 'poll-count', `${count} · ${percent}%`));
    const bar = element('div', 'poll-bar'); bar.setAttribute('aria-hidden', 'true');
    const fill = element('div', 'poll-fill'); fill.style.width = `${percent}%`; bar.append(fill);
    row.append(label, bar);
    if (count) {
      const voters = element('div', 'poll-voters'); voters.setAttribute('aria-label', `Проголосовали за «${option.text}»`);
      for (const voter of option.voters.slice(0, POLL_AVATARS)) {
        const avatar = avatarNode(voter.name, voter.avatar_key ? userAvatarUrl(voter.id, voter.avatar_key) : null, 'avatar poll-avatar');
        linkAvatarToDirect(avatar, voter.id, voter.name); voters.append(avatar);
      }
      if (count > POLL_AVATARS) {
        const more = element('details', 'poll-more');
        const key = `${message.id}:${index}`; more.open = openPollVoterLists.has(key);
        const summary = element('summary', '', '…');
        // Recorded on click (synchronous, before the default toggle): the async `toggle` event can
        // arrive after a re-render has already replaced this element.
        summary.addEventListener('click', () => { if (more.open) openPollVoterLists.delete(key); else openPollVoterLists.add(key); }); summary.setAttribute('aria-label', `Все проголосовавшие: ${count}`); summary.title = `Все проголосовавшие (${count})`;
        const all = element('ul', 'poll-all'); for (const voter of option.voters) all.append(element('li', '', voter.name));
        more.append(summary, all); voters.append(more);
      }
      row.append(voters);
    }
    card.append(row);
  });
  card.append(element('div', 'meta poll-total', total ? `Проголосовало: ${total}` : 'Пока никто не голосовал'));
  return card;
}

function showManagedAttachmentRemoval(message:Message):void {
  if(!bootstrap?.managed_files_enabled||message.retained_native||message.deleted_at||message.pending)return;
  const valid=selectionGuard(),dialog=element('dialog','message-editor');dialog.setAttribute('aria-label','Убрать вложение');
  const error=element('p','error');error.setAttribute('role','alert');
  dialog.append(element('h3','','Убрать вложение'));
  const close=element('button','ghost','Закрыть');close.type='button';close.addEventListener('click',()=>dialog.close());dialog.addEventListener('close',()=>dialog.remove());
  for(const attachment of message.attachments??[]){
    const command=managedAttachmentUnlinkCommand(attachment,message.version,true);if(!command)continue;
    const remove=element('button','ghost',attachment.name);remove.type='button';remove.title='Убрать из сообщения';
    remove.addEventListener('click',()=>{
      if(!valid()||remove.disabled)return;
      const controls=[...dialog.querySelectorAll<HTMLButtonElement>('button')];controls.forEach(button=>button.disabled=true);error.textContent='';
      void (async()=>{try{
        await api(command.path,{method:'DELETE',body:JSON.stringify(command.body)});
        if(!valid()||!dialog.isConnected)return;
        dialog.close();await catchUp(message.conversation_id);
      }catch(failure){if(valid()&&dialog.isConnected){error.textContent=errorMessage(failure,'Не удалось убрать вложение');controls.forEach(button=>button.disabled=false);}}
      })();
    });dialog.append(remove);
  }
  dialog.append(error,close);document.body.append(dialog);dialog.showModal();close.focus();
}

function restoreKey(message:Message):string{return `${message.conversation_id}:${message.id}:${message.version}`;}

// One deadline timer for the mounted history; expiry never rebuilds message DOM.
function refreshRestoreButtons():void{
 if(restoreDeadlineTimer!==null)window.clearTimeout(restoreDeadlineTimer);restoreDeadlineTimer=null;
 if(!restoreNow||!bootstrap)return;
 let next=Infinity;
 for(const button of root.querySelectorAll<HTMLButtonElement>('.message-restore')){
  const left=restoreTimeLeft(button.dataset.restoreUntil,restoreNow());
  button.hidden=left<=0;button.disabled=left<=0||restoringMessages.has(button.dataset.restoreKey??'');
  if(left>0)next=Math.min(next,left);
 }
 if(Number.isFinite(next))restoreDeadlineTimer=window.setTimeout(refreshRestoreButtons,Math.min(60_000,Math.max(1,next)));
}

async function restoreMessageFromUi(message:Message):Promise<void>{
 if(!bootstrap||current?.id!==message.conversation_id||!currentDetails||currentDetails.id!==current.id
  ||current.archived_at||message.retained_native||!message.deleted_at||!restoreNow
  ||restoreTimeLeft(message.restore_until,restoreNow())<=0)return;
 const key=restoreKey(message);if(restoringMessages.has(key))return;
 const valid=selectionGuard(),epoch=currentDetails.membership_epoch_id;
 const allowed=()=>valid()&&currentDetails?.id===message.conversation_id&&currentDetails.membership_epoch_id===epoch;
 const shared=currentDetails.shared_room;
 const path=shared?`/v1/shared-rooms/${message.conversation_id}/messages/${message.id}/restore`:`/v1/messages/${message.id}/restore`;
 const command={version:message.version,...(shared?{attempt_id:crypto.randomUUID(),membership_epoch_id:epoch}:{})};
 let revision:bigint|null=null;
 restoringMessages.add(key);refreshRestoreButtons();
 try{
  const outcome=await restoreWithReconciliation({valid:allowed,
   send:async()=>{const response=await api<{snapshot_revision?:string}>(path,{method:'POST',body:JSON.stringify(command)});
    if(typeof response.snapshot_revision==='string'&&/^[0-9]+$/.test(response.snapshot_revision))revision=BigInt(response.snapshot_revision);},
   refresh:()=>catchUp(message.conversation_id,revision),
   restored:()=>messages.some(row=>row.id===message.id&&!row.deleted_at&&row.version>message.version),
  });
  if(outcome==='accepted'&&allowed())toast('Сообщение восстановлено. Ожидаем обновление чата.');
 }catch(error){if(allowed())toast(errorMessage(error));}
 finally{restoringMessages.delete(key);if(allowed())refreshRestoreButtons();}
}

async function mutate(path: string, method: string, body: unknown): Promise<void> {
  return runUiAction(async (request, valid) => {
    await request(path, { method, body: JSON.stringify(body) });
    if (current) await loadMessages(current.id); if (!valid()) return; await loadConversations(valid); if (!valid()) return; renderShell();
  });
}

// Enter pressed while a file is still uploading: the message waits for the upload (sent from
// uploadFiles() once the last file settles) instead of leaving without it.
let sendAfterUploads: { selection: number; account: Bootstrap } | null = null;
async function queueMessage(body: string): Promise<void> {
  if (bootstrap && outboxResetOwner === bootstrap) { toast('Дождитесь завершения сброса очереди.'); return; }
  const lengthError = messageLengthError(body);
  if (lengthError) { toast(lengthError); return; }
  if (current && bootstrap && navigationState?.generation !== selectionGeneration && parseTodoCommand(body)?.kind === 'open') {
    const input = root.querySelector<MessageInput>('.composer .message-input');
    if (input?.value === body) { input.value = ''; localStorage.removeItem(draftKey(current.id)); fitComposer(input); }
    showTasks(false); return;
  }
  if (current && bootstrap && composerUploads.length) {
    sendAfterUploads = { selection: selectionGeneration, account: bootstrap };
    toast('Сообщение уйдёт, как только загрузится файл'); return;
  }
  if (!current || !bootstrap || navigationState?.generation === selectionGeneration || (!body.trim() && !composerAttachmentIds.length)) return;
  const account = bootstrap; const valid = sessionGuard(); const conversationId = current.id; const generation = selectionGeneration;
  const key = draftKey(conversationId);
  const sameSelection = (): boolean => valid() && bootstrap === account && current?.id === conversationId && selectionGeneration === generation;
  const managedUploads=composerAttachments.filter((attachment):attachment is ManagedUploadAttachment=>Boolean(attachment.upload));
  if(managedUploads.length&&!bootstrap.managed_files_enabled){toast('Отправка файлов временно недоступна. Черновик сохранён.');return;}
  if(managedUploads.length&&(managedUploads.length!==composerAttachments.length||composerStickerAttachmentIds.size)){toast('Отправьте файлы и стикеры отдельными сообщениями.');return;}
  if(managedUploads.some(attachment=>attachment.upload.roomId!==conversationId||attachment.upload.membershipEpoch!==currentDetails?.membership_epoch_id)){toast('Доступ к чату изменился. Прикрепите файлы заново.');return;}
  const legacyAttachmentIds=composerAttachmentIds.filter(id=>!managedUploads.some(attachment=>attachment.id===id));
  const command = {
    client_message_id: crypto.randomUUID(), membership_epoch_id: currentDetails?.membership_epoch_id, body, mentions: mentionsInText(body),
    reply_to_message_id: replyToMessageId, attachment_ids: legacyAttachmentIds,
    ...(managedUploads.length?{managed_files:managedUploads.map(attachment=>({reserveAttempt:attachment.upload.reserveAttempt,uploadId:attachment.upload.uploadId})),managed_room_kind:managedUploads[0]!.upload.kind,managed_file_names:managedUploads.map(attachment=>attachment.name)}:{}),
    entities: legacyAttachmentIds.filter(id=>composerStickerAttachmentIds.has(id)).map(id=>({type:'sticker',attachment_id:id})),
  };
  const local: Message = { id: command.client_message_id, conversation_id: current.id, seq: '0', sender: { id: ownConversationAuthorId(current.id) ?? bootstrap.user.id, name: bootstrap.user.display_name }, kind: 'user', body, mentions: command.mentions, reply_to_message_id: command.reply_to_message_id, attachments: [...composerAttachments], entities: command.entities, version: 1, created_at: new Date().toISOString(), edited_at: null, deleted_at: null, reactions: [], permalink: '', pending: true };
  // Capture before exposing the optimistic row: cancellation can precede the first IDB open.
  const commandKey = pendingKey(command.client_message_id, account);
  const cancelled = (): boolean => cancelledPending.has(commandKey);
  historyReturnAnchor = null;
  messages.push(local);
  clearStoredReply();
  replyToMessageId = null; composerReplyTarget = null; composerMentionIds = []; composerMentionTokens.clear(); mentionPicker = null; composerAttachmentIds = []; composerStickerAttachmentIds.clear(); composerAttachments = [];
  savingPending.set(commandKey, { account, valid: () => valid() && !cancelled(), item: { ...command, conversation_id: conversationId } });
  requestedHistoryAnchor = {conversationId,atBottom:true,scrollTop:0};
  renderShell();
  let persisted = false;
  try {
    const durable = await pendingPut(conversationId, command, account.user, () => !cancelled());
    persisted = Boolean(durable);
    if (!durable || cancelled() || !valid()) return; // Never send an old command with a replacement session.
    // History refresh can replace the optimistic row while dispatch is awaiting IDB.
    // Publish its durable fallback first so normal local cancellation stays reachable.
    projectPendingCommand(durable, account, valid);
    if (cancelled() || !valid()) return;
    savingPending.delete(commandKey);
    if (localStorage.getItem(key) === body) localStorage.removeItem(key);
    if (sameSelection()) renderShell();
    await dispatchPending(command.client_message_id, account, valid);
    if (cancelled() || !valid()) return;
    forgetPendingProjection(command.client_message_id, account);
    if (!sameSelection()) return;
    await loadingStatus.run('Обновляем сообщения…', sameSelection, async () => {
      await loadMessages(conversationId);
      if (!sameSelection()) return;
      await loadConversations(sameSelection);
      if (sameSelection()) { requestedHistoryAnchor = {conversationId,atBottom:true,scrollTop:0}; renderShell(); }
    });
  } catch (error) {
    if (cancelled() || !valid()) return;
    local.error = errorMessage(error, 'Ошибка отправки');
    try { if (persisted) {
      await pendingFailure(command.client_message_id, account, error);
      const durable = await pendingGet(command.client_message_id);
      if (durable) projectPendingCommand(durable, account, valid);
    } }
    catch (storageFailure) { local.error = errorMessage(storageFailure); }
    if (cancelled() || !valid()) return;
    if (sameSelection()) { renderShell(); toast(local.error); }
  } finally { savingPending.delete(commandKey); if (sameSelection()) renderShell(); }
}

function hasOlderHistory(page: { messages: Message[]; has_more_before?: boolean }): boolean {
  if (typeof page.has_more_before === 'boolean') return page.has_more_before;
  // Cached client against rollback server: no metadata yet. Remove once8372cfd is no longer supported.
  const first = page.messages.find(message => !message.pending)?.seq;
  const floor = BigInt(currentDetails?.visible_from_seq ?? '1');
  return Boolean(first && BigInt(first) > (floor > 1n ? floor : 1n));
}

async function loadOlderMessagesFromUi(): Promise<void> {
  const valid = selectionGuard();
  const progress = loadingStatus.begin('Загружаем историю…', valid, 0);
  try {
    const added = await loadOlderMessages();
    if (valid() && added > 0) toast('Сообщения загружены');
  } catch (error) {
    if (valid()) toast(errorMessage(error, 'Не удалось загрузить историю'));
  } finally { progress.finish(); }
}

async function loadOlderMessages(): Promise<number> {
  if (!current || !bootstrap || !messages.length) return 0;
  const valid = sessionGuard(); const conversationId = current.id;
  const selection = selectionGeneration; const snapshot = historyGeneration; const request = ++olderHistoryGeneration;
  const window = messages; const overlapsReplacement = historyReplacementPending !== null;
  const oldest = messages.find((item) => !item.pending)?.seq; if (!oldest) return 0;
  const page = await api<{ messages: Message[]; has_more_before?: boolean }>(`/v1/conversations/${conversationId}/messages?before=${oldest}&limit=100`);
  const selected = (): boolean => valid() && current?.id === conversationId && selection === selectionGeneration;
  const active = (): boolean => selected() && snapshot === historyGeneration && request === olderHistoryGeneration
    && messages === window && !overlapsReplacement && historyReplacementPending === null;
  const retryNotice = (): void => { if (selected() && request === olderHistoryGeneration) toast('История обновилась. Нажмите «Загрузить более ранние» ещё раз.'); };
  // A replacement owns the single bounded reply scheduler. Never start an older
  // batch (even empty) against it or append a page fetched for a displaced window.
  if (!active()) { retryNotice(); return 0; }
  await resolveReplySources(page.messages, conversationId, active);
  if (!active()) { retryNotice(); return 0; }
  const known = new Set(messages.map((item) => item.id));
  historyHasEarlier = hasOlderHistory(page);
  const earlier = page.messages.filter((item) => BigInt(item.seq) < BigInt(oldest) && !known.has(item.id));
  messages = [...earlier, ...messages]; renderShell();
  return earlier.length;
}


async function loadNewerMessages(): Promise<void> {
  if (!current || !bootstrap || !messages.length || historyReplacementPending !== null || newerHistoryPending !== null) return;
  const id = current.id; const last = messages.filter(message => !message.pending).at(-1)?.seq;
  if (!last) return;
  const valid = selectionGuard(); const generation = historyGeneration;
  const request = ++newerHistoryGeneration; const window = messages;
  const active = (): boolean => valid() && generation === historyGeneration && request === newerHistoryGeneration
    && messages === window && historyReplacementPending === null;
  newerHistoryPending = request;
  try {
    const result = await api<{ messages: Message[] }>(`/v1/conversations/${id}/messages?after=${encodeURIComponent(last)}&limit=100`);
    if (!active()) return;
    await resolveReplySources(result.messages, id, active);
    if (!active()) return;
    const history = root.querySelector<HTMLElement>('#messages');
    if (history?.dataset.conversationId === id) requestedHistoryAnchor = { ...captureHistoryAnchor(history, id), atBottom:false };
    const known = new Set(messages.map(message => message.id));
    messages = [...messages, ...result.messages.filter(message => BigInt(message.seq) > BigInt(last) && !known.has(message.id))];
    if (currentDetails && BigInt(messages.at(-1)?.seq ?? '0') >= BigInt(currentDetails.last_message_seq ?? '0')) { historyWindowAfter = null; historyWindowAtTail = true; }
    renderShell();
  } finally { if (newerHistoryPending === request) newerHistoryPending = null; }
}

// Members that can be mentioned in the open chat (self excluded), loaded once per chat.
async function loadMentionCandidates(): Promise<void> {
  if (!current || mentionCandidates?.conversationId === current.id) return;
  const conversationId = current.id; const valid = selectionGuard();
  const users = current.type === 'group'
    ? (await api<{ members: MentionCandidate[] }>(`/v1/conversations/${encodeURIComponent(conversationId)}/members`)).members
    : [await api<MentionCandidate>(`/v1/conversations/${encodeURIComponent(conversationId)}/peer`)];
  if (!valid()) return;
  mentionCandidates = { conversationId, users: users.filter((user) => user.id !== bootstrap?.user.id) };
  const input = root.querySelector<MessageInput>('[aria-label="Сообщение"]');
  const picker = root.querySelector<HTMLElement>('.mention-picker');
  if (input && picker) renderMentionPicker(picker, input);
}
// "@" + up to three words right before the caret, at a word start.
function updateMentionPicker(input: MessageInput): void {
  const caret = input.selectionStart ?? input.value.length;
  const match = /(?:^|\s|[*_]{1,3})@([^\s@]*(?: [^\s@]*){0,2})$/u.exec(input.value.slice(0, caret));
  if (!match) { mentionPicker = null; return; }
  const query = match[1]!;
  // "@Андрей Кузнецов " — a finished mention followed by a space must not reopen the picker.
  if (/\s$/.test(query) && composerMentionTokens.has(`@${query.trim()}`)) { mentionPicker = null; return; }
  mentionPicker = { start: caret - query.length - 1, query, active: mentionPicker?.query === query ? mentionPicker.active : 0 };
  void loadMentionCandidates().catch(() => undefined);
}
function mentionOptions(): { label: string; token: string; ids: string[] }[] {
  if (!mentionPicker || !mentionCandidates || mentionCandidates.conversationId !== current?.id) return [];
  const query = mentionPicker.query.trim().toLocaleLowerCase('ru-RU');
  const matches = (name: string): boolean => !query || name.toLocaleLowerCase('ru-RU').startsWith(query)
    || name.toLocaleLowerCase('ru-RU').split(/\s+/).some((word) => word.startsWith(query));
  const options: { label: string; token: string; ids: string[] }[] = [];
  if (current?.type === 'group' && mentionCandidates.users.length && (!query || 'all'.startsWith(query) || 'все'.startsWith(query))) {
    options.push({ label: '@all — все участники', token: '@all', ids: mentionCandidates.users.map((user) => user.id) });
  }
  for (const user of mentionCandidates.users) if (matches(user.display_name)) options.push({ label: user.display_name, token: `@${user.display_name}`, ids: [user.id] });
  return options.slice(0, 8);
}
function renderMentionPicker(picker: HTMLElement, input: MessageInput): void {
  const options = mentionOptions();
  picker.replaceChildren(); picker.hidden = !options.length;
  if (!mentionPicker || !options.length) return;
  mentionPicker.active = Math.min(mentionPicker.active, options.length - 1);
  options.forEach((option, index) => {
    const item = element('div', `mention-option${index === mentionPicker!.active ? ' active' : ''}`, option.label);
    item.setAttribute('role', 'option'); item.setAttribute('aria-selected', String(index === mentionPicker!.active));
    // mousedown (not click) so the message field keeps focus and caret.
    item.addEventListener('mousedown', (event) => { event.preventDefault(); applyMention(option, input); renderMentionPicker(picker, input); });
    picker.append(item);
  });
  const active = picker.querySelector<HTMLElement>('.mention-option.active');
  if (active) {
    const item = active.getBoundingClientRect(), box = picker.getBoundingClientRect();
    const scale = picker.offsetHeight ? box.height / picker.offsetHeight : 1;
    if (item.top < box.top) picker.scrollTop -= (box.top - item.top) / scale;
    else if (item.bottom > box.bottom) picker.scrollTop += (item.bottom - box.bottom) / scale;
  }
}
function handleMentionKey(event: KeyboardEvent, input: MessageInput): boolean {
  const options = mentionOptions(); if (!mentionPicker || !options.length) return false;
  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
    mentionPicker.active = (mentionPicker.active + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
  } else if ((event.key === 'Enter' || event.key === 'Tab') && !event.shiftKey && !event.isComposing) {
    applyMention(options[mentionPicker.active]!, input);
  } else if (event.key === 'Escape') {
    mentionPicker = null;
  } else return false;
  event.preventDefault(); event.stopPropagation();
  return true;
}
function applyMention(option: { token: string; ids: string[] }, input: MessageInput): void {
  if (!mentionPicker) return;
  const caret = input.selectionStart ?? input.value.length;
  input.setRangeText(`${option.token} `, mentionPicker.start, caret, 'end'); fitComposer(input);
  composerMentionTokens.set(option.token, option.ids);
  composerMentionIds = mentionsInText(input.value);
  mentionPicker = null;
  if (current) localStorage.setItem(draftKey(current.id), input.value);
  input.focus();
}
function mentionsInText(body: string): string[] {
  return [...new Set([...composerMentionTokens].filter(([token]) => body.includes(token)).flatMap(([, ids]) => ids))];
}


async function uploadFiles(files: File[], sticker = false): Promise<void> {
  return runUiAction(async (request, valid) => {
    if (!current) return;
    if((sticker&&composerAttachments.some(attachment=>attachment.upload))||(!sticker&&bootstrap?.managed_files_enabled&&!bootstrap.attachment_transfer_queue_enabled&&composerStickerAttachmentIds.size)){toast('Отправьте файлы и стикеры отдельными сообщениями.');return;}
    const uploadConversation=current;
    const uploadAccess=currentDetails?.id===current.id?currentDetails:null;
    const queued = files.slice(0, Math.max(0, 10 - composerAttachmentIds.length)).map((file) => ({
      file, controller:new AbortController(), key: crypto.randomUUID(), name: file.name, preview: isAttachmentImage(file.type) ? URL.createObjectURL(file) : null,
    }));
    for(const item of queued)uploadControllers.set(item.key,item.controller);
    // Show every file at once as "uploading" so a paste/attach gives immediate feedback.
    composerUploads = [...composerUploads, ...queued.map(({ key, name, preview }) => ({ key, name, preview,phase:'preparing' as const,percent:null }))]; renderShell();
    let failed = false;
    try{
    for (const { file, key, preview,controller } of queued) {
      if(controller.signal.aborted){failed=true;uploadControllers.delete(key);if(preview)URL.revokeObjectURL(preview);continue;}
      let attachmentId:string|null=null;
      const settle = (): void => { composerUploads = composerUploads.filter((item) => item.key !== key); };
      try {
        const progress = loadingStatus.begin(`Загружаем файлы: ${queued.findIndex(item => item.key === key) + 1} из ${queued.length}…`, ()=>valid()&&!controller.signal.aborted);
        const observeUpload=(update:UploadTransfer)=>{
              if(!valid()||controller.signal.aborted)return;
              const upload=composerUploads.find(item=>item.key===key);if(!upload)return;
              if(upload.phase===update.phase&&upload.percent===update.percent)return;
              Object.assign(upload,update);
              const chip=root.querySelector<HTMLElement>(`[data-upload-key="${key}"]`);if(chip)updateUploadProgress(chip,upload);
              progress.update(update.phase==='processing'?'Обрабатываем файл…':`Загружаем файл${update.percent===null?'':`: ${update.percent}%`}…`);
        };
        try {
          if(bootstrap?.managed_files_enabled&&!bootstrap.attachment_transfer_queue_enabled&&!sticker){
            if(!uploadAccess?.membership_epoch_id)throw new Error('Обновите чат перед загрузкой файла');
            const ready=await uploadChatManagedFile(request,{kind:uploadAccess.shared_room?'shared':'native',roomId:uploadConversation.id,membershipEpoch:uploadAccess.membership_epoch_id},file,{reserveAttempt:key,contentAttempt:crypto.randomUUID(),cancelAttempt:crypto.randomUUID()},controller.signal,observeUpload);
            controller.signal.throwIfAborted();composerAttachmentIds.push(ready.id);composerAttachments.push(ready);
            if(preview)attachmentPreviews.set(ready.id,preview);settle();renderShell();continue;
          }
          const init = await request<{ id: string }>(`/v1/conversations/${uploadConversation.id}/attachments/init`, {
            method: 'POST', body: JSON.stringify({ name: file.name, mime: file.type, size: file.size, ...(uploadAccess?.shared_room?{attempt_id:key,membership_epoch_id:uploadAccess.membership_epoch_id}:{}) }),
          });
          attachmentId=init.id;
          controller.signal.throwIfAborted();
          const ready = await request<{ mime: string; size: number }>(`/v1/attachments/${init.id}/content`, {
            method: 'PUT', headers: { 'Content-Type': 'application/octet-stream' }, body: file,signal:controller.signal,
            onUploadProgress:observeUpload,
          });
          controller.signal.throwIfAborted();
          composerAttachmentIds.push(init.id); if(sticker)composerStickerAttachmentIds.add(init.id); composerAttachments.push({ id: init.id, name: file.name, mime: ready.mime, size: ready.size });
          if (preview) attachmentPreviews.set(init.id, preview);
          settle(); renderShell();
        } finally { progress.finish(); }
      } catch (error) {
        settle(); failed = true; if (preview) URL.revokeObjectURL(preview);
        if (!valid()) return;
        renderShell();
        if(controller.signal.aborted){
          if(attachmentId)try{await request(`/v1/attachments/${attachmentId}`,{method:'DELETE'});}catch(error){if(valid())toast(error instanceof Error?error.message:'Не удалось завершить отмену загрузки');}
        }else toast(error instanceof Error ? error.message : 'Файл не загружен');
      }finally{uploadControllers.delete(key);}
    }
    const deferred = sendAfterUploads;
    if (deferred && !composerUploads.length) {
      sendAfterUploads = null;
      // A failed file keeps the text in the composer: the user decides whether to send without it.
      if (!failed && valid() && current && bootstrap === deferred.account && selectionGeneration === deferred.selection) {
        await queueMessage(localStorage.getItem(draftKey(current.id)) ?? '');
      }
    }
    }finally{for(const item of queued)uploadControllers.delete(item.key);}
  });
}

async function showReaders(messageId: string): Promise<void> {
  return runUiAction(async (request) => {
    // The server knows who has read the message, not when: a read cursor moves per chat, not per
    // message. Printing a time from the missing field is what produced "Иван Иванов · Invalid Date".
    const data = await request<{ readers: { display_name: string }[]; recipient_count?: number }>(`/v1/messages/${messageId}/readers`);
    if (!data.readers.length) { alert('Пока никто не прочитал'); return; }
    const total = typeof data.recipient_count === 'number' ? data.recipient_count : data.readers.length;
    const names = data.readers.map((item) => item.display_name).join('\n');
    alert(total > data.readers.length ? `Прочитали ${data.readers.length} из ${total}:\n${names}` : `Прочитали:\n${names}`);
  });
}

let latestOwnEditPending = false;
async function editLatestOwnMessage(input: MessageInput): Promise<void> {
  if (latestOwnEditPending || !current || !bootstrap || current.archived_at) return;
  const valid = selectionGuard(); const id = current.id; const userId = ownConversationAuthorId(current.id);
  const epoch = currentDetails?.membership_epoch_id;
  latestOwnEditPending = true;
  try {
    const result = await api<{messages: Message[]; membership_epoch_id: string}>(`/v1/conversations/${id}/messages?own_latest=1`);
    // Typing, a dialog, selection or access changes supersede the shortcut.
    const composer = root.querySelector<MessageInput>('.composer .message-input');
    if (!valid() || !composer || composer.value || !input.isConnected || document.activeElement !== composer
        || activeEditor || document.querySelector('dialog[open]') || currentDetails?.membership_epoch_id !== epoch
        || result.membership_epoch_id !== epoch) return;
    const message = result.messages[0];
    if (!message) { toast('Нет сообщений для редактирования'); return; }
    if (message.conversation_id === id && message.sender?.id === userId && message.kind === 'user'
        && !message.retained_native && !message.deleted_at && !message.pending && !message.poll) await editMessageFromUi(message);
  } catch (error) { if (valid()) toast(errorMessage(error)); }
  finally { latestOwnEditPending = false; }
}

async function editMessageFromUi(message: Message): Promise<void> {
  if (activeEditor || message.retained_native) return;
  const sharedMutation = currentDetails?.id === message.conversation_id && currentDetails.shared_room;
  const mutationPath = sharedMutation ? `/v1/shared-rooms/${message.conversation_id}/messages/${message.id}` : `/v1/messages/${message.id}`;
  const mutationEpoch = sharedMutation ? currentDetails!.membership_epoch_id : null;
  let savedAttempt = {body:'',id:crypto.randomUUID()};
  const valid = selectionGuard(); const returnFocus = (document.activeElement?.closest('.message')?.querySelector('.message-more') ?? document.activeElement) as HTMLElement | null;
  const dialog = element('dialog', 'message-editor'); dialog.setAttribute('aria-label', 'Изменить сообщение');
  const form = element('form'); const input = createMessageInput('Редактирование сообщения',valid); input.value = message.body;
  const error = element('div', 'error'); error.setAttribute('role', 'alert');
  const save = element('button', 'primary', 'Сохранить'); save.type = 'submit';
  const cancel = element('button', 'ghost', 'Отмена'); cancel.type = 'button';
  let focusSuperseded = false;
  const trackFocus = (event: FocusEvent): void => { if (event.target instanceof Node && !dialog.contains(event.target)) focusSuperseded = true; };
  document.addEventListener('focusin', trackFocus);
  const mayRestore = (): boolean => valid() && !focusSuperseded && !document.querySelector('dialog[open]:not(.message-editor)');
  const close = (restore = true): void => {
    const allowed = mayRestore(); document.removeEventListener('focusin', trackFocus);
    dialog.remove(); if (activeEditor?.dialog === dialog) activeEditor = null;
    if (restore && allowed) {
      const trigger = [...root.querySelectorAll<HTMLElement>('.message')].find(node => node.dataset.messageId === message.id)?.querySelector<HTMLElement>('.message-more');
      (returnFocus?.isConnected ? returnFocus : trigger ?? document.querySelector<HTMLElement>('.composer .message-input'))?.focus();
    }
  };
  cancel.addEventListener('click', () => close()); dialog.addEventListener('cancel', event => { event.preventDefault(); close(); });
  input.addEventListener('keydown', event => { if (event.defaultPrevented) return; if (event.key === 'Enter' && (event.ctrlKey || event.metaKey) && !event.isComposing) { event.preventDefault(); form.requestSubmit(); } });
  form.addEventListener('submit', event => {
    event.preventDefault(); if (!valid() || save.disabled) return;
    if (!input.value.trim()) { error.textContent = 'Введите текст сообщения'; input.focus(); return; }
    const lengthError = messageLengthError(input.value);
    if (lengthError) { error.textContent = lengthError; input.focus(); return; }
    save.disabled = true; input.readOnly = true; error.textContent = '';
    void (async () => {
      try {
        if(savedAttempt.body!==input.value)savedAttempt={body:input.value,id:crypto.randomUUID()};
        await api(mutationPath, { method: 'PATCH', body: JSON.stringify({body:input.value,version:message.version,mentions:message.mentions ?? [],...(sharedMutation?{attempt_id:savedAttempt.id,membership_epoch_id:mutationEpoch}:{entities:message.entities ?? []})}) });
        if (!valid() || !dialog.isConnected) return;
        await loadMessages(message.conversation_id); if (!valid() || !dialog.isConnected) return;
        const restore = mayRestore(); close(false); renderShell();
        if (restore && valid() && !document.querySelector('dialog[open]')) document.querySelector<MessageInput>('.composer .message-input')?.focus();
      } catch (failure) { if (valid() && dialog.isConnected) { error.textContent = failure instanceof Error ? failure.message : 'Не удалось сохранить'; save.disabled = false; input.readOnly = false; input.focus(); } }
    })();
  });
  form.append(element('h3', '', 'Изменить сообщение'), input, element('p', 'editor-hint', 'Ctrl/⌘ + Enter — сохранить · Esc — отменить'), error, cancel, save);
  dialog.append(form); document.body.append(dialog); activeEditor = {dialog, valid, close}; dialog.showModal(); input.focus(); input.setSelectionRange(input.value.length, input.value.length);
}

function focusSidebarSearch(): void {
  root.querySelector<HTMLInputElement>('[aria-label="Поиск чатов"]')?.focus({ preventScroll: true });
}

function cancelMessageSearch(): void {
  ++messageSearchGeneration;
  messageSearchController?.abort(); messageSearchController = null; messageSearch = null;
}

// Plain text nodes only: message contents must never become search-result HTML.
function searchExcerpt(message: Message, query: string): HTMLElement {
  const text = message.body.replace(/\s+/g, ' ');
  const terms = query.match(/[\p{L}\p{N}_+#-]+/gu) ?? [];
  const escaped = terms.map(term => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const pattern = escaped.length ? new RegExp(escaped.join('|'), 'giu') : null;
  const first = pattern?.exec(text);
  const start = Math.max(0, (first?.index ?? 0) - 50);
  const excerpt = text.slice(start, start + 180);
  const node = element('span', 'preview search-excerpt', `${message.sender?.name ?? 'Система'}: ${start ? '…' : ''}`);
  let offset = 0;
  if (pattern) {
    pattern.lastIndex = 0;
    for (const match of excerpt.matchAll(pattern)) {
      node.append(document.createTextNode(excerpt.slice(offset, match.index)), element('mark', '', match[0]));
      offset = match.index + match[0].length;
    }
  }
  node.append(document.createTextNode(excerpt.slice(offset) + (start + excerpt.length < text.length ? '…' : '')));
  return node;
}

async function searchMessagesFromUi(query: string): Promise<void> {
  cancelMessageSearch();
  const generation = messageSearchGeneration;
  const q = query.trim().slice(0, 200);
  if (!q) { renderShell(); return; }
  const scopeId = messageSearchScope?.id ?? null;
  const ownsSelection = selectionGuard();
  const valid = (): boolean => ownsSelection() && generation === messageSearchGeneration;
  if (!valid()) return;
  const controller = new AbortController(); messageSearchController = controller;
  const progress = loadingStatus.begin('Ищем сообщения…', valid);
  try {
    const params = new URLSearchParams({ q, limit: '50' });
    if (scopeId) params.set('conversation_id', scopeId);
    const data = await api<{ results: SearchResult[] }>(`/v1/search/messages?${params}`, { signal: controller.signal });
    if (!valid()) return;
    messageSearch = { query: q, scopeId, results: data.results };
    renderShell();
  } catch (error) {
    if (!valid() || controller.signal.aborted) return;
    messageSearch = { query: q, scopeId, results: [], error: error instanceof Error ? error.message : 'Не удалось найти сообщения' };
    renderShell();
  } finally {
    progress.finish();
    if (messageSearchController === controller) messageSearchController = null;
  }
}

async function markReadAllFromUi(): Promise<void> {
  return runUiAction(async (request, valid) => {
    await request('/v1/conversations/read-all', { method: 'POST', body: '{}' }); await loadConversations(valid); if (!valid()) return; renderShell(); toast('Все доступные сообщения отмечены прочитанными');
  });
}

let profileDialogOpen = false;
async function uploadOwnAvatarFromUi(file: File, refresh: () => void): Promise<void> {
  const valid = sessionGuard();
  try {
    if (file.size > 5 * 1024 * 1024) throw new Error('Файл слишком большой (максимум 5 МБ)');
    const buffer = await file.arrayBuffer();
    const result = await api<{ avatar_key: string }>('/v1/me/avatar', { method: 'PUT', headers: { 'Content-Type': 'application/octet-stream' }, body: buffer });
    if (!valid() || !bootstrap) return;
    // Profile presentation changes preserve the account identity captured by sidebar/session guards.
    bootstrap.user = { ...bootstrap.user, avatar_key: result.avatar_key };
    refresh(); renderShell(); toast('Картинка обновлена');
  } catch (error) { if (valid()) toast(error instanceof Error ? error.message : 'Не удалось обновить картинку'); }
}
async function deleteOwnAvatarFromUi(refresh: () => void): Promise<void> {
  const valid = sessionGuard();
  try {
    await api('/v1/me/avatar', { method: 'DELETE', body: '{}' });
    if (!valid() || !bootstrap) return;
    bootstrap.user = { ...bootstrap.user, avatar_key: null };
    refresh(); renderShell(); toast('Картинка удалена');
  } catch (error) { if (valid()) toast(error instanceof Error ? error.message : 'Не удалось удалить картинку'); }
}
async function updateOwnStatusFromUi(status: string, refresh: () => void): Promise<void> {
  const valid = sessionGuard();
  try {
    const result = await api<{ status_text: string | null }>('/v1/me', { method: 'PATCH', body: JSON.stringify({ status_text: status || null }) });
    if (!valid() || !bootstrap) return;
    bootstrap.user = { ...bootstrap.user, status_text: result.status_text };
    refresh(); renderShell(); toast('Статус обновлён');
  } catch (error) { if (valid()) toast(error instanceof Error ? error.message : 'Не удалось обновить статус'); }
}
async function openBotFatherFromUi(): Promise<void> {
  const valid = sessionGuard();
  try {
    const { conversation_id } = await api<{ conversation_id: string }>('/v1/botfather', { method: 'POST', body: '{}' });
    if (!valid()) return;
    activeProfileDialog?.close();
    await openConversation(conversation_id);
  } catch (error) { if (valid()) toast(error instanceof Error ? error.message : 'Не удалось открыть @BotFather'); }
}
async function renderWorkspaceSettings(container: HTMLElement, back: () => void): Promise<void> {
  const validSession = sessionGuard();
  const view = element('div', 'workspace-settings');
  const returnButton = element('button', 'ghost', '‹ Настройки профиля'); returnButton.type = 'button';
  returnButton.addEventListener('click', back);
  view.append(returnButton, element('h4', '', 'Настройки пространства'), element('p', 'meta', 'Загрузка…'));
  container.replaceChildren(view);
  const valid = (): boolean => validSession() && view.isConnected;
  try {
    const settings = await api<{ message_time_limit_enabled: boolean }>('/v1/workspace/settings');
    if (!valid()) return;
    const form = element('form');
    const toggle = element('input'); toggle.type = 'checkbox'; toggle.checked = settings.message_time_limit_enabled;
    const label = element('label', 'profile-toggle'); label.append(toggle, element('span', '', 'Ограничить редактирование и удаление 24 часами'));
    const save = element('button', '', 'Сохранить'); save.type = 'submit';
    const errorText = element('p', 'error'); errorText.hidden = true;
    form.append(label, element('p', 'meta', 'Если выключено, автор может изменить или удалить своё сообщение в любое время. Модераторы сохраняют право удалять сообщения в группах.'), save, errorText);
    form.addEventListener('submit', event => {
      event.preventDefault(); if (!valid()) return;
      save.disabled = true; errorText.hidden = true;
      void api('/v1/workspace/settings', {method:'PATCH', body:JSON.stringify({message_time_limit_enabled:toggle.checked})})
        .then(() => { if (valid()) toast('Настройки пространства сохранены'); })
        .catch(error => { if (valid()) { errorText.textContent = error.message; errorText.hidden = false; } })
        .finally(() => { if (valid()) save.disabled = false; });
    });
    view.replaceChildren(returnButton, element('h4', '', 'Настройки пространства'), form);
  } catch (error) {
    if (valid()) view.replaceChildren(returnButton, element('p', 'error', error instanceof Error ? error.message : 'Не удалось загрузить настройки'));
  }
}
function renderProfileDialogContent(container: HTMLElement, refresh: () => void): void {
  if (!bootstrap) return;
  container.replaceChildren();
  const avatarWrap = element('div', 'chat-info-avatar');
  const avatarKey = bootstrap.user.avatar_key;
  avatarWrap.append(avatarNode(bootstrap.user.display_name, avatarKey ? userAvatarUrl(bootstrap.user.id, avatarKey) : null));
  const fileInput = element('input'); fileInput.type = 'file'; fileInput.accept = 'image/png,image/jpeg,image/webp,image/gif'; fileInput.hidden = true;
  fileInput.addEventListener('change', () => { const file = fileInput.files?.[0]; if (file) void uploadOwnAvatarFromUi(file, refresh); fileInput.value = ''; });
  const avatarActions = element('div', 'chat-info-avatar-actions');
  const uploadButton = element('button', 'ghost', avatarKey ? 'Заменить картинку' : 'Загрузить картинку');
  uploadButton.type = 'button'; uploadButton.addEventListener('click', () => fileInput.click());
  avatarActions.append(fileInput, uploadButton);
  if (avatarKey) {
    const removeButton = element('button', 'ghost', 'Удалить картинку'); removeButton.type = 'button';
    removeButton.addEventListener('click', () => void deleteOwnAvatarFromUi(refresh));
    avatarActions.append(removeButton);
  }
  avatarWrap.append(avatarActions);
  container.append(avatarWrap);
  container.append(element('h4', 'profile-name', bootstrap.user.display_name));
  if (bootstrap.user.email) container.append(element('p', 'meta profile-email', bootstrap.user.email));

  const resetQueue = element('input'); resetQueue.type = 'checkbox';
  const resetQueueLabel = element('label', 'profile-toggle');
  resetQueueLabel.append(resetQueue, element('span', '', 'Также сбросить очередь отправки этого аккаунта'));
  const exportQueue = element('button', 'ghost', 'Скачать неотправленные сообщения'); exportQueue.type = 'button';
  const queueOwner = bootstrap;
  const queueValid = sessionGuard();
  const pendingOwner: PendingOwner = {workspaceId: queueOwner.user.workspace_id, userId: queueOwner.user.id, epoch: accountEpoch(queueOwner.user)};
  exportQueue.addEventListener('click', () => {
    if (exportQueue.disabled || !queueValid() || bootstrap !== queueOwner) return;
    exportQueue.disabled = true;
    void (async () => {
      const db = await openDb();
      try {
        const rows = await readPendingSnapshot(db, pendingOwner);
        if (!queueValid() || bootstrap !== queueOwner) return;
        if (!rows.length) { toast('Очередь отправки пуста.'); return; }
        const text = JSON.stringify({version:1, exported_at:new Date().toISOString(), messages:rows}, null, 2);
        const url = URL.createObjectURL(new Blob([text], {type:'application/json'}));
        const link = element('a'); link.href = url; link.download = 'chat-unsent-messages.json';
        document.body.append(link); link.click(); link.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } finally { db.close(); }
    })().catch(error => { if (queueValid()) toast(errorMessage(error)); }).finally(() => { exportQueue.disabled = false; });
  });
  const resetQueueForUpdate = async (): Promise<boolean> => {
    if (!queueValid() || bootstrap !== queueOwner) return false;
    if (pendingFlights.size || [...savingPending.values()].some(item => item.account === queueOwner)) {
      throw new Error('Отправка ещё выполняется. Дождитесь результата перед сбросом очереди.');
    }
    outboxResetOwner = queueOwner;
    try {
      const db = await openDb();
      try {
        const rows = await readPendingSnapshot(db, pendingOwner);
        if (!queueValid() || bootstrap !== queueOwner) return false;
        if (!rows.length) return true;
        if (!confirm(`Удалить из локальной очереди ${rows.length} сообщений? Неотправленные тексты будут удалены из очереди. При необходимости сначала скачайте их. Проверьте историю: часть сообщений могла уже сохраниться на сервере. Черновики останутся.`)) return false;
        await clearPendingSnapshot(db, pendingOwner, rows, () => queueValid() && bootstrap === queueOwner);
        if (!queueValid() || bootstrap !== queueOwner) return false;
        const ids = new Set(rows.map(row => String(row.client_message_id)));
        for (const id of ids) cancelledPending.add(pendingKey(id, queueOwner));
        messages = messages.filter(message => !message.pending || !ids.has(message.id));
        if (legacyPending?.account === queueOwner) legacyPending.items = legacyPending.items.filter(row => !ids.has(String(row.client_message_id)));
        toast('Локальная очередь очищена. Черновики и сообщения на сервере сохранены.');
        renderShell();
        return true;
      } finally { db.close(); }
    } finally { if (outboxResetOwner === queueOwner) outboxResetOwner = null; }
  };
  const update = element('button', 'ghost', 'Обновить приложение и очистить кеш'); update.type = 'button';
  const updateOwner = sessionGuard();
  const updateDraftWarning = 'Дождитесь отправки сообщения или уберите вложения из черновика перед обновлением.';
  update.addEventListener('click', () => {
    if (update.disabled || !updateOwner()) return;
    if (navigationWouldLoseAttachmentWork()) { toast(updateDraftWarning); return; }
    update.disabled = true; update.textContent = 'Загружаем обновление…';
    void (async () => {
      if (resetQueue.checked && !await resetQueueForUpdate()) return;
      if (!updateOwner()) return;
      await refreshApplicationAssets();
      if (!updateOwner()) return;
      // The dialog can be closed and a new upload/send started while downloads are in flight.
      if (navigationWouldLoseAttachmentWork()) { toast(updateDraftWarning); return; }
      const target = new URL(location.href); target.searchParams.set('app-update', String(Date.now()));
      location.replace(target.href);
    })().catch(error => { if (updateOwner()) toast(errorMessage(error)); })
      .finally(() => { update.disabled = false; update.textContent = 'Обновить приложение и очистить кеш'; });
  });
  container.append(element('h4', '', 'Обновление приложения'), update, resetQueueLabel, exportQueue,
    element('p', 'meta', 'Загрузить свежую версию и перезапустить эту вкладку. Вход, настройки и черновики сохранятся.'));

  container.append(element('h4', '', 'Оформление'));
  const themeBox = element('input'); themeBox.type = 'checkbox'; themeBox.checked = theme === 'dark';
  const themeLabel = element('label', 'profile-toggle');
  themeLabel.append(themeBox, element('span', '', 'Тёмная тема'));
  container.append(themeLabel, element('p', 'meta', 'Выбор сохраняется в этом браузере.'));
  themeBox.addEventListener('change', () => {
    theme = themeBox.checked ? 'dark' : 'light';
    applyTheme(theme);
    if (!saveTheme(theme, (key, value) => localStorage.setItem(key, value))) toast('Тема применена, но браузер не сохранил выбор');
  });

  container.append(element('h4', '', 'Статус'));
  const statusForm = element('form', 'chat-info-rename');
  const statusInput = element('input'); statusInput.value = bootstrap.user.status_text ?? '';
  statusInput.setAttribute('aria-label', 'Статус'); statusInput.maxLength = 120; statusInput.placeholder = 'Например: в отпуске до пятницы';
  const statusSave = element('button', '', 'Сохранить'); statusSave.type = 'submit';
  statusForm.append(statusInput, statusSave);
  statusForm.addEventListener('submit', (event) => { event.preventDefault(); void updateOwnStatusFromUi(statusInput.value.trim(), refresh); });
  container.append(statusForm);

  if (bootstrap.user.is_workspace_admin) {
    const workspace = element('button', 'ghost', 'Настройки пространства'); workspace.type = 'button';
    workspace.addEventListener('click', () => void renderWorkspaceSettings(container, refresh));
    container.append(workspace);
  }

  // A quiet, clearly separated footer action: logout is destructive-adjacent (ends the
  // session), not the dialog's primary/recommended action -- it should not out-compete the
  // avatar/status controls above it, and it was previously sitting with zero gap directly
  // under the status form ("stuck to it").
  container.append(element('h4', '', 'Приложение'));
  const install = element('div', 'profile-push');
  const row = element('div', 'profile-push-row');
  if (standaloneApp()) {
    install.append(element('p', 'meta', 'Чат уже запущен как приложение: значок живёт в доке или на панели задач.'));
  } else {
    install.append(element('p', 'meta', 'Отдельное окно и значок в доке или на панели задач, как у обычной программы.'));
    // One button in both cases: it either opens the browser's install prompt or, where no such
    // prompt exists (Safari, Firefox, iOS), the steps for that browser.
    const button = element('button', '', 'Установить приложение'); button.type = 'button';
    button.addEventListener('click', () => {
      const prompt = installPrompt;
      if (!prompt) { showInstallHelp(); return; }
      installPrompt = null;
      void prompt.prompt().catch(() => undefined).then(() => refresh());
    });
    row.append(button);
  }
  const desktopHelp = element('button', 'ghost', 'На рабочий стол и в автозапуск'); desktopHelp.type = 'button';
  desktopHelp.addEventListener('click', () => showDesktopHelp());
  row.append(desktopHelp);
  install.append(row);
  container.append(install);

  if (bootstrap.push.enabled) {
    container.append(element('h4', '', 'Уведомления'));
    const box = element('div', 'profile-push');
    const support = pushSupport();
    if (support === 'ready' && pushDeviceBound) {
      box.append(element('p', 'meta', 'Включены на этом устройстве.'));
      if (pushDiagnostics) {
        const workerText = pushDiagnostics.worker === 'active' ? 'служебный скрипт активен'
          : pushDiagnostics.worker === 'installing' ? 'служебный скрипт устанавливается' : 'служебный скрипт не зарегистрирован';
        const subscriptionText = pushDiagnostics.subscribed
          ? `подписка браузера есть${pushDiagnostics.host ? ` (${pushDiagnostics.host})` : ''}`
          : 'подписки браузера нет';
        box.append(element('p', 'meta', `Состояние: ${workerText}, ${subscriptionText}.`));
      }
      if (lastPushReceived?.at) {
        const at = new Date(lastPushReceived.at);
        box.append(element('p', 'meta', `Последнее уведомление этот браузер получил ${at.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}.`));
      }
      const row = element('div', 'profile-push-row');
      const help = element('button', 'ghost', 'Не приходят уведомления?'); help.type = 'button';
      help.addEventListener('click', () => showNotificationHelp());
      const check = element('button', '', 'Проверить уведомление'); check.type = 'button';
      check.addEventListener('click', () => void testBrowserPush());
      const off = element('button', 'ghost', 'Выключить на этом устройстве'); off.type = 'button';
      off.addEventListener('click', () => void unbindBrowserPush().then(refresh));
      row.append(check, off, help);
      box.append(row);
    } else if (support === 'ready') {
      box.append(element('p', 'meta', 'Приходят, когда чат закрыт или свёрнут.'));
      const on = element('button', '', 'Включить уведомления'); on.type = 'button';
      on.addEventListener('click', () => void bindBrowserPush(true).then(refresh));
      box.append(on);
    } else if (support === 'denied') {
      box.append(element('p', 'meta', 'Браузер запретил уведомления для этого сайта. Разрешите их в настройках сайта (значок замка рядом с адресом) и вернитесь сюда.'));
    } else if (support === 'ios-needs-install') {
      box.append(element('p', 'meta', 'На iPhone и iPad уведомления приходят только из приложения на экране «Домой»: нажмите «Поделиться», затем «На экран „Домой“», откройте чат с домашнего экрана и включите уведомления здесь.'));
    } else {
      box.append(element('p', 'meta', 'Этот браузер не умеет показывать уведомления.'));
    }
    const settings = element('button', 'ghost', 'Когда присылать'); settings.type = 'button';
    settings.addEventListener('click', () => void notificationSettingsFromUi());
    box.append(settings);
    const background = element('input'); background.type = 'checkbox'; background.checked = suppressBackgroundPush();
    const backgroundLabel = element('label', 'profile-toggle');
    backgroundLabel.append(background, element('span', '', 'Без push из открытого чата в фоновой вкладке'));
    box.append(backgroundLabel, element('p', 'meta', 'Только в этом браузере, пока вкладка подключена. Другие чаты и устройства продолжают получать уведомления.'));
    background.addEventListener('change', () => {
      const key = backgroundPushPreferenceKey(); if (!key) return;
      try { localStorage.setItem(key, background.checked ? 'on' : 'off'); }
      catch { background.checked = suppressBackgroundPush(); toast('Браузер не сохранил настройку'); return; }
      reportConversationVisibility();
    });
    container.append(box);
  }

  // Bots are made by talking to @BotFather, so this is a door, not a form: it opens the dialog and
  // steps out of the way. Nothing about names, tokens or the API has to be explained twice.
  container.append(element('h4', '', 'Боты'));
  const bots = element('div', 'profile-push');
  bots.append(element('p', 'meta', 'Свой бот для чата — напишите @BotFather, он спросит имя и выдаст токен.'));
  const botsRow = element('div', 'profile-push-row');
  const botfather = element('button', '', 'Открыть @BotFather'); botfather.type = 'button';
  botfather.addEventListener('click', () => void openBotFatherFromUi());
  botsRow.append(botfather);
  bots.append(botsRow);
  container.append(bots);

  container.append(element('h4', '', 'Звук'));
  const soundBox = element('input'); soundBox.type = 'checkbox'; soundBox.defaultChecked = incomingSoundEnabled();
  const sound = element('label', 'profile-toggle');
  sound.append(soundBox, element('span', '', 'Звук входящих сообщений'));
  const soundBlock = element('div', 'profile-push');
  soundBlock.append(sound, element('p', 'meta', 'Звучит, только когда чат открыт. Когда он закрыт, о сообщении сообщает уведомление.'));
  const preference = soundPreferences();
  const soundChoice = element('select'); soundChoice.id = 'profile-message-sound';
  const soundLabel = element('label', 'sound-choice-label', 'Звук сообщения'); soundLabel.htmlFor = soundChoice.id;
  for (const choice of MESSAGE_SOUNDS) {
    const option = element('option', '', choice.label); option.value = choice.id; soundChoice.append(option);
  }
  soundChoice.value = preference.tone;
  const volumeGroup = element('fieldset', 'sound-volume'); volumeGroup.append(element('legend', '', 'Громкость'));
  const volumeOptions = element('div', 'sound-volume-options');
  const volumeLabels = ['0 — без звука', '1 — тихо', '2 — средне', '3 — громко'] as const;
  const saveSound = (field: 'tone' | 'volume', value: string): boolean => {
    if (!bootstrap) return false;
    try { localStorage.setItem(`${soundKey(bootstrap.user)}:${field}`, value); }
    catch { toast('Браузер не сохранил настройку звука'); return false; }
    soundAttemptVersion += 1; incomingSound?.pause(); lastSoundOutcome = null; soundHintDismissed = false;
    soundElement(); soundBlock.querySelector('.sound-result')?.remove();
    renderShell(); return true;
  };
  soundChoice.addEventListener('change', () => {
    if (!saveSound('tone', soundChoice.value)) soundChoice.value = soundPreferences().tone;
  });
  for (const level of [0, 1, 2, 3] as const) {
    const label = element('label'); label.title = volumeLabels[level];
    const radio = element('input'); radio.type = 'radio'; radio.name = 'message-sound-volume'; radio.value = String(level);
    radio.checked = preference.volume === level; radio.setAttribute('aria-label', volumeLabels[level]);
    radio.addEventListener('change', () => {
      if (!radio.checked) return;
      if (!saveSound('volume', radio.value)) {
        const saved = soundPreferences().volume;
        volumeOptions.querySelectorAll<HTMLInputElement>('input').forEach(input => { input.checked = input.value === String(saved); });
      }
    });
    label.append(radio, element('span', '', String(level))); volumeOptions.append(label);
  }
  volumeGroup.append(volumeOptions);
  soundBlock.append(soundLabel, soundChoice, volumeGroup, element('p', 'meta', '0 — без звука. Выбор сохраняется для этого аккаунта в этом браузере.'));
  // A refusal is invisible by nature, so the person gets a way to ask and a straight answer. The
  // answer is rendered from lastSoundOutcome and the dialog is re-rendered after an attempt: holding
  // this node across the await would write into a node a re-render had already replaced.
  const soundRow = element('div', 'profile-push-row');
  const check = element('button', '', 'Проверить звук'); check.type = 'button';
  const attempt = async (): Promise<void> => { const outcome = await playIncomingSound(true); if (!outcome.ok) soundHintDismissed = false; refresh(); renderShell(); };
  check.addEventListener('click', () => void attempt());
  const soundHelp = element('button', 'ghost', 'Не слышно звука?'); soundHelp.type = 'button';
  soundHelp.addEventListener('click', () => showSoundHelp());
  soundRow.append(check, soundHelp);
  soundBlock.append(soundRow);
  if (lastSoundOutcome) soundBlock.append(element('p', 'meta sound-result', soundOutcomeText(lastSoundOutcome)));
  container.append(soundBlock);
  const keyboardBox = element('input'); keyboardBox.type = 'checkbox'; keyboardBox.defaultChecked = enterSends();
  keyboardBox.setAttribute('aria-label', 'Отправлять по Enter');
  const keyboard = element('label', 'profile-toggle'); keyboard.append(keyboardBox, element('span', '', 'Отправлять по Enter'));
  container.append(keyboard, element('p', 'meta', 'Экранная клавиатура: Enter переносит строку. Для обычной клавиатуры: выключено — Enter переносит строку, Ctrl/⌘ + Enter отправляет. Настройка для этого браузера.'));
  keyboardBox.addEventListener('change', () => {
    if (!bootstrap) return;
    try { localStorage.setItem(`pm-chat:enter-sends:${bootstrap.user.workspace_id}:${bootstrap.user.id}`, keyboardBox.checked ? 'on' : 'off'); } catch { toast('Браузер не сохранил настройку'); }
    renderShell();
  });
  soundBox.addEventListener('change', () => { setIncomingSound(soundBox.checked); renderShell(); if (soundBox.checked) void attempt(); });

  const footer = element('div', 'profile-dialog-footer');

  const logoutButton = element('button', 'ghost', 'Выйти'); logoutButton.type = 'button';
  logoutButton.addEventListener('click', () => void logoutUser());
  footer.append(logoutButton);
  container.append(footer);
}
// Fixed-size previews share the source attachment's authorized endpoint; no copied upload.
function appendReplyImages(container: HTMLElement, attachments?: Attachment[]): void {
  const images = attachments?.filter(attachment => isAttachmentImage(attachment.mime));
  if (!images?.length) return;
  const strip = element('span', 'reply-images');
  for (const attachment of images) {
    const image = attachmentImage(attachment, 'reply-image');
    image.alt = attachment.name; image.width = 64; image.height = 64; image.loading = 'lazy'; image.decoding = 'async';
    strip.append(image);
  }
  container.append(strip);
}

// Reply and quote share the same preview and narrowly scoped cancellation.
function cancelReply(): void {
  clearStoredReply();
  replyToMessageId = null; composerReplyTarget = null;
  renderShell();
  root.querySelector<MessageInput>('[aria-label="Сообщение"]')?.focus({ preventScroll: true });
}

function composerReplyState(): HTMLElement {
  const replyId = replyToMessageId;
  const ownsComposer = selectionGuard();
  const target = messages.find((item) => item.id === replyId) ?? (composerReplyTarget?.id === replyId ? composerReplyTarget : null);
  const state = element('div', 'composer-state');
  const content = element('div', 'composer-reply-content', target ? `Ответ: ${target.sender?.name ?? 'Система'}: ${target.deleted_at ? 'Сообщение удалено' : target.body.slice(0, 80) || 'Вложение'}` : 'Ответ на сообщение');
  if (target && !target.deleted_at) appendReplyImages(content, target.attachments);
  const cancel = menuItem('', 'close', 'composer-reply-cancel');
  cancel.setAttribute('aria-label', 'Отменить ответ'); cancel.title = 'Отменить ответ';
  cancel.addEventListener('click', () => { if (ownsComposer() && replyToMessageId === replyId) cancelReply(); });
  state.append(content, cancel);
  return state;
}

/** Quoting: the words go into the composer as a quotation, and the message also points back at the
 *  one it came from.
 *
 *  Both halves are needed, which the first version got wrong. Carrying only the words leaves a
 *  quote hanging in the air -- a reader sees whose sentence it is but not which message it came
 *  from and cannot get to it ("непонятно, откуда процитировано"). Pointing only at the message
 *  loses the fragment, which is the whole reason people quote one sentence out of ten. So a quote
 *  carries its words and its own source link, independently of the reply target. If part of the message is selected, that part is quoted. */
function quoteMessage(message: Message): void {
  const field = root.querySelector<MessageInput>('[aria-label="Сообщение"]');
  if (!field) return;
  const selected = selectionWithin(message.id);
  const source = (selected || message.body || '').trim();
  if (!source) { toast('Нечего цитировать'); return; }
  messageSelection = null;   // used up: the next quote must not reuse it

  // A long message would fill the composer and could push the whole thing over the length limit,
  // so the quote is clipped -- the point is what is being answered, not a full copy.
  const lines = source.split(/\r?\n/);
  const kept = lines.slice(0, 12);
  let quoted = kept.join('\n');
  if (quoted.length > 600) quoted = `${quoted.slice(0, 600).trimEnd()}…`;
  else if (kept.length < lines.length) quoted = `${quoted}\n…`;

  const author = message.sender?.name ?? 'Системное сообщение';
  // Each quotation keeps its own permalink; the reply target is independent.
  const sourcePath = `/c/${encodeURIComponent(message.conversation_id)}/m/${encodeURIComponent(message.id)}`;
  const block = [`> ${author}:`, ...quoted.split('\n').map((line) => `> ${line}`), `> [К цитируемому сообщению](${sourcePath})`, '', ''].join('\n');
  const at = field.selectionStart ?? field.value.length;
  const before = field.value.slice(0, at);
  const prefix = !before || before.endsWith('\n') ? '' : '\n';
  field.setRangeText(`${prefix}${block}`, at, field.selectionEnd ?? at, 'end');
  // Preserve an independently selected reply; a standalone quote keeps the existing reply UX.
  if (!replyToMessageId) setReplyTarget(message);
  const state = root.querySelector('.composer-state');
  if (state) state.replaceWith(composerReplyState());
  else field.closest('form')?.prepend(composerReplyState());
  field.focus(); field.dispatchEvent(new Event('input'));
}

// The last thing selected inside a message, remembered while it is being selected. Reading the
// selection at the moment "Цитировать" is pressed does not work: opening the "⋯" menu moves focus
// to its first item and the selection is gone by then -- the quote silently became the whole
// message instead of the sentence the person had picked.
let messageSelection: { messageId: string; text: string } | null = null;
document.addEventListener('selectionchange', () => {
  const selection = window.getSelection();
  if (!selection || selection.isCollapsed || !selection.rangeCount) return;   // a collapse is not a cancel
  const node = selection.getRangeAt(0).commonAncestorContainer;
  const element = node.nodeType === Node.ELEMENT_NODE ? node as Element : node.parentElement;
  const message = element?.closest<HTMLElement>('.message');
  const text = selection.toString().trim();
  if (message?.dataset.messageId && text) messageSelection = { messageId: message.dataset.messageId, text };
});

/** What the person selected inside that message, if anything. Tied to the message id, so a
 *  selection made in one message is never quoted into another. */
function selectionWithin(messageId: string): string {
  return messageSelection?.messageId === messageId ? messageSelection.text : '';
}

let activeTodoDialog: TodoDialog | null = null;
let activeTodoConversationId:string|null=null;
function isPersonalConversation(): boolean {
  const row = conversations.find(item=>item.id===current?.id) ?? currentDetails;
  return Boolean(bootstrap && current?.type==='direct' && row?.peer_user_id===bootstrap.user.id);
}
function todoOwner():string {return bootstrap?`${sessionGeneration}:${bootstrap.user.workspace_id}:${bootstrap.user.id}`:'';}
type TodoSummaryEntry={value:TodoSummary|null;requestedAt:number;generation:number;epoch:string|null};
let todoSummaryOwner='';let todoSummaryGeneration=0;const todoSummaries=new Map<string,TodoSummaryEntry>();
function ensureTodoSummaryOwner():void {if(todoSummaryOwner!==todoOwner()){todoSummaries.clear();todoSummaryOwner=todoOwner();++todoSummaryGeneration;}}
function todoLabel(personal:boolean):string {
  ensureTodoSummaryOwner();const key=personal?'personal':current?.id;const entry=key?todoSummaries.get(key):undefined;
  const summary=entry&&(personal||(currentDetails?.id===key&&entry.epoch===currentDetails?.membership_epoch_id))?entry.value:null;
  return summary?`Задачи · ${summary.completed} из ${summary.total}`:'Задачи';
}
function paintTodoHeader(button:HTMLElement):void {
  let label=button.querySelector<HTMLElement>('.todo-header-label');if(!label){label=element('span','todo-header-label');button.append(label);}label.textContent=todoLabel(isPersonalConversation());button.dataset.tip=isPersonalConversation()?`Мои задачи: ${label.textContent}`:label.textContent;
}
function storeTodoSummary(key:string,entry:TodoSummaryEntry):void {
  todoSummaries.set(key,entry);
  if(todoSummaries.size>64)todoSummaries.delete(todoSummaries.keys().next().value!);
}
function acceptTodoSummary(key:string,summary:TodoSummary,epoch:string|null):void {
  if(!bootstrap||!Number.isSafeInteger(summary.total)||!Number.isSafeInteger(summary.completed)||summary.completed<0||summary.completed>summary.total)return;
  ensureTodoSummaryOwner();storeTodoSummary(key,{value:summary,requestedAt:Date.now(),generation:++todoSummaryGeneration,epoch});
  if((key==='personal'&&isPersonalConversation())||(key===current?.id&&!isPersonalConversation())){const button=root.querySelector<HTMLElement>('.chat-tasks-button');if(button)paintTodoHeader(button);}
}
function refreshTodoSummary(personal:boolean,force=false):void {
  if(!bootstrap||(!personal&&!current))return;ensureTodoSummaryOwner();
  const key=personal?'personal':current!.id,epoch=personal?null:currentDetails?.membership_epoch_id??null,entry=todoSummaries.get(key);
  if(!force&&entry&&entry.epoch===epoch&&Date.now()-entry.requestedAt<30000)return;
  const generation=++todoSummaryGeneration,valid=personal?sessionGuard():selectionGuard(),owner=todoOwner();
  storeTodoSummary(key,{value:entry?.epoch===epoch?entry.value:null,requestedAt:Date.now(),generation,epoch});
  const path=personal?'/v1/todo/personal/summary':`/v1/conversations/${encodeURIComponent(key)}/todo/summary`;
  void api<TodoSummary>(path).then(summary=>{
    if(valid()&&todoOwner()===owner&&todoSummaries.get(key)?.generation===generation)acceptTodoSummary(key,summary,epoch);
  }).catch(()=>undefined);
}
function todoViewKey(scope:string):string {return `pm-chat:todo-view:${bootstrap!.user.workspace_id}:${bootstrap!.user.id}:${scope}`;}
function showTasks(personal: boolean): void {
  if(isPersonalConversation())personal=true;
  if (!bootstrap || (!personal && !current)) return;
  activeTodoDialog?.close();
  const ownsSelection = personal ? sessionGuard() : selectionGuard();
  const epoch = personal ? null : currentDetails?.membership_epoch_id;
  const valid = (): boolean => ownsSelection() && (personal || epoch === currentDetails?.membership_epoch_id);
  const path = personal ? '/v1/todo/personal' : `/v1/conversations/${encodeURIComponent(current!.id)}/todo`;
  const scope=personal?'personal':current!.id;
  const viewKey=todoViewKey(scope);
  const controller = showTodoList({ title: personal ? 'Мои задачи' : 'Задачи чата', subtitle: personal ? undefined : current!.display_title,
    path, personal, relatedPath:personal?'/v1/todo/related':undefined, membershipEpoch: personal ? undefined : epoch, request: api, valid, notify: toast,
    storageKey:`pm-chat:todo-hidden:${bootstrap.user.workspace_id}:${bootstrap.user.id}:${scope}`,
    onClose: () => { if (activeTodoDialog === controller) {activeTodoDialog = null;activeTodoConversationId=null;} },
    onCreated: () => { if (personal && valid()) void loadConversations(ownsSelection, true).catch(() => undefined); },
    onSummary: (summary,related)=>{if(valid()&&!related)acceptTodoSummary(scope,summary,personal?null:epoch??null);},
    onChanged: item=>{if(valid()){if(item.conversation_id)todoSummaries.delete(item.conversation_id);refreshTodoSummary(personal,true);}},
    onViewChange: view=>switchView(view),
    jump: item => { if(!valid())return;if(item.source_conversation_id&&item.source_message_id)void openConversation(item.source_conversation_id,item.source_message_id);else if(item.conversation_id)void openConversation(item.conversation_id); },
  });
  activeTodoDialog = controller;
  let switching=false;
  async function switchView(view:'dialog'|'inline'):Promise<void> {
    if(!valid()||activeTodoDialog!==controller||switching)return;
    switching=true;try {
      if(personal&&view==='inline'&&!isPersonalConversation()){
        const self=await api<{id:string}>(`/v1/direct/${encodeURIComponent(bootstrap!.user.id)}`,{method:'POST',body:'{}'});
        if(!valid()||activeTodoDialog!==controller)return;
        await openConversation(self.id);
      }
      if(!valid()||activeTodoDialog!==controller)return;
      const host=root.querySelector<HTMLElement>('#messages');
      if(view==='inline'&&((personal&&!isPersonalConversation())||!host))throw new Error('Не удалось открыть задачи в чате');
      activeTodoConversationId=view==='inline'?current!.id:null;
      controller.setView(view,host??undefined);
      try{localStorage.setItem(viewKey,view);}catch{ /* Current presentation still works. */ }
      renderShell();
      if(view==='inline')controller.element.scrollIntoView({block:'nearest'});
    }catch(error){if(valid()&&activeTodoDialog===controller)toast(errorMessage(error));}finally{switching=false;}
  }
  try{if(localStorage.getItem(viewKey)==='inline')void switchView('inline');}catch{ /* Default modal remains available. */ }
}
async function addMessageTask(message: Message, personal: boolean): Promise<void> {
  if(isPersonalConversation())personal=true;
  const id = current?.id; const epoch = currentDetails?.membership_epoch_id;
  if (!id || message.conversation_id !== id || message.pending || message.deleted_at) return;
  return runUiAction(async (request, valid) => {
    await request(personal ? '/v1/todo/personal' : `/v1/conversations/${encodeURIComponent(id)}/todo`, {
      method: 'POST', body: JSON.stringify({ source_message_id: message.id, client_request_id: crypto.randomUUID(), ...(!personal ? { membership_epoch_id: epoch } : {}) }),
    });
    toast(personal ? 'Добавлено в мои задачи' : 'Добавлено в задачи чата');
    refreshTodoSummary(personal,true);
    if (personal) await loadConversations(valid, true).catch(() => undefined);
  });
}
// Forwarding: pick a chat, optionally say something of your own, send. The list is the chats this
// client already holds -- forwarding is a quick action, and a round trip to search for a chat would
// make it a slow one.
let activeForwardDialog: { valid: () => boolean; close: () => void } | null = null;
function showForwardDialog(message: Message): void {
  if(message.attachments?.some(attachment=>attachment.managed||attachment.upload)){toast('Пересылка файлов из хранилища пока недоступна. Сообщение сохранено.');return;}
  if (activeForwardDialog || !bootstrap) return;
  const valid = selectionGuard();
  const ownUserId = bootstrap.user.id;
  const dialog = element('dialog', 'message-editor forward-dialog'); dialog.setAttribute('aria-label', 'Переслать сообщение');
  const close = (): void => { if (activeForwardDialog?.close === close) activeForwardDialog = null; if (dialog.open) dialog.close(); dialog.remove(); };
  dialog.addEventListener('close', () => { if (activeForwardDialog?.close === close) activeForwardDialog = null; dialog.remove(); });
  activeForwardDialog = { valid, close };

  const head = element('div', 'chat-info-head');
  const closeX = element('button', 'ghost', '✕'); closeX.type = 'button'; closeX.setAttribute('aria-label', 'Закрыть'); closeX.addEventListener('click', close);
  head.append(element('h3', '', 'Переслать сообщение'), closeX);

  const preview = element('p', 'meta forward-preview', (message.body || 'Вложение').slice(0, 160));
  const filter = element('input'); filter.type = 'search';
  filter.setAttribute('aria-label', 'Фильтр по названию'); filter.placeholder = 'Фильтр по названию';
  // The same shape as the add-members list: a checkbox, an avatar, a name. Picking chats and picking
  // colleagues are the same kind of choice and should not look like two different products.
  const list = element('div', 'add-members-list forward-list'); list.setAttribute('role', 'group'); list.setAttribute('aria-label', 'Куда переслать');
  const comment = element('textarea'); comment.setAttribute('aria-label', 'Комментарий'); comment.rows = 2;
  comment.placeholder = 'Комментарий (необязательно)';
  const note = element('p', 'meta', 'Пересланное останется в том чате, даже если удалить оригинал.');
  const error = element('p', 'error'); error.hidden = true; error.setAttribute('role', 'alert');

  // Several chats at once, ticked the way people are ticked when they are added to a group: the
  // same gesture for the same kind of choice. No highlighted row -- the tick already says what is
  // chosen, and a coloured plate behind it only adds weight.
  const chosen = new Set<string>();
  let sending = false;
  const send = element('button', 'primary', 'Переслать'); send.type = 'button'; send.disabled = true;
  const refreshSend = (): void => {
    send.disabled = sending || !chosen.size;
    send.textContent = chosen.size > 1 ? `Переслать (${chosen.size})` : 'Переслать';
  };
  // Chats first, then colleagues there is no chat with yet. A chat nobody has written in is not in
  // the list at all, so without the second half there would be no way to pass a message on to
  // someone you have not written to before -- and the person would simply not be here to pick.
  type Target = { key: string; title: string; avatar: string | null; conversationId: string | null; userId: string | null };
  let colleagues: AddCandidate[] = [];
  const targets = (): Target[] => {
    const selfChat = conversations.find(item=>item.type==='direct' && item.peer_user_id===ownUserId);
    const self: Target = {key:`u:${ownUserId}`,title:'Сообщения себе',avatar:selfChat?conversationAvatarUrl(selfChat):null,conversationId:selfChat?.id??null,userId:selfChat?null:ownUserId};
    const chats: Target[] = conversations.filter((item) => !item.archived_at && item.peer_user_id!==ownUserId).map((item) => ({
      key: `c:${item.id}`, title: item.display_title, conversationId: item.id, userId: null,
      avatar: conversationAvatarUrl(item),
    }));
    const already = new Set(conversations.map((item) => item.peer_user_id).filter(Boolean));
    const people: Target[] = colleagues.filter((person) => !already.has(person.id) && person.id !== bootstrap?.user.id).map((person) => ({
      key: `u:${person.id}`, title: person.display_name, conversationId: null, userId: person.id,
      avatar: person.avatar_key ? userAvatarUrl(person.id, person.avatar_key) : null,
    }));
    return [self, ...chats, ...people];
  };
  const paint = (): void => {
    list.replaceChildren();
    const needle = filter.value.trim().toLocaleLowerCase('ru-RU');
    const shown = targets()
      .filter((item) => !needle || item.title.toLocaleLowerCase('ru-RU').includes(needle))
      .slice(0, 100);
    if (!shown.length) { list.append(element('p', 'meta', 'Ничего не найдено')); return; }
    for (const item of shown) {
      const row = element('label', 'add-members-row');
      const box = element('input') as HTMLInputElement;
      box.type = 'checkbox'; box.checked = chosen.has(item.key);
      box.setAttribute('aria-label', item.title);
      box.addEventListener('change', () => { if (box.checked) chosen.add(item.key); else chosen.delete(item.key); refreshSend(); });
      row.append(box, avatarNode(item.title, item.avatar), element('span', 'chat-title', item.title));
      list.append(row);
    }
  };
  filter.addEventListener('input', paint);
  paint();
  void api<{ users: AddCandidate[] }>('/v1/users?limit=500')
    .then((answer) => { if (valid() && dialog.isConnected) { colleagues = answer.users.filter((person) => !person.is_bot); paint(); } })
    .catch(() => undefined);   // the chats are already on screen; colleagues are the addition

  send.addEventListener('click', () => {
    if (!valid() || sending || !chosen.size) return;
    const picked = targets().filter((item) => chosen.has(item.key));
    sending = true; refreshSend(); error.hidden = true;
    void runUiAction(async (request) => {
      const done: string[] = [];
      try {
        // One call per chat, in order. If the third fails, the first two have already arrived --
        // saying how far it got is more useful than pretending nothing happened.
        for (const target of picked) {
          if (!valid() || !dialog.isConnected) { close(); return; }
          // A colleague with no chat yet: the chat is made first, the same call the avatar uses,
          // which returns the existing one if there is any.
          const conversationId = target.conversationId
            ?? String((await request<{ id: string }>(`/v1/direct/${encodeURIComponent(target.userId!)}`, { method: 'POST', body: '{}' })).id);
          if (!valid() || !dialog.isConnected) { close(); return; }
          await request(`/v1/conversations/${encodeURIComponent(conversationId)}/forward`, {
            method: 'POST', body: JSON.stringify({ message_ids: [message.id], comment: comment.value.trim() || undefined }),
          });
          done.push(target.title);
        }
        if (!valid()) return;
        close();
        toast(done.length === 1 ? `Переслано в «${done[0]}»` : `Переслано в ${done.length} чата(ов)`);
        await loadConversations().catch(() => undefined);
        if (valid()) renderShell();
      } catch (failure) {
        if (!valid() || !dialog.isConnected) { close(); return; }
        const reason = failure instanceof Error ? failure.message : 'Не удалось переслать';
        error.textContent = done.length ? `${reason}. Успели переслать в: ${done.join(', ')}` : reason;
        error.hidden = false; sending = false; refreshSend();
      }
    });
  });
  const cancel = element('button', 'ghost', 'Отмена'); cancel.type = 'button'; cancel.addEventListener('click', close);
  const footer = element('div', 'forward-actions'); footer.append(send, cancel);
  const body = element('div');
  body.append(preview, filter, list, comment, note, error, footer);
  dialog.append(head, body);
  document.body.append(dialog);
  dialog.showModal();
}

// Bug report dialog: title + details + optional screenshot (file or paste), plus technical context.
// Never includes message content. Lives on document.body, so renders don't wipe a draft.
let bugReportDraft: {valid:()=>boolean;title:string;details:string;screenshot:File|null}|null = null;
let activeBugDialog: { valid: () => boolean; close: () => void } | null = null;
const bugScreenshotTypes = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
function bugReportContext(): Record<string, unknown> {
  return {
    url: location.href, user_agent: navigator.userAgent, language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    viewport: `${innerWidth}x${innerHeight}`, screen: `${screen.width}x${screen.height}`, pixel_ratio: devicePixelRatio,
    build: document.querySelector('script[src*="app.js"]')?.getAttribute('src') ?? null,
    online: navigator.onLine, connection: connectionLabel,
    conversation_id: current?.id ?? null, conversation_type: current?.type ?? null,
    startup_error: readStartupFailure((key) => sessionStorage.getItem(key)),
  };
}
function showBugReportDialog(): void {
  if (activeBugDialog || !bootstrap) return;
  const valid = sessionGuard();
  if (bugReportDraft && !bugReportDraft.valid()) bugReportDraft = null;
  const savedDraft = bugReportDraft; let submitted = false; let remembered = false;
  const remember = ():void => { if (remembered) return; remembered = true; bugReportDraft = valid() && !submitted ? {valid,title:title.value,details:details.value,screenshot} : null; };
  const dialog = element('dialog', 'message-editor bug-report'); dialog.setAttribute('aria-label', 'Сообщить о баге');
  const close = (): void => { if (submit.disabled && valid() && !submitted) return; remember(); if (activeBugDialog?.close === close) activeBugDialog = null; if (dialog.open) dialog.close(); dialog.remove(); };
  dialog.addEventListener('close', () => { remember(); if (activeBugDialog?.close === close) activeBugDialog = null; dialog.remove(); });
  dialog.addEventListener('cancel', event => { if (submit.disabled && valid()) event.preventDefault(); });
  activeBugDialog = { valid, close };
  const form = element('form');
  const title = element('input'); title.required = true; title.maxLength = 180; title.placeholder = 'Кратко: что сломалось'; title.setAttribute('aria-label', 'Кратко, что сломалось');
  const details = element('textarea'); details.required = true; details.maxLength = 5000; details.rows = 6;
  details.placeholder = 'Что делали, что ожидали и что произошло'; details.setAttribute('aria-label', 'Подробности');
  let screenshot: File | null = null;
  const file = element('input'); file.type = 'file'; file.accept = bugScreenshotTypes.join(','); file.hidden = true;
  const attach = element('button', 'ghost', 'Приложить скриншот'); attach.type = 'button';
  const attached = element('span', 'meta');
  const setScreenshot = (next: File | null): void => {
    if (next && (!bugScreenshotTypes.includes(next.type) || next.size > 8 * 1024 * 1024)) { error.textContent = 'Скриншот: PNG, JPEG, WebP или GIF до 8 МБ'; error.hidden = false; return; }
    screenshot = next; error.hidden = true;
    attached.textContent = next ? `Скриншот: ${next.name || 'из буфера обмена'}` : 'Можно вставить скриншот через Ctrl/⌘+V';
    attach.textContent = next ? 'Убрать скриншот' : 'Приложить скриншот';
  };
  attach.addEventListener('click', () => { if (screenshot) setScreenshot(null); else file.click(); });
  file.addEventListener('change', () => { setScreenshot(file.files?.[0] ?? null); file.value = ''; });
  dialog.addEventListener('paste', (event) => {
    const image = [...(event.clipboardData?.files ?? [])].find((item) => item.type.startsWith('image/'));
    if (image) { event.preventDefault(); setScreenshot(image); }
  });
  const error = element('p', 'error'); error.hidden = true; error.setAttribute('role', 'alert');
  const submit = element('button', 'primary', 'Отправить'); submit.type = 'submit';
  const cancel = element('button', 'ghost', 'Закрыть'); cancel.type = 'button'; cancel.title = 'Черновик сохранится до выхода из аккаунта или перезагрузки страницы'; cancel.addEventListener('click', close);
  const shot = element('div', 'bug-report-shot'); shot.append(file, attach, attached);
  const actions = element('div', 'bug-report-actions'); actions.append(submit, cancel);
  form.append(
    element('p', 'meta', 'Мы приложим адрес страницы, браузер и размер окна. Текст ваших переписок не отправляется.'),
    title, details, shot, error, actions);
  title.value = savedDraft?.title ?? ''; details.value = savedDraft?.details ?? '';
  setScreenshot(savedDraft?.screenshot ?? null);
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    if (!valid() || submit.disabled) return;
    submit.disabled = true; cancel.disabled = true; closeX.disabled = true; error.hidden = true;
    void (async () => {
      try {
        const created = await api<{ id: string; number: number }>('/v1/bug-reports', { method: 'POST', body: JSON.stringify({ title: title.value, description: details.value, context: bugReportContext() }) });
        let shotFailed = false;
        if (screenshot) {
          try { await api(`/v1/bug-reports/${created.id}/screenshot`, { method: 'PUT', headers: { 'Content-Type': 'application/octet-stream' }, body: await screenshot.arrayBuffer() }); }
          catch { shotFailed = true; }
        }
        if (!valid()) return;
        submitted = true; close();
        toast(shotFailed ? `Баг №${created.number} принят, но скриншот не приложился` : `Спасибо! Баг №${created.number} принят`);
      } catch (failure) {
        if (!valid()) return;
        submit.disabled = false; cancel.disabled = false; closeX.disabled = false;
        error.textContent = failure instanceof Error ? failure.message : 'Не удалось отправить'; error.hidden = false;
      }
    })();
  });
  const mine = element('div', 'bug-report-mine'); mine.hidden = true;
  const newTab = element('button', 'ghost', 'Новый отчёт'); newTab.type = 'button';
  const mineTab = element('button', 'ghost', 'Мои отчёты'); mineTab.type = 'button';
  const showTab = (showMine: boolean): void => {
    form.hidden = showMine; mine.hidden = !showMine;
    newTab.setAttribute('aria-pressed', String(!showMine)); mineTab.setAttribute('aria-pressed', String(showMine));
    if (showMine) void renderOwnBugReports(mine, valid); else title.focus();
  };
  newTab.addEventListener('click', () => showTab(false)); mineTab.addEventListener('click', () => showTab(true));
  const head = element('div', 'chat-info-head');
  const closeX = element('button', 'ghost', '✕'); closeX.type = 'button'; closeX.setAttribute('aria-label', 'Закрыть'); closeX.addEventListener('click', close);
  head.append(element('h3', '', 'Сообщить о баге'), closeX);
  const tabs = element('div', 'bug-report-tabs'); tabs.append(newTab, mineTab);
  dialog.append(head, tabs, form, mine); document.body.append(dialog); dialog.showModal(); showTab(false);
}

// Wording matters more than it looks: these two lines are the whole answer to "is my report
// done?". «Ждёт проверки» sent the owner to look for a fix on production that was still sitting in
// a branch, and «Готово» did not say the fix had actually shipped. A status is only useful if it
// names the state of the product, not the state of our work.
const bugStatusLabels: Record<string, string> = {
  new: 'Новый', in_progress: 'В работе', waiting_check: 'Исправление готово, ждёт выкладки', needs_rework: 'На доработке',
  done: 'Исправлено, на проде', rejected: 'Отклонён', needs_human: 'На проверке у человека', enhancement: 'Предложение — ждёт решения',
};
function bugStatusLabel(status: string): string { return bugStatusLabels[status] ?? status; }
function bugDate(value: string): string { return new Date(value).toLocaleString([], { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); }

async function renderOwnBugReports(container: HTMLElement, valid: () => boolean): Promise<void> {
  container.replaceChildren(element('p', 'meta', 'Загрузка…'));
  try {
    const { reports } = await api<{ reports: { id: string; number: number; title: string; status: string; updated_at: string }[] }>('/v1/bug-reports');
    if (!valid()) return;
    container.replaceChildren();
    if (!reports.length) { container.append(element('p', 'meta', 'Вы ещё не отправляли отчётов.')); return; }
    const list = element('div', 'bug-report-list'); list.setAttribute('role', 'list'); list.setAttribute('aria-label', 'Мои отчёты');
    for (const item of reports) {
      const row = element('button', 'bug-report-row'); row.type = 'button'; row.setAttribute('role', 'listitem');
      row.append(element('span', 'chat-title', `№${item.number} · ${item.title}`),
        element('span', `bug-status bug-status-${item.status}`, bugStatusLabel(item.status)), element('span', 'meta', bugDate(item.updated_at)));
      row.addEventListener('click', () => void renderOwnBugReport(container, valid, item.id));
      list.append(row);
    }
    container.append(list);
  } catch (error) { if (valid()) container.replaceChildren(element('p', 'error', error instanceof Error ? error.message : 'Не удалось загрузить')); }
}

async function renderOwnBugReport(container: HTMLElement, valid: () => boolean, id: string): Promise<void> {
  container.replaceChildren(element('p', 'meta', 'Загрузка…'));
  try {
    const report = await api<{ number: number; title: string; description: string; status: string; has_screenshot: boolean; created_at: string; events: { status: string; reply: string | null; created_at: string }[] }>(`/v1/bug-reports/${encodeURIComponent(id)}`);
    if (!valid()) return;
    const back = element('button', 'ghost', '‹ Все отчёты'); back.type = 'button'; back.addEventListener('click', () => void renderOwnBugReports(container, valid));
    const timeline = element('ol', 'bug-report-timeline'); timeline.setAttribute('aria-label', 'История');
    for (const event of report.events) {
      const step = element('li');
      step.append(element('span', 'meta', bugDate(event.created_at)), element('span', `bug-status bug-status-${event.status}`, bugStatusLabel(event.status)));
      if (event.reply) step.append(element('p', 'bug-report-reply', event.reply));
      timeline.append(step);
    }
    container.replaceChildren(back, element('h4', '', `№${report.number} · ${report.title}`),
      element('span', `bug-status bug-status-${report.status}`, bugStatusLabel(report.status)),
      element('p', 'bug-report-description', report.description),
      ...(report.has_screenshot ? [element('p', 'meta', 'Скриншот приложен')] : []), timeline);
  } catch (error) { if (valid()) container.replaceChildren(element('p', 'error', error instanceof Error ? error.message : 'Не удалось загрузить')); }
}

function showProfileDialog(): void {
  if (activeProfileDialog || !bootstrap) return;
  const valid = sessionGuard();
  const dialog = element('dialog', 'message-editor'); dialog.setAttribute('aria-label', 'Настройки профиля');
  const close = (): void => { if (activeProfileDialog?.close === close) activeProfileDialog = null; if (dialog.open) dialog.close(); dialog.remove(); };
  dialog.addEventListener('cancel', () => { if (activeProfileDialog?.close === close) activeProfileDialog = null; });
  dialog.addEventListener('close', () => { if (activeProfileDialog?.close === close) activeProfileDialog = null; dialog.remove(); });
  activeProfileDialog = { valid, close };
  const header = element('div', 'chat-info-head');
  header.append(element('h3', '', 'Настройки профиля'));
  const closeButton = element('button', 'ghost', '✕'); closeButton.type = 'button'; closeButton.setAttribute('aria-label', 'Закрыть настройки профиля');
  closeButton.addEventListener('click', close);
  header.append(closeButton);
  const content = element('div');
  const refresh = (): void => renderProfileDialogContent(content, refresh);
  refresh();
  void Promise.all([loadLastPushReceived(), loadPushDiagnostics()]).then(() => { if (activeProfileDialog?.close === close && !content.querySelector('.workspace-settings')) refresh(); });
  dialog.append(header, content);
  document.body.append(dialog);
  dialog.showModal();
}

type NotificationPreferences = { enabled: boolean; preview: boolean; default_mode: string; personal_default_mode?: string | null; quiet_start: string | null; quiet_end: string | null; timezone: string; email_digest_enabled: boolean; email_digest_hours: number };

async function saveNotificationPreferences(preferences: NotificationPreferences): Promise<void> {
  return runUiAction(async (request, valid) => {
    await request('/v1/notification-preferences', { method: 'PUT', body: JSON.stringify(preferences) });
    await loadConversations(valid); if (valid()) renderShell();
    toast('Настройки уведомлений сохранены');
  });
}

// One form instead of the old chain of browser prompt() boxes, which was unusable on a phone.
async function notificationSettingsFromUi(): Promise<void> {
  return runUiAction(async (request) => {
    const preferences = await request<NotificationPreferences>('/v1/notification-preferences');
    if (activeEditor) return;
    const dialog = element('dialog', 'message-editor notification-settings');
    dialog.setAttribute('aria-label', 'Когда присылать уведомления');
    const valid = sessionGuard();
    const close = (): void => { if (activeEditor?.close === close) activeEditor = null; if (dialog.open) dialog.close(); dialog.remove(); };
    dialog.addEventListener('close', () => { if (activeEditor?.close === close) activeEditor = null; dialog.remove(); });
    activeEditor = { dialog, valid, close };
    const header = element('div', 'chat-info-head');
    header.append(element('h3', '', 'Когда присылать уведомления'));
    const closeButton = element('button', 'ghost', '✕'); closeButton.type = 'button';
    closeButton.setAttribute('aria-label', 'Закрыть настройки уведомлений');
    closeButton.addEventListener('click', close);
    header.append(closeButton);
    const form = element('form', 'notification-form');

    const enabledLabel = element('label', 'profile-toggle');
    const enabledBox = element('input'); enabledBox.type = 'checkbox'; enabledBox.defaultChecked = preferences.enabled;
    enabledBox.setAttribute('aria-label', 'Уведомления включены');
    enabledLabel.append(enabledBox, element('span', '', 'Уведомления включены'));
    const modeLabel = element('label', 'notification-field', 'В групповых чатах по умолчанию');
    const mode = element('select'); mode.setAttribute('aria-label', 'Присылать');
    for (const [value, text] of [['all', 'Все сообщения'], ['mentions', 'Упоминания и ответы мне'], ['off', 'Не присылать']] as const) {
      const option = element('option', '', text); option.value = value;
      if (value === preferences.default_mode) option.defaultSelected = true;
      mode.append(option);
    }
    modeLabel.append(mode);
    const personalLabel = element('label', 'notification-field', 'В личных переписках по умолчанию');
    const personalMode = element('select'); personalMode.setAttribute('aria-label', 'В личных переписках по умолчанию');
    for (const [value, text] of [['', 'Как в групповых чатах'], ['all', 'Все сообщения'], ['mentions', 'Упоминания и ответы мне'], ['off', 'Не присылать']] as const) {
      const option = element('option', '', text); option.value = value;
      option.defaultSelected = value === (preferences.personal_default_mode ?? ''); personalMode.append(option);
    }
    personalLabel.append(personalMode);
    const policyHint = element('p', 'meta', 'Индивидуальные настройки чатов имеют приоритет. Значок приложения считает сообщения, подходящие под настройки уведомлений.');

    const preview = element('label', 'profile-toggle');
    const previewBox = element('input'); previewBox.type = 'checkbox'; previewBox.defaultChecked = preferences.preview;
    previewBox.setAttribute('aria-label', 'Показывать текст сообщения');
    preview.append(previewBox, element('span', '', 'Показывать имя и текст сообщения на экране блокировки'));

    const quiet = element('div', 'notification-quiet');
    quiet.append(element('span', 'meta', 'Тихие часы (пусто — не молчать)'));
    const quietRow = element('div', 'notification-quiet-row');
    const from = element('input'); from.type = 'time'; from.setAttribute('aria-label', 'Тихие часы с'); from.defaultValue = (preferences.quiet_start ?? '').slice(0, 5);
    const to = element('input'); to.type = 'time'; to.setAttribute('aria-label', 'Тихие часы до'); to.defaultValue = (preferences.quiet_end ?? '').slice(0, 5);
    quietRow.append(element('span', '', 'с'), from, element('span', '', 'до'), to);
    quiet.append(quietRow);
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || preferences.timezone;
    quiet.append(element('span', 'meta', `Часовой пояс: ${timezone}`));

    // The e-mail fallback for the times nobody has the chat open at all.
    const digest = element('div', 'notification-quiet');
    const digestToggle = element('label', 'profile-toggle');
    const digestBox = element('input'); digestBox.type = 'checkbox'; digestBox.defaultChecked = preferences.email_digest_enabled !== false;
    digestBox.setAttribute('aria-label', 'Письмо о непрочитанных');
    digestToggle.append(digestBox, element('span', '', 'Письма о непрочитанных сообщениях'));
    const hoursRow = element('div', 'notification-quiet-row');
    const hours = element('input'); hours.type = 'number'; hours.min = '1'; hours.max = '72';
    hours.setAttribute('aria-label', 'Периодичность писем в часах');
    hours.defaultValue = String(preferences.email_digest_hours ?? 4);
    hoursRow.append(element('span', '', 'раз в'), hours, element('span', '', 'ч., если есть непрочитанные'));
    digest.append(digestToggle, hoursRow);

    const actions = element('div', 'notification-actions');
    const save = element('button', '', 'Сохранить'); save.type = 'submit';
    const cancel = element('button', 'ghost', 'Отмена'); cancel.type = 'button'; cancel.addEventListener('click', close);
    actions.append(save, cancel);
    form.append(enabledLabel, modeLabel, personalLabel, policyHint, preview, quiet, digest, actions);
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const selected = mode.value;
      close();
      void saveNotificationPreferences({
        enabled: enabledBox.checked, preview: previewBox.checked, default_mode: selected, personal_default_mode: personalMode.value || null, timezone,
        quiet_start: from.value || null, quiet_end: to.value || null,
        email_digest_enabled: digestBox.checked,
        email_digest_hours: Math.min(72, Math.max(1, Number(hours.value) || 4)),
      });
    });
    dialog.append(header, form);
    document.body.append(dialog);
    dialog.showModal();
  });
}

async function requestExportFromUi(): Promise<void> {
  return runUiAction(async (request, valid) => {
    const job = await request<{ id: string }>('/v1/exports', { method: 'POST', body: '{}' }); toast('Экспорт готовится');
    for (let attempt = 0; attempt < 40; attempt += 1) {
      await new Promise((resolve) => window.setTimeout(resolve, 500));
      if (!valid()) return;
      const state = await request<{ state: string; download_url: string | null }>(`/v1/exports/${job.id}`);
      if (state.state === 'ready' && state.download_url) { const link = element('a'); link.href = state.download_url; link.download = 'pm-chat-export.json'; link.click(); toast('Экспорт готов'); return; }
      if (['failed', 'expired'].includes(state.state)) { toast(`Экспорт: ${state.state}`); return; }
    }
    toast('Экспорт продолжает готовиться — повторите позже');
  });
}

async function conversationSettingsFromUi(): Promise<void> {
  if (!current || activeEditor) return;
  const id = current.id; const valid = selectionGuard();
  return runUiAction(async (request) => {
    const settings = await request<{ notification_mode: string; notification_mode_override?: string | null; muted_until: string | null }>(`/v1/conversations/${id}/settings`);
    if (!valid() || activeEditor) return;
    const dialog = element('dialog', 'message-editor notification-settings'); dialog.setAttribute('aria-label', 'Уведомления чата');
    const close = (): void => { if (activeEditor?.dialog === dialog) activeEditor = null; dialog.remove(); };
    activeEditor = { dialog, valid, close }; dialog.addEventListener('close', close);
    const form = element('form', 'notification-form');
    const modeLabel = element('label', 'notification-field', 'Присылать');
    const mode = element('select'); mode.setAttribute('aria-label', 'Уведомления этого чата');
    const selectedMode = Object.prototype.hasOwnProperty.call(settings, 'notification_mode_override') ? (settings.notification_mode_override ?? '') : settings.notification_mode;
    for (const [value, text] of [['','Как в настройках профиля'],['all','Все сообщения'],['mentions','Упоминания и ответы мне'],['off','Не присылать']]) {
      const option = element('option', '', text); option.value = value!; option.defaultSelected = value === selectedMode; mode.append(option);
    }
    modeLabel.append(mode);
    const pauseLabel = element('label', 'notification-field', 'Пауза');
    const pause = element('select'); pause.setAttribute('aria-label', 'Приостановить уведомления');
    const paused = settings.muted_until && Date.parse(settings.muted_until) > Date.now();
    const options = paused ? [['keep', `До ${new Date(settings.muted_until!).toLocaleString('ru-RU')}`], ['none','Без паузы'], ['8h','На 8 часов']] : [['none','Без паузы'], ['8h','На 8 часов']];
    for (const [value, text] of options) { const option = element('option', '', text); option.value = value!; pause.append(option); }
    pauseLabel.append(pause);
    const error = element('p', 'error'); error.setAttribute('role', 'alert');
    const save = element('button', 'primary', 'Сохранить'); save.type = 'submit';
    const cancel = element('button', 'ghost', 'Отмена'); cancel.type = 'button'; cancel.addEventListener('click', close);
    form.addEventListener('submit', event => {
      event.preventDefault(); if (!valid() || save.disabled) return;
      save.disabled = true; error.textContent = '';
      const muted_until = pause.value === 'keep' ? settings.muted_until : pause.value === '8h' ? new Date(Date.now()+8*3_600_000).toISOString() : null;
      void request(`/v1/conversations/${id}/settings`, { method:'PUT', body:JSON.stringify({notification_mode:mode.value || null, muted_until}) })
        .then(() => { if (valid()) { close(); toast('Настройки чата сохранены'); void loadConversations(valid, true).catch(() => undefined); } })
        .catch(failure => { if (valid()) { save.disabled = false; error.textContent = failure instanceof Error ? failure.message : 'Не удалось сохранить'; } });
    });
    form.append(element('h3', '', 'Уведомления чата'), modeLabel, pauseLabel, error, cancel, save);
    dialog.append(form); document.body.append(dialog); dialog.showModal(); mode.focus();
  });
}

async function loadChatInfoMembers(valid = selectionGuard()): Promise<void> {
  if (!current || current.type !== 'group') { currentMembers = []; return; }
  chatInfoMembersLoading = true;
  try {
    const members = await api<{ members: Member[] }>(`/v1/conversations/${current.id}/members`);
    if (!valid()) return;
    currentMembers = members.members;
  } finally { if (valid()) chatInfoMembersLoading = false; }
}

async function loadScheduledMessages(valid = selectionGuard()): Promise<void> {
  if (!current) { scheduledMessages = null; return; }
  const result = await api<{ scheduled: Scheduled[] }>(`/v1/conversations/${current.id}/scheduled`);
  if (!valid()) return;
  scheduledMessages = result.scheduled;
}

async function loadConversationPins(valid = selectionGuard()): Promise<void> {
  if (!current) { conversationPins = null; return; }
  const result = await api<{ pins: Message[] }>(`/v1/conversations/${current.id}/pins`);
  if (!valid()) return;
  conversationPins = result.pins;
}

async function loadChatInfoPeer(valid = selectionGuard()): Promise<void> {
  if (!current || current.type !== 'direct') { chatInfoPeer = null; return; }
  const peer = await api<PeerProfile>(`/v1/conversations/${encodeURIComponent(current.id)}/peer`).catch(() => null);
  if (valid()) chatInfoPeer = peer;
}

// Clicking someone's avatar opens (or creates) the direct chat with them. Wired by a data attribute
// and ONE delegated listener, because avatar <img>s are reused across renders and must not collect
// per-render click handlers.
function linkAvatarToDirect(avatar: HTMLElement, userId: string, name: string): HTMLElement {
  if (!bootstrap || userId === bootstrap.user.id) return avatar;
  avatar.dataset.openDirect = userId; avatar.setAttribute('role', 'button'); avatar.tabIndex = 0;
  // The tooltip says who this is and nothing else. It used to read "<имя> — написать лично", which
  // in the read-receipt row amounted to inviting everyone who read a message into a private chat --
  // work belongs in the chat where the team can see it. The click still opens the direct chat, and
  // the accessible name says so plainly, without suggesting it.
  // "Открыть чат: <имя>" rather than "Открыть чат с <имя>": a name would have to be in the
  // instrumental case for that to read as Russian, and we cannot decline people's names.
  avatar.setAttribute('aria-label', `Открыть чат: ${name}`); avatar.title = name;
  return avatar;
}
async function openDirectWithUser(userId: string): Promise<void> {
  return runUiAction(async (request, valid) => {
    const created = await request<{ id: string }>(`/v1/direct/${encodeURIComponent(userId)}`, { method: 'POST', body: '{}' });
    await loadConversations(valid); if (!valid()) return;
    await openConversation(conversations.find((item) => item.id === created.id) ?? created.id);
  });
}
function openDirectFromEvent(event: Event): void {
  const avatar = event.target instanceof Element ? event.target.closest<HTMLElement>('[data-open-direct]') : null;
  if (!avatar?.dataset.openDirect) return;
  if (event instanceof KeyboardEvent && event.key !== 'Enter' && event.key !== ' ') return;
  event.preventDefault(); event.stopPropagation();
  void openDirectWithUser(avatar.dataset.openDirect);
}
document.addEventListener('click', openDirectFromEvent);
document.addEventListener('keydown', openDirectFromEvent);
for (const type of ['pointerdown', 'keydown']) document.addEventListener(type, primeIncomingSound, { once: true });

// A link to this chat inside a message (someone pasting a permalink) should land where a permalink
// lands, not reload the whole app in front of the reader. Delegated, like the avatar handler:
// message nodes are reused across renders and must not collect per-render listeners. Anything
// openRoute does not recognise, including every external link, is left to the browser.
document.addEventListener('click', (event) => {
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const link = event.target instanceof Element ? event.target.closest('a') : null;
  if (!link || link.target === '_blank' || !link.closest('.message-body')) return;
  const url = new URL(link.href, location.href);
  if (url.origin !== location.origin) return;
  event.preventDefault();
  void openRoute(url.pathname).then((handled) => { if (!handled) location.assign(url.href); });
});

// Files dragged from the computer onto the history or composer go through the same upload path as the attach button.
installFileDrop(document, {
  accepts: (target) => target instanceof Element && root.contains(target) && Boolean(target.closest('.messages, .composer'))
    && Boolean(current && !current.archived_at && root.querySelector('.conversation .composer')),
  drop: (files) => void uploadFiles(files),
  highlight: (active) => root.classList.toggle('file-drop-active', active),
});
// A file dropped anywhere else in the app -- the sidebar, the header, a hair short of the composer
// -- must not make the browser navigate the tab to that file, which is what it does by default and
// which would take the chat off the screen. Nothing happens instead.
for (const type of ['dragover', 'drop']) {
  document.addEventListener(type, (event) => {
    const transfer = (event as DragEvent).dataTransfer;
    if (event.defaultPrevented || !carriesFiles(transfer) || !(event.target instanceof Element) || !root.contains(event.target)) return;
    event.preventDefault();
    if (transfer) transfer.dropEffect = 'none';
  });
}

async function toggleChatInfo(): Promise<void> {
  chatInfoOpen = !chatInfoOpen;
  if (!chatInfoOpen || !current) { renderShell(); return; }
  // Always refresh on open, not just the first time: an empty members/pins list from a
  // prior open is truthy (not null/undefined), so a "only if unset" check would never
  // refetch and could keep showing a stale list (e.g. a pin added since the panel closed).
  renderShell();
  const valid = selectionGuard();
  const isGroup = current.type === 'group';
  await Promise.all([
    isGroup ? loadChatInfoMembers(valid) : loadChatInfoPeer(valid),
    loadConversationPins(valid),
  ]);
  if (!valid()) return;
  if (valid()) renderShell();
}

// Header one-click shortcut onto the existing add-member control: opens the info panel (if not
// already open, leaving it open if it is) and focuses its select, instead of a duplicate prompt()
// flow. The panel's own render gates the select on canManage, matching this button's own gating.
// "Добавить участников" dialog: every colleague not yet in the group (≤500, fetched once), filtered
// locally by name, with avatar, last-seen and a checkbox; adds all checked in one go.
let activeAddMembersDialog: { valid: () => boolean; close: () => void } | null = null;
type AddCandidate = { id: string; display_name: string; avatar_key: string | null; last_seen_at: string | null; online: boolean; is_bot?: boolean };
// "15:47" today, "вчера в 15:47", otherwise "20.09 в 15:47" (year added when not the current one).
function editedLabel(at: Date): string {
  // Russian-language label → Russian formats regardless of the browser locale (no "04:10 PM").
  const time = at.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  const today = new Date(); const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  if (at.toDateString() === today.toDateString()) return time;
  if (at.toDateString() === yesterday.toDateString()) return `вчера в ${time}`;
  const day = at.toLocaleDateString('ru-RU', at.getFullYear() === today.getFullYear() ? { day: '2-digit', month: '2-digit' } : { day: '2-digit', month: '2-digit', year: 'numeric' });
  return `${day} в ${time}`;
}
function lastSeenLabel(user: AddCandidate): string {
  // A bot has no working day: "был(а) 3 ч назад" about a program reads as a person who went home.
  if (user.is_bot) return 'бот';
  if (user.online) return 'в сети';
  if (!user.last_seen_at) return 'ещё не заходил(а)';
  const seen = new Date(user.last_seen_at); const minutes = Math.floor((Date.now() - seen.getTime()) / 60000);
  if (minutes < 1) return 'был(а) только что';
  if (minutes < 60) return `был(а) ${minutes} мин назад`;
  if (minutes < 24 * 60) return `был(а) ${Math.floor(minutes / 60)} ч назад`;
  const time = seen.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (minutes < 48 * 60) return `был(а) вчера в ${time}`;
  return `был(а) ${seen.toLocaleDateString()}`;
}
async function showAddMembersDialog(): Promise<void> {
  if (activeAddMembersDialog || !current || current.type !== 'group') return;
  const conversationId = current.id; const valid = selectionGuard();
  const dialog = element('dialog', 'message-editor add-members'); dialog.setAttribute('aria-label', 'Добавить участников');
  const close = (): void => { if (activeAddMembersDialog?.close === close) activeAddMembersDialog = null; if (dialog.open) dialog.close(); dialog.remove(); };
  dialog.addEventListener('close', () => { if (activeAddMembersDialog?.close === close) activeAddMembersDialog = null; dialog.remove(); });
  activeAddMembersDialog = { valid, close };
  const head = element('div', 'chat-info-head');
  const closeX = element('button', 'ghost', '✕'); closeX.type = 'button'; closeX.setAttribute('aria-label', 'Закрыть'); closeX.addEventListener('click', close);
  head.append(element('h3', '', 'Добавить участников'), closeX);
  const filter = element('input'); filter.type = 'search'; filter.placeholder = 'Фильтр по имени'; filter.setAttribute('aria-label', 'Фильтр по имени');
  const list = element('div', 'add-members-list'); list.setAttribute('role', 'list'); list.setAttribute('aria-label', 'Коллеги для добавления');
  list.append(element('p', 'meta', 'Загрузка…'));
  const error = element('p', 'error'); error.hidden = true; error.setAttribute('role', 'alert');
  const submit = element('button', 'primary', 'Добавить'); submit.type = 'button'; submit.disabled = true;
  const cancel = element('button', 'ghost', 'Отмена'); cancel.type = 'button'; cancel.addEventListener('click', close);
  const actions = element('div', 'bug-report-actions'); actions.append(submit, cancel);
  dialog.append(head, filter, list, error, actions); document.body.append(dialog); dialog.showModal(); filter.focus();

  const selected = new Set<string>();
  const refreshSubmit = (): void => { submit.disabled = !selected.size; submit.textContent = selected.size ? `Добавить (${selected.size})` : 'Добавить'; };
  // Everyone is listed; people already in the chat show a checked, disabled box.
  let candidates: AddCandidate[] = [];
  const memberIds = new Set<string>();
  const renderList = (): void => {
    const needle = filter.value.trim().toLocaleLowerCase('ru-RU');
    const shown = candidates.filter((user) => !needle || user.display_name.toLocaleLowerCase('ru-RU').includes(needle));
    list.replaceChildren();
    if (!shown.length) list.append(element('p', 'meta', 'Никого не найдено'));
    for (const user of shown) {
      const member = memberIds.has(user.id);
      const row = element('label', `add-members-row${member ? ' member' : ''}`); row.setAttribute('role', 'listitem');
      const box = element('input'); box.type = 'checkbox'; box.checked = member || selected.has(user.id); box.disabled = member;
      box.addEventListener('change', () => { if (box.checked) selected.add(user.id); else selected.delete(user.id); refreshSubmit(); });
      const text = element('span', 'add-members-text');
      text.append(element('span', 'chat-title', user.display_name),
        element('span', `meta${user.online ? ' online' : ''}`, member ? `уже в чате · ${lastSeenLabel(user)}` : lastSeenLabel(user)));
      row.append(box, avatarNode(user.display_name, user.avatar_key ? userAvatarUrl(user.id, user.avatar_key) : null), text);
      list.append(row);
    }
  };
  filter.addEventListener('input', renderList);
  try {
    const [users, members] = await Promise.all([
      api<{ users: AddCandidate[] }>('/v1/users?limit=500'),
      api<{ members: Member[] }>(`/v1/conversations/${encodeURIComponent(conversationId)}/members`),
    ]);
    if (!valid() || activeAddMembersDialog?.close !== close) return;
    for (const member of members.members) memberIds.add(member.id);
    candidates = users.users;
    renderList();
  } catch (failure) {
    if (!valid()) return;
    list.replaceChildren(); error.textContent = failure instanceof Error ? failure.message : 'Не удалось загрузить коллег'; error.hidden = false;
  }
  submit.addEventListener('click', () => void (async () => {
    if (!selected.size || submit.disabled) return;
    submit.disabled = true; error.hidden = true;
    const userIds = [...selected];
    try {
      // One request, one transaction, one system message listing everyone added.
      await api(`/v1/conversations/${encodeURIComponent(conversationId)}/members`, { method: 'POST', body: JSON.stringify({ user_ids: userIds }) });
    } catch (failure) {
      if (!valid()) return;
      error.textContent = failure instanceof Error ? failure.message : 'Не удалось добавить'; error.hidden = false;
      refreshSubmit();
      return;
    }
    if (!valid()) return;
    chatInfoAvailableUsers = null;
    await loadChatInfoMembers(valid).catch(() => undefined); await loadConversations(valid).catch(() => undefined);
    if (!valid()) return;
    renderShell(); close(); toast(userIds.length === 1 ? 'Участник добавлен' : `Добавлено участников: ${userIds.length}`);
  })());
}

async function renameChatFromUi(title: string): Promise<void> {
  return runUiAction(async (request, valid) => {
    if (!current) return;
    await request(`/v1/conversations/${current.id}`, { method: 'PATCH', body: JSON.stringify({ title }) });
    current = { ...current, display_title: title };
    await loadConversations(valid); if (!valid()) return; renderShell(); toast('Название обновлено');
  });
}

async function uploadChatAvatarFromUi(file: File): Promise<void> {
  return runUiAction(async (request, valid) => {
    if (!current) return;
    if (file.size > 5 * 1024 * 1024) throw new Error('Файл слишком большой (максимум 5 МБ)');
    const buffer = await file.arrayBuffer();
    const result = await request<{ avatar_key: string }>(`/v1/conversations/${current.id}/avatar`, {
      method: 'PUT', headers: { 'Content-Type': 'application/octet-stream' }, body: buffer,
    });
    if (!valid()) return;
    if (currentDetails) currentDetails = { ...currentDetails, avatar_key: result.avatar_key };
    await loadConversations(valid); if (!valid()) return; renderShell(); toast('Картинка обновлена');
  });
}

async function deleteChatAvatarFromUi(): Promise<void> {
  return runUiAction(async (request, valid) => {
    if (!current) return;
    await request(`/v1/conversations/${current.id}/avatar`, { method: 'DELETE', body: '{}' });
    if (!valid()) return;
    if (currentDetails) currentDetails = { ...currentDetails, avatar_key: null };
    await loadConversations(valid); if (!valid()) return; renderShell(); toast('Картинка удалена');
  });
}

async function setChatMemberRoleFromUi(userId: string, role: string): Promise<void> {
  return runUiAction(async (request, valid) => {
    if (!current) return;
    await request(`/v1/conversations/${current.id}/members/${userId}/role`, { method: 'PUT', body: JSON.stringify({ role }) });
    await loadChatInfoMembers(valid); if (valid()) renderShell();
  });
}

async function removeChatMemberFromUi(userId: string, name: string): Promise<void> {
  return runUiAction(async (request, valid, _prompt, confirm) => {
    if (!current) return;
    if (!confirm(`Убрать «${name}» из группы?`)) return;
    await request(`/v1/conversations/${current.id}/members/${userId}`, { method: 'DELETE', body: '{}' });
    chatInfoAvailableUsers = null;
    await loadChatInfoMembers(valid); if (valid()) renderShell();
  });
}

async function updateChatDescriptionFromUi(description: string): Promise<void> {
  return runUiAction(async (request, valid) => {
    if (!current) return;
    await request(`/v1/conversations/${current.id}`, { method: 'PATCH', body: JSON.stringify({ description }) });
    if (!valid()) return;
    if (currentDetails) currentDetails = { ...currentDetails, description };
    renderShell(); toast('Описание обновлено');
  });
}

async function transferChatOwnershipFromUi(userId: string, name: string): Promise<void> {
  return runUiAction(async (request, valid, _prompt, confirm) => {
    if (!current) return;
    if (!confirm(`Сделать «${name}» владельцем? Вы станете участником.`)) return;
    await request(`/v1/conversations/${current.id}/owner`, { method: 'PUT', body: JSON.stringify({ user_id: userId }) });
    await loadChatInfoMembers(valid); if (!valid()) return;
    await loadConversations(valid); if (valid()) renderShell();
    toast('Владелец изменён');
  });
}

async function setChatArchivedFromUi(archived: boolean): Promise<void> {
  return runUiAction(async (request, valid) => {
    if (!current) return;
    await request(`/v1/conversations/${current.id}/${archived ? 'archive' : 'unarchive'}`, { method: 'POST', body: '{}' });
    if (!valid()) return;
    current = { ...current, archived_at: archived ? new Date().toISOString() : null };
    if (currentDetails) currentDetails = { ...currentDetails, archived_at: current.archived_at };
    await loadConversations(valid); if (!valid()) return; renderShell();
    toast(archived ? 'Чат архивирован' : 'Чат разархивирован');
  });
}

async function leaveChatFromUi(): Promise<void> {
  return runUiAction(async (request, valid, _prompt, confirm) => {
    if (!current) return;
    if (!confirm('Покинуть эту группу?')) return;
    try {
      await request(`/v1/conversations/${current.id}/leave`, { method: 'POST', body: '{}' });
    } catch (error) {
      if (error instanceof Error && (error as Error & { code?: string }).code === 'OWNER_REQUIRED') {
        toast('Сначала передайте владение другому участнику'); return;
      }
      throw error;
    }
    await conversationPages.select(null); clearConversationSelection();
    const clearedGeneration = selectionGeneration;
    const cleared = () => valid() && selectionGeneration === clearedGeneration && current === null;
    await loadConversations(cleared); if (cleared()) renderShell();
  });
}

// Closing leaves only the list on screen: nothing is selected, so nothing gets read. The server is
// told the chat is no longer on screen, otherwise it would keep suppressing its notifications.
function mobileHistoryOwner(): string {
  return bootstrap ? `${bootstrap.user.workspace_id}:${bootstrap.user.id}:${accountEpoch(bootstrap.user)}` : '';
}

function closeConversationFromUi(): void {
  if (!current) return;
  if (matchMedia('(max-width:760px)').matches && sessionGuard()() && history.state?.pmChatMobile === mobileHistoryOwner()) {
    history.back();
    return;
  }
  rememberConversation();
  catchupController?.abort(); catchupController = null; catchups.clear(); pendingCurrentRevision = null;
  void conversationPages.select(null); clearConversationSelection();
  chatInfoOpen = false;
  renderShell();
}
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape' || event.defaultPrevented || event.isComposing || !current) return;
  if (document.querySelector('dialog[open]')) return;
  event.preventDefault();
  // A reply being written is the nearer thing to cancel. Pulling the whole chat off the screen
  // because somebody changed their mind about what they were answering would be a surprise -- and
  // until now the only way out of a reply was «Очистить» in the + menu, which also drops the files
  // already attached. A second Escape, with nothing left to cancel, closes the chat.
  if (replyToMessageId) { cancelReply(); return; }
  closeConversationFromUi();
});

function roleLabel(role: string): string {
  return role === 'owner' ? 'Владелец' : role === 'admin' ? 'Админ' : 'Участник';
}

// Groups readers by the LAST currently-loaded message their read cursor reaches, not every
// message up to that point -- otherwise the same reader's avatar would repeat under every
// earlier message they've also necessarily read, instead of marking only their current
// position (matching how read receipts read in other chat apps).
function groupReadReceipts(rows: Message[], cursors: ReadCursor[]): Map<string, ReadCursor[]> {
  const groups = new Map<string, ReadCursor[]>();
  for (const reader of cursors) {
    let readSeq: bigint;
    try { readSeq = BigInt(reader.last_read_seq); } catch { continue; }
    for (let i = rows.length - 1; i >= 0; i -= 1) {
      const row = rows[i]!;
      if (row.pending) continue;
      if (BigInt(row.seq) <= readSeq) { const list = groups.get(row.id) ?? []; list.push(reader); groups.set(row.id, list); break; }
    }
  }
  return groups;
}

function pluralizeMembers(count: number): string {
  const mod10 = count % 10; const mod100 = count % 100;
  const word = mod10 === 1 && mod100 !== 11 ? 'участник' : [2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100) ? 'участника' : 'участников';
  return `${count} ${word}`;
}

function renderChatInfoMember(member: Member, canManage: boolean, isOwnerViewer: boolean): HTMLElement {
  const row = element('div', 'chat-info-member'); row.dataset.userId = member.id;
  const top = element('div', 'chat-info-member-top');
  top.append(linkAvatarToDirect(avatarNode(member.display_name, member.avatar_key ? userAvatarUrl(member.id, member.avatar_key) : null), member.id, member.display_name));
  const text = element('span', 'chat-info-member-text');
  const name = element('span', 'chat-info-member-name'); name.append(element('span', '', member.display_name));
  if (member.role !== 'member') name.append(element('span', 'role', roleLabel(member.role)));
  const presence = { id: member.id, display_name: member.display_name, avatar_key: member.avatar_key ?? null, last_seen_at: member.last_seen_at ?? null, online: member.online === true, is_bot: member.is_bot === true };
  text.append(name, element('span', `presence${presence.online ? ' online' : ''}`, lastSeenLabel(presence)));
  top.append(text);
  row.append(top);
  // Management actions live in a compact "⋯" menu on the member's own line.
  if (canManage && member.role !== 'owner' && member.id !== bootstrap?.user.id) {
    const actions = element('div');
    const toggle = menuItem(member.role === 'admin' ? 'Убрать из админов' : 'Сделать админом', 'shield', 'ghost');
    toggle.addEventListener('click', () => void setChatMemberRoleFromUi(member.id, member.role === 'admin' ? 'member' : 'admin'));
    const remove = menuItem('Убрать', 'userMinus', 'ghost');
    remove.addEventListener('click', () => void removeChatMemberFromUi(member.id, member.display_name));
    actions.append(toggle, remove);
    if (isOwnerViewer) {
      const makeOwner = menuItem('Сделать владельцем', 'crown', 'ghost');
      makeOwner.addEventListener('click', () => void transferChatOwnershipFromUi(member.id, member.display_name));
      actions.append(makeOwner);
    }
    top.append(actionMenu(`Действия с участником ${member.display_name}`, `member:${sessionGeneration}:${selectionGeneration}:${member.id}`, actions, 'member-more'));
  }
  return row;
}

function renderChatInfo(): HTMLElement {
  const aside = element('aside', 'chat-info'); aside.setAttribute('aria-label', 'Информация о чате');
  if (!current || !bootstrap) return aside;
  const conversation = current;
  const header = element('div', 'chat-info-head');
  header.append(element('h3', '', 'Информация о чате'));
  const close = element('button', 'ghost', '✕'); close.type = 'button'; close.setAttribute('aria-label', 'Закрыть информацию о чате');
  close.addEventListener('click', () => { chatInfoOpen = false; renderShell(); });
  header.append(close);
  aside.append(header);

  const isGroup = conversation.type === 'group';
  if (!isGroup) {
    // Direct chat: the panel is the other person's card — large avatar, name, status, presence, e-mail.
    const peer = chatInfoPeer;
    const card = element('div', 'peer-card');
    if (!peer) card.append(element('p', 'meta', 'Загрузка…'));
    else {
      const presence: AddCandidate = { id: peer.id, display_name: peer.display_name, avatar_key: peer.avatar_key, last_seen_at: peer.last_seen_at, online: peer.online, is_bot: peer.is_bot };
      card.append(avatarNode(peer.display_name, peer.avatar_key ? userAvatarUrl(peer.id, peer.avatar_key) : null, 'avatar peer-avatar'),
        element('h3', 'peer-name', peer.display_name),
        element('p', `meta peer-presence${peer.online ? ' online' : ''}`, lastSeenLabel(presence)));
      if (peer.status_text) card.append(element('p', 'peer-status', peer.status_text));
      if (peer.email) { const mail = element('a', 'peer-email', peer.email); mail.href = `mailto:${peer.email}`; card.append(mail); }
    }
    aside.append(card);
  }
  const canManage = isGroup && ['owner', 'admin'].includes(currentDetails?.role ?? '');
  const avatarWrap = element('div', 'chat-info-avatar');
  const avatarKey = currentDetails?.avatar_key ?? null;
  avatarWrap.append(avatarNode(conversation.display_title, avatarKey ? chatInfoAvatarUrl(conversation.id, avatarKey) : null));
  if (canManage) {
    const fileInput = element('input'); fileInput.type = 'file'; fileInput.accept = 'image/png,image/jpeg,image/webp,image/gif'; fileInput.hidden = true;
    fileInput.addEventListener('change', () => { const file = fileInput.files?.[0]; if (file) void uploadChatAvatarFromUi(file); fileInput.value = ''; });
    const actions = element('div', 'chat-info-avatar-actions');
    const uploadButton = element('button', 'ghost', avatarKey ? 'Заменить картинку' : 'Загрузить картинку');
    uploadButton.type = 'button'; uploadButton.addEventListener('click', () => fileInput.click());
    actions.append(fileInput, uploadButton);
    if (avatarKey) {
      const removeButton = element('button', 'ghost', 'Удалить картинку'); removeButton.type = 'button';
      removeButton.addEventListener('click', () => void deleteChatAvatarFromUi());
      actions.append(removeButton);
    }
    avatarWrap.append(actions);
  }
  if (isGroup) aside.append(avatarWrap);   // direct chats show the peer card above instead

  const isOwnerViewer = isGroup && currentDetails?.role === 'owner';

  if (isGroup) {
    aside.append(element('h4', '', 'Название'));
    const renameForm = element('form', 'chat-info-rename chat-info-title-form');
    const titleInput = element('input'); titleInput.defaultValue = conversation.display_title; titleInput.setAttribute('aria-label', 'Название чата'); titleInput.maxLength = 200;
    titleInput.disabled = !canManage;
    const saveButton = element('button', '', 'Сохранить'); saveButton.type = 'submit'; saveButton.disabled = !canManage;
    renameForm.append(titleInput, saveButton);
    renameForm.addEventListener('submit', (event) => {
      event.preventDefault();
      const title = titleInput.value.trim();
      if (title && title !== conversation.display_title) void renameChatFromUi(title);
    });
    aside.append(renameForm);

    aside.append(element('h4', '', 'Описание'));
    const descriptionForm = element('form', 'chat-info-rename chat-info-description-form');
    const descriptionInput = element('textarea'); descriptionInput.defaultValue = currentDetails?.description ?? '';
    descriptionInput.setAttribute('aria-label', 'Описание чата'); descriptionInput.maxLength = 2000; descriptionInput.rows = 2;
    descriptionInput.disabled = !canManage;
    const descriptionSave = element('button', '', 'Сохранить'); descriptionSave.type = 'submit'; descriptionSave.disabled = !canManage;
    descriptionForm.append(descriptionInput, descriptionSave);
    descriptionForm.addEventListener('submit', (event) => {
      event.preventDefault();
      const description = descriptionInput.value.trim();
      if (description !== (currentDetails?.description ?? '')) void updateChatDescriptionFromUi(description);
    });
    aside.append(descriptionForm);
  }

  aside.append(element('h4', '', 'Ссылка на чат'));
  const linkRow = element('div', 'chat-info-link');
  const linkInput = element('input'); linkInput.readOnly = true; linkInput.defaultValue = `${location.origin}/c/${conversation.id}`; linkInput.setAttribute('aria-label', 'Ссылка на чат');
  const copyButton = element('button', 'ghost', 'Копировать'); copyButton.type = 'button'; copyButton.addEventListener('click', () => void copyLink(linkInput.value));
  linkRow.append(linkInput, copyButton);
  aside.append(linkRow);

  if (isGroup) {
    const membersHead = element('div', 'chat-info-members-head');
    membersHead.append(element('h4', '', currentMembers.length ? `Участники · ${currentMembers.length}` : 'Участники'));
    if (canManage) {
      const add = element('button', 'icon-button chat-info-add'); add.type = 'button';
      add.setAttribute('aria-label', 'Добавить в группу'); add.title = 'Добавить в группу';
      add.innerHTML = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M19 8v6M22 11h-6"/></svg>';
      add.addEventListener('click', () => void showAddMembersDialog());
      membersHead.append(add);
    }
    aside.append(membersHead);
    const admins = currentMembers.filter((member) => member.role !== 'member');
    const tools = element('div', 'chat-info-members-tools');
    const tabs = element('div', 'chat-info-member-tabs'); tabs.setAttribute('role', 'group'); tabs.setAttribute('aria-label', 'Показать участников');
    for (const [value, label] of [['all', `Все ${currentMembers.length}`], ['admins', `Админы и владелец ${admins.length}`]] as const) {
      const tab = element('button', `chip${chatInfoMemberFilter === value ? ' active' : ''}`, label); tab.type = 'button';
      tab.setAttribute('aria-pressed', String(chatInfoMemberFilter === value));
      tab.addEventListener('click', () => { chatInfoMemberFilter = value; renderShell(); });
      tabs.append(tab);
    }
    tools.append(tabs);
    if (currentMembers.length > 6) {
      const filter = element('input', 'chat-info-member-filter'); filter.type = 'search'; filter.placeholder = 'Найти участника';
      filter.setAttribute('aria-label', 'Фильтр участников'); filter.defaultValue = chatInfoMemberQuery;
      filter.addEventListener('input', () => { chatInfoMemberQuery = filter.value; renderShell(); });
      tools.append(filter);
    }
    aside.append(tools);
    const list = element('div', 'chat-info-members');
    const query = chatInfoMemberQuery.trim().toLocaleLowerCase('ru-RU');
    // Online first, then most recently seen, then by name: the order explains itself via the
    // presence line under each name. Role is a filter and a badge, not a sort key.
    const seenAt = (member: Member): number => member.online ? Infinity : member.last_seen_at ? Date.parse(member.last_seen_at) : 0;
    const shown = (chatInfoMemberFilter === 'admins' ? admins : currentMembers)
      .filter((member) => !query || member.display_name.toLocaleLowerCase('ru-RU').includes(query))
      .sort((a, b) => seenAt(b) - seenAt(a) || a.display_name.localeCompare(b.display_name, 'ru-RU'));
    if (chatInfoMembersLoading && !currentMembers.length) list.append(element('div', 'empty', 'Загрузка участников…'));
    else if (!shown.length) list.append(element('div', 'empty', 'Никого не найдено'));
    else for (const member of shown) list.append(renderChatInfoMember(member, canManage, isOwnerViewer));
    aside.append(list);

    if (isOwnerViewer) {
      const archiveButton = element('button', 'ghost', conversation.archived_at ? 'Разархивировать чат' : 'Архивировать чат');
      archiveButton.type = 'button'; archiveButton.addEventListener('click', () => void setChatArchivedFromUi(!conversation.archived_at));
      aside.append(archiveButton);
    }
    if (currentDetails?.role !== 'owner') {
      const leaveButton = element('button', 'ghost', 'Покинуть группу'); leaveButton.type = 'button';
      leaveButton.addEventListener('click', () => void leaveChatFromUi());
      aside.append(leaveButton);
    }
  }

  const tasks = menuItem('Задачи чата', 'check', 'ghost');
  const ownsTasks = selectionGuard(); tasks.addEventListener('click', () => { if (ownsTasks()) showTasks(false); });
  aside.append(tasks);
  aside.append(element('h4', '', 'Закреплённые сообщения'));
  const pinsList = element('div', 'chat-info-pins');
  if (!conversationPins) pinsList.append(element('div', 'empty', 'Загрузка…'));
  else if (!conversationPins.length) pinsList.append(element('div', 'empty', 'Нет закреплённых сообщений'));
  else for (const pinned of conversationPins) {
    const item = element('button', 'ghost chat-info-pin', pinned.body.slice(0, 100) || 'Вложение');
    item.type = 'button'; item.dataset.messageId = pinned.id; item.addEventListener('click', () => void openConversation(current ?? conversation, pinned.id));
    pinsList.append(item);
  }
  aside.append(pinsList);

  return aside;
}

async function loadConversations(valid = sessionGuard(), background = false, onValidated?: (authorized: boolean) => void): Promise<void> {
  if (!sidebarOwner) return;
  if (navigationState?.generation === selectionGeneration && !navigationState.error) { refreshAfterNavigation = true; return; }
  await runSidebarOperation(() => conversationPages.refresh(authorized => {
    if (valid() && authorized) applyConversationSnapshot();
    onValidated?.(valid() && authorized);
  }), { revalidatesCurrent: Boolean(current), valid, background });
}

// No cross-epoch cache. Shared physical admission survives every selection/session.
const replyWork = new ReplyWork<{ target: Message }>();
async function resolveReplySources(rows: Message[], conversationId: string, valid: () => boolean): Promise<void> {
  if (!valid()) return;
  const sources = new Map<string, Message['replySource']>();
  const summarize = (source: Message): Message['replySource'] => source.deleted_at
    ? { state: 'deleted' } : { state: 'available', text: source.body.slice(0, 180) || 'Вложение', author: source.sender?.name ?? 'Система',
      images: source.attachments?.filter(attachment => isAttachmentImage(attachment.mime)) };
  for (const row of rows) if (!row.pending && row.conversation_id === conversationId) sources.set(row.id, summarize(row));
  // Bundled summaries belong to the authorized history snapshot. Missing field
  // keeps bounded legacy context lookups for a rolling server rollback.
  const legacy = rows.filter(row => !row.deleted_at && row.reply_source === undefined);
  const missing = [...new Set(legacy.map(row => row.reply_to_message_id).filter((id): id is string => Boolean(id) && !sources.has(id!)))];
  const results = await replyWork.run(missing, (id, signal) => {
    signal.throwIfAborted();
    if (!valid()) throw new StaleUiAction();
    return api<{ target: Message }>(`/v1/conversations/${conversationId}/messages/context?message_id=${encodeURIComponent(id)}&before=0&after=0`, { signal });
  });
  if (!valid() || !results) return;
  for (const [id, result] of results) {
    if ('error' in result) sources.set(id, { state: (result.error as { status?: number })?.status === 404 ? 'unavailable' : 'error' });
    else sources.set(id, result.value.target.id === id && result.value.target.conversation_id === conversationId
      ? summarize(result.value.target) : { state: 'unavailable' });
  }
  for (const row of rows) if (row.reply_to_message_id) row.replySource = row.reply_source ?? sources.get(row.reply_to_message_id);
}

async function loadReadCursors(conversationId: string, active: () => boolean): Promise<void> {
  let next: ReadCursor[] = [];
  // A malformed body (no readers array) must degrade to "no receipts", never break rendering.
  try { const readers = (await api<{ readers?: unknown }>(`/v1/conversations/${conversationId}/read-cursors`)).readers; next = Array.isArray(readers) ? readers as ReadCursor[] : []; }
  catch { /* Non-critical overlay: leave the previous cursors rather than blocking history. */ return; }
  if (!active() || JSON.stringify(next) === JSON.stringify(readCursors)) return;
  readCursors = next;
  // Fire-and-forget from the caller's perspective: this resolves after the render that already
  // followed loadMessages(), so it needs its own render to actually show the fresh avatars.
  renderShell();
}

async function loadMessages(conversationId: string, targetId?: string, afterSeq?: string, openingUnread = false): Promise<void> {
  const valid = sessionGuard(); const selection = selectionGeneration; const request = ++historyGeneration; replyWork.cancel();
  historyReplacementPending = request;
  try {
    // ConversationPages independently authorized this exact detail before history.
    if (currentDetails?.id !== conversationId) throw new Error('Доступ к чату не подтверждён');
    const access = { id: conversationId, membership_epoch_id: currentDetails.membership_epoch_id, visible_from_seq: currentDetails.visible_from_seq };
    // Recent unread messages belong in a full tail window, not a tiny after-cursor page
    // that expands on the next refresh. Far unread is fetched before publishing any rows.
    let useTail = !targetId && (afterSeq === undefined || openingUnread);
    let result = await api<{ messages: Message[]; snapshot_revision: string; has_more_before?: boolean }>(targetId
      ? `/v1/conversations/${conversationId}/messages/context?message_id=${encodeURIComponent(targetId)}&before=30&after=30`
      : `/v1/conversations/${conversationId}/messages?limit=100${!useTail && afterSeq !== undefined ? `&after=${encodeURIComponent(afterSeq)}` : ''}`);
    const active = (): boolean => valid() && current?.id === conversationId && selection === selectionGeneration && request === historyGeneration;
    if (!active()) return;
    if (openingUnread && !targetId && afterSeq !== undefined && result.messages.length
        && BigInt(result.messages[0]!.seq) > BigInt(afterSeq) + 1n) {
      useTail = false;
      result = await api<{ messages: Message[]; snapshot_revision: string; has_more_before?: boolean }>(
        `/v1/conversations/${conversationId}/messages?limit=100&after=${encodeURIComponent(afterSeq)}`);
      if (!active()) return;
    }
    await resolveReplySources(result.messages, conversationId, active);
    if (!active()) return;
    // Authoritative replacement, never cross-snapshot retention. An older page
    // captured against the previous array must be retried from this window.
    messages = result.messages; historyAccess = access; historyHasEarlier = hasOlderHistory(result);
    historyWindowAtTail = useTail
      || Boolean(messages.length && BigInt(messages.at(-1)!.seq) >= BigInt(currentDetails?.last_message_seq ?? '0'));
    revisionCursors.clear();
    revisionCursors.set(conversationId, BigInt(result.snapshot_revision));
  } finally {
    if (historyReplacementPending === request) historyReplacementPending = null;
  }
}
// Deliberately NOT called from inside loadMessages() itself: that function's exact source is
// extracted verbatim into tests/history-ownership.test.ts's isolated VM harness via string
// markers, which hand-resolves every api() call by array position -- an extra request from in
// there has no matching resolver and desyncs every requests[N] assertion in that suite. Callers
// of loadMessages() (openConversation(), catchUp()) call this separately right after instead.
function refreshReadCursors(conversationId: string, active: () => boolean): void {
  void loadReadCursors(conversationId, active);
}

async function openConversation(destination: Conversation | CurrentConversation | string, targetId?: string): Promise<void> {
  const id = typeof destination === 'string' ? destination : destination.id;
  const row = conversations.find((item) => item.id === id);
  const presentation = typeof destination === 'string' ? row : destination;
  const leaving = current?.id ?? null;
  if (matchMedia('(max-width:760px)').matches && sessionGuard()()) {
    const addressed = parseChatRoute(location.pathname, bootstrap!.user.workspace_id);
    const path = targetId ? addressed?.conversationId === id && addressed.targetId === targetId
      ? location.pathname + location.search + location.hash
      : `/w/${encodeURIComponent(bootstrap!.user.workspace_id)}/c/${encodeURIComponent(id)}/m/${encodeURIComponent(targetId)}` : chatPath(id);
    updateMobileChatHistory(history, path, mobileHistoryOwner());
  }
  // Re-selecting a mounted chat keeps its view. Fresh detail still authorizes the
  // background check; a changed visibility boundary takes the ordinary open path.
  if (!targetId && !contextTargetId && leaving === id && sessionGuard()() && !navigationState && !sidebarCurrentValidation
    && !snapshotLoading.has(id) && historyReplacementPending === null && newerHistoryPending === null) {
    const access = conversationPages.snapshot();
    const detailWork = conversationPages.workload().detail;
    const historyNode = root.querySelector<HTMLElement>('#messages');
    if (!detailWork.inflight && !detailWork.queued && access.activeId === id && access.current?.id === id
      && currentDetails?.id === id && currentDetails.workspace_id === bootstrap?.user.workspace_id
      && currentDetails.membership_epoch_id === access.current.membership_epoch_id
      && currentDetails.visible_from_seq === access.current.visible_from_seq
      && historyAccess?.id === id && historyAccess.membership_epoch_id === currentDetails.membership_epoch_id
      && historyAccess.visible_from_seq === currentDetails.visible_from_seq && historyNode?.dataset.conversationId === id) {
      const valid = selectionGuard();
      const route = chatHistoryUpdate(location.pathname, id);
      if (route) history.pushState({}, '', route);
      appliedPath = chatPath(id);
      if (mobileListVisible) { mobileListVisible = false; renderShell(); subscribeCurrent(); }
      try {
        const authorized = await loadingStatus.run('Проверяем доступ к чату…', valid, () => conversationPages.select(id));
        if (!valid() || authorized === 'stale') return;
        applyConversationSnapshot(true);
        if (authorized !== 'authorized' || !currentDetails) { renderShell(); return; }
        if (historyAccess?.membership_epoch_id !== currentDetails.membership_epoch_id
          || historyAccess.visible_from_seq !== currentDetails.visible_from_seq) {
          await openConversation(destination);
          return;
        }
        renderShell();
        await catchUp(id);
      } catch (error) {
        if (valid()) toast(errorMessage(error));
      }
      return;
    }
  }
  // In-place links to an already mounted message keep the authorized selection, draft,
  // revision cursor and history DOM. A pending/failed access check or history replacement
  // must still take the ordinary guarded loading path.
  if (targetId && leaving === id && sessionGuard()() && !navigationState && !sidebarCurrentValidation
    && !snapshotLoading.has(id) && historyReplacementPending === null && newerHistoryPending === null) {
    const access = conversationPages.snapshot();
    const detailWork = conversationPages.workload().detail;
    const target = messages.find(message => message.id === targetId && message.conversation_id === id && !message.pending);
    const node = root.querySelector<HTMLElement>(`#messages .message[data-message-id="${CSS.escape(targetId)}"]`);
    if (!detailWork.inflight && !detailWork.queued && access.activeId === id && access.current?.id === id
      && currentDetails?.id === id && currentDetails.workspace_id === bootstrap?.user.workspace_id
      && currentDetails.membership_epoch_id === access.current.membership_epoch_id
      && currentDetails.visible_from_seq === access.current.visible_from_seq
      && historyAccess?.id === id && historyAccess.membership_epoch_id === currentDetails.membership_epoch_id
      && historyAccess.visible_from_seq === currentDetails.visible_from_seq
      && target && /^\d+$/.test(target.seq) && /^\d+$/.test(currentDetails.visible_from_seq)
      && BigInt(target.seq) >= BigInt(currentDetails.visible_from_seq) && node?.dataset.selection === String(selectionGeneration)) {
      contextTargetId = targetId; revealedTargetKey = null; initialUnreadPosition = null; requestedHistoryAnchor = null;
      mobileListVisible = false; appliedPath = location.pathname;
      renderShell(); subscribeCurrent(); return;
    }
  }
  if (messageSearchScope && messageSearchScope.id !== id) { messageSearchScope = null; queueConversationQuery('', 0); }
  else if (messageSearchController) queueConversationQuery(sidebarSearchValue);
  rememberConversation();   // keep the conversation we are leaving, for an instant switch back
  pmTaskPreviews.detach(); filesCards.detach();
  const lastRead = conversations.find((item) => item.id === id)?.last_read_seq;
  const valid = sessionGuard(); const generation = ++selectionGeneration; replyWork.cancel();clearManagedAttachmentLoads();
  replyToMessageId = null; composerReplyTarget = null; composerMentionIds = []; composerMentionTokens.clear(); mentionPicker = null; composerAttachmentIds = []; composerStickerAttachmentIds.clear(); composerAttachments = [];
  current = {
    id,
    type: presentation?.type ?? 'direct',
    display_title: presentation?.display_title || 'Диалог',
    archived_at: presentation?.archived_at ?? null,
  };
  initialUnreadPosition = null; historyWindowAfter = null; historyWindowAtTail = false; historyHasEarlier = false; newerHistoryPending = null; historyReturnAnchor = null; requestedHistoryAnchor = null;
  currentDetails = null; historyAccess = null; contextTargetId = targetId ?? null; messages = []; readCursors = []; mobileListVisible = false;
  chatInfoOpen = false; currentMembers = []; chatInfoMembersLoading = false; chatInfoAvailableUsers = null; conversationPins = null; chatInfoPeer = null; chatInfoAddUserId = ''; chatInfoMemberFilter = 'all'; chatInfoMemberQuery = ''; scheduledMessages = null;
  catchupController?.abort(); catchupController = null; catchups.clear(); pendingCurrentRevision = null;
  revisionCursors.clear(); bufferedEvents.clear(); snapshotLoading.clear();
  navigationState = { generation };
  unreadBoundary = lastRead && /^\d+$/.test(lastRead) ? { generation, seq: BigInt(lastRead), tail: null } : null;
  sidebarCurrentValidation = null;
  sidebarValidationFocus = null;
  snapshotLoading.set(id, generation);
  // A fresh cached copy of this conversation renders straight away; the requests below still run
  // and replace it (including the access check, which is what actually authorizes the view).
  // Only a switch to a different conversation is served from memory. Reopening the current
  // chat reaches this path when navigation or access needs a fresh load. A cached permalink is
  // withheld until fresh authorization below, then reused only within the same visibility epoch.
  let warm = targetId || leaving === id || (row?.unread ?? 0) > 0 ? null : cachedConversation(id);
  if (warm) {
    historyAccess = { id, membership_epoch_id: warm.details.membership_epoch_id, visible_from_seq: warm.details.visible_from_seq };
    currentDetails = warm.details; current = currentFromDetail(warm.details, row); messages = warm.messages; historyWindowAtTail = warm.atTail; historyHasEarlier = warm.hasEarlier;
    readCursors = warm.readCursors; revisionCursors.set(id, BigInt(warm.cursor));
    historyScrollAnchor = { conversationId: id, atBottom: warm.atBottom, scrollTop: warm.scrollTop ?? 0 };
    navigationState = null;
  }
  // Publish destination + noninteractive loading UI atomically before any await.
  const route = targetId ? null : chatHistoryUpdate(location.pathname, id);
  if (route) history.pushState({}, '', route);
  appliedPath = targetId ? location.pathname : chatPath(id);
  const progress = loadingStatus.begin('Открываем чат…', () => valid() && generation === selectionGeneration && current?.id === id);
  try {
    const authorization = conversationPages.select(id);
    renderShell(); subscribeCurrent();
    try {
      let authorized = await authorization;
      if (!valid() || generation !== selectionGeneration || current?.id !== id) return;
      // Search/sidebar revalidation can supersede this detail request while retaining an old
      // snapshot. Recheck once; a stale result must never authorize cached or fetched rows.
      if (authorized === 'stale') {
        authorized = await conversationPages.select(id);
        if (!valid() || generation !== selectionGeneration || current?.id !== id) return;
      }
      if (authorized !== 'authorized') throw new Error('Не удалось подтвердить доступ к чату. Повторите попытку.');
      applyConversationSnapshot(true);
      if (!current || !conversationPages.snapshot().current) { forgetConversation(id); renderShell(); return; }
      const detailWork = conversationPages.workload().detail;
      if (targetId && leaving !== id && currentDetails && !detailWork.inflight && !detailWork.queued) {
        const candidate = cachedConversation(id);
        const target = candidate?.messages.find(message => message.id === targetId && message.conversation_id === id && !message.pending);
        if (candidate && target && candidate.details.id === id && currentDetails.workspace_id === bootstrap?.user.workspace_id
          && candidate.details.workspace_id === currentDetails.workspace_id
          && candidate.details.membership_epoch_id === currentDetails.membership_epoch_id
          && candidate.details.visible_from_seq === currentDetails.visible_from_seq
          && /^\d+$/.test(target.seq) && /^\d+$/.test(currentDetails.visible_from_seq)
          && BigInt(target.seq) >= BigInt(currentDetails.visible_from_seq)
          && /^\d+$/.test(candidate.cursor) && /^\d+$/.test(currentDetails.last_revision)
          && BigInt(candidate.cursor) <= BigInt(currentDetails.last_revision)) {
          warm = candidate; messages = warm.messages; readCursors = warm.readCursors;
          historyAccess = { id, membership_epoch_id: warm.details.membership_epoch_id, visible_from_seq: warm.details.visible_from_seq };
          historyWindowAtTail = warm.atTail; historyHasEarlier = warm.hasEarlier;
          revisionCursors.set(id, BigInt(warm.cursor));
          navigationState = null; renderShell();
        }
      }
      // Jumping to a pin re-opens the same conversation; only an actual switch restarts the cycle.
      if (leaving !== id) pinnedBarPage = 0;
      void loadConversationPins(valid).catch(() => undefined);
      void loadScheduledMessages(valid).catch(() => undefined);
      if (warm) {
        // The cached history is already on screen: ask only what changed since its revision instead
        // of refetching the whole window (catchUp() reloads it by itself when something did).
        refreshReadCursors(id, () => valid() && generation === selectionGeneration && current?.id === id);
        progress.update('Проверяем новые сообщения…');
        await catchUp(id);
      } else {
        const cursor = currentDetails?.last_read_seq ?? lastRead;
        const firstUnread = currentDetails?.first_unread_seq;
        // Current servers locate the actual unread message beyond own/system/deleted
        // prefixes. The fallback supports an older server during rolling deployment.
        const after = firstUnread ? String(BigInt(firstUnread)-1n)
          : firstUnread === undefined && (row?.unread ?? 0) > 0 ? cursor : undefined;
        if (!targetId && after && /^\d+$/.test(after)) {
          historyWindowAfter = after;
          unreadBoundary = { generation, seq:BigInt(cursor ?? after), tail:null };
          initialUnreadPosition = generation;
        }
        progress.update('Загружаем сообщения…');
        await loadMessages(id, targetId, historyWindowAfter ?? undefined, true);
        refreshReadCursors(id, () => valid() && generation === selectionGeneration && current?.id === id);
      }
    } catch (error) {
      if (valid() && generation === selectionGeneration && current?.id === id) {
        forgetConversation(id);
        messages = []; readCursors = [];
        navigationState = { generation, error: error instanceof Error ? error.message : 'Ошибка загрузки чата' };
        renderShell(); toast(navigationState.error!);
      }
      return;
    } finally {
      if (snapshotLoading.get(id) === generation) snapshotLoading.delete(id);
    }
    if (!valid() || generation !== selectionGeneration || current?.id !== id) return;
    // Everything already in the chat when this visit started can carry the "new messages" line;
    // whatever arrives later is being read live and gets no line above it.
    if (unreadBoundary?.generation === generation && unreadBoundary.tail === null) {
      const tail = messages.at(-1)?.seq;
      unreadBoundary.tail = tail && /^\d+$/.test(tail) ? BigInt(tail) : 0n;
    }
    const queued = bufferedEvents.get(id)?.[0]; bufferedEvents.clear();
    const queuedRevision = realtimeRevision(queued?.revision);
    if (queuedRevision !== null && queuedRevision > (revisionCursors.get(id) ?? 0n)) await catchUp(id, queuedRevision);
    if (!valid() || generation !== selectionGeneration || current?.id !== id) return;
    await restoreDraftReply(id, () => valid() && generation === selectionGeneration && current?.id === id);
    if (!valid() || generation !== selectionGeneration || current?.id !== id) return;
    navigationState = null;
    renderShell(); subscribeCurrent();
    if (!activeEditor && !contextTargetId && matchMedia('(pointer: fine)').matches && document.activeElement === document.body) root.querySelector<MessageInput>('.composer .message-input')?.focus({preventScroll:true});
    if (refreshAfterNavigation) { refreshAfterNavigation = false; void scheduleLiveConversationRefresh(); }
  } finally { progress.finish(); }
}

// Resolves when the initial colleague list settles, so callers can observe completion.
function openNewChat(): Promise<void> {
  if (sidebarSearchTimer !== null) window.clearTimeout(sidebarSearchTimer);
  sidebarSearchTimer = null; cancelMessageSearch();
  newChat = { group: false, query: '', users: null, error: null, selected: [], title: '', busy: false };
  renderShell();
  root.querySelector<HTMLInputElement>('[aria-label="Поиск коллег"]')?.focus();
  return loadColleagues(newChat);
}

function closeNewChat(): void {
  if (newChatTimer !== null) window.clearTimeout(newChatTimer);
  newChatTimer = null; newChat = null; queueConversationQuery(sidebarSearchValue, 0);
}

function queueColleagueQuery(value: string, delay = 250): void {
  const state = newChat; if (!state) return;
  state.query = value.slice(0, 100);
  if (newChatTimer !== null) window.clearTimeout(newChatTimer);
  newChatTimer = window.setTimeout(() => { newChatTimer = null; void loadColleagues(state); }, delay);
}

async function loadColleagues(state: NewChatState): Promise<void> {
  const valid = sessionGuard(); const query = state.query.trim();
  try {
    const users = (await api<{ users: Colleague[] }>(`/v1/users?q=${encodeURIComponent(query)}`)).users;
    if (!valid() || newChat !== state || state.query.trim() !== query) return;
    state.users = users.filter((user) => user.id !== bootstrap?.user.id); state.error = null;
  } catch (error) {
    if (!valid() || newChat !== state) return;
    state.error = error instanceof Error ? error.message : 'Не удалось загрузить коллег';
  }
  renderShell();
}

async function startNewConversation(state: NewChatState, path: string, body: unknown): Promise<void> {
  if (state.busy) return;
  state.busy = true; renderShell();
  await runUiAction(async (request, valid) => {
    const created = await request<{ id: string }>(path, { method: 'POST', body: JSON.stringify(body) });
    await loadConversations(valid); if (!valid()) return;
    if (newChat === state) newChat = null;
    await openConversation(conversations.find((item) => item.id === created.id) ?? created.id);
  });
  if (newChat === state) { state.busy = false; renderShell(); }
}

function renderNewChat(state: NewChatState): HTMLElement {
  const pane = element('section', 'new-chat'); pane.setAttribute('aria-label', state.group ? 'Новая группа' : 'Новый чат');
  const head = element('div', 'new-chat-head');
  const back = element('button', 'ghost', state.group ? '‹ Назад' : '✕'); back.type = 'button';
  back.setAttribute('aria-label', state.group ? 'Назад к выбору собеседника' : 'Закрыть новый чат');
  back.addEventListener('click', () => { if (state.group) { state.group = false; renderShell(); } else closeNewChat(); });
  head.append(element('h3', '', state.group ? 'Новая группа' : 'Новый чат'), back);
  pane.append(head);
  let submit: HTMLButtonElement | null = null;
  const refreshSubmit = (): void => {
    if (!submit) return;
    submit.disabled = state.busy || !state.title.trim() || !state.selected.length;
    submit.textContent = state.selected.length ? `Создать группу (${state.selected.length + 1})` : 'Создать группу';
  };
  if (state.group) {
    const title = element('input'); title.setAttribute('aria-label', 'Название группы'); title.placeholder = 'Название группы'; title.maxLength = 200; title.defaultValue = state.title;
    title.addEventListener('input', () => { state.title = title.value; refreshSubmit(); });
    pane.append(title, element('p', 'meta', 'Отметьте участников'));
  } else {
    const group = element('button', 'new-chat-group', 'Создать группу'); group.type = 'button';
    group.addEventListener('click', () => { state.group = true; renderShell(); root.querySelector<HTMLInputElement>('[aria-label="Название группы"]')?.focus(); });
    pane.append(group);
  }
  const people = element('div', 'colleagues'); people.setAttribute('role', 'list'); people.setAttribute('aria-label', 'Коллеги');
  if (state.error) { const error = element('p', 'sidebar-error', state.error); error.setAttribute('role', 'alert'); people.append(error); }
  else if (!state.users) people.append(element('p', 'meta', 'Загрузка коллег…'));
  else if (!state.users.length) people.append(element('p', 'meta', 'Никого не найдено'));
  for (const user of state.users ?? []) {
    const avatar = avatarNode(user.display_name, user.avatar_key ? userAvatarUrl(user.id, user.avatar_key) : null);
    if (state.group) {
      const row = element('label', 'colleague'); row.setAttribute('role', 'listitem'); row.dataset.userId = user.id;
      const box = element('input'); box.type = 'checkbox'; box.defaultChecked = state.selected.includes(user.id);
      box.addEventListener('change', () => {
        state.selected = box.checked ? [...state.selected, user.id] : state.selected.filter((id) => id !== user.id);
        refreshSubmit();
      });
      row.append(box, avatar, element('span', 'chat-title', user.display_name));
      people.append(row);
    } else {
      const row = element('button', 'colleague'); row.type = 'button'; row.setAttribute('role', 'listitem'); row.dataset.userId = user.id; row.disabled = state.busy;
      row.append(avatar, element('span', 'chat-title', user.display_name));
      row.addEventListener('click', () => void startNewConversation(state, `/v1/direct/${user.id}`, {}));
      people.append(row);
    }
  }
  if (state.users?.length === 50) people.append(element('p', 'meta', 'Показаны первые 50 — уточните поиск'));
  pane.append(people);
  if (state.group) {
    submit = element('button', 'primary new-chat-submit'); submit.type = 'button';
    submit.addEventListener('click', () => void startNewConversation(state, '/v1/conversations', { title: state.title.trim(), member_ids: state.selected, history_policy: 'last_100' }));
    refreshSubmit(); pane.append(submit);
  }
  return pane;
}

// Coming back to a tab that was in the background: if the connection did not survive, open a new
// one now rather than waiting out the backoff. The person is looking at the chat again, and that
// wait is exactly the "it is stuck" they would otherwise see.
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState !== 'visible' || !bootstrap || !reconnectEnabled || reconnectTimer === null) return;
  window.clearTimeout(reconnectTimer); reconnectTimer = null;
  reconnectDelay = 1500;
  void connectRealtime();
});

async function connectRealtime(): Promise<void> {
  const sessionValid = sessionGuard(); if (!bootstrap || !reconnectEnabled) return;
  const generation = ++realtimeGeneration;
  const valid = (): boolean => sessionValid() && reconnectEnabled && generation === realtimeGeneration;
  if (reconnectTimer !== null) window.clearTimeout(reconnectTimer); reconnectTimer = null;
  const retry = (): void => {
    if (!valid() || reconnectTimer !== null) return;
    reconnectTimer = window.setTimeout(() => {
      reconnectTimer = null;
      if (valid()) void connectRealtime();
    }, reconnectDelay);
    reconnectDelay = Math.min(reconnectDelay * 2, 30_000);
  };
  socket?.close(); socket = null;
  if (heartbeatTimer !== null) window.clearInterval(heartbeatTimer);
  try {
    const ticket = await api<{ ticket: string }>('/v1/realtime/ticket', { method: 'POST', body: JSON.stringify({ device_id: deviceId() }) });
    if (!valid()) return;
    const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
    socket = new WebSocket(`${protocol}//${location.host}/v1/realtime?ticket=${encodeURIComponent(ticket.ticket)}`);
    socket.addEventListener('open', () => {
      if (!valid()) return;
      // The delay is not reset by the handshake alone, only by a connection that proves it can
      // live. A server that refuses this socket on purpose -- too many tabs open, a ticket already
      // spent -- closes it right after opening, and resetting here turned that refusal into two
      // requests a second, indefinitely.
      if (realtimeSettleTimer !== null) window.clearTimeout(realtimeSettleTimer);
      realtimeSettleTimer = window.setTimeout(() => { realtimeSettleTimer = null; if (valid()) reconnectDelay = 1500; }, 5_000);
      connectionLabel = 'В сети'; subscribeCurrent(); socket?.send(JSON.stringify({ type: 'ping' }));
      heartbeatTimer = window.setInterval(() => {
        if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify({ type: 'ping' }));
      }, 15_000);
      if (current) void catchUp(current.id);
      renderShell();
    });
    socket.addEventListener('close', (event) => {
      if (!valid()) return;
      if (heartbeatTimer !== null) window.clearInterval(heartbeatTimer); heartbeatTimer = null;
      if (realtimeSettleTimer !== null) { window.clearTimeout(realtimeSettleTimer); realtimeSettleTimer = null; }
      // 1008 is the server refusing this connection deliberately, not a network blip. Retrying at
      // the pace of a blip only adds load, so it goes straight to the longest delay.
      if (event.code === 1008) reconnectDelay = 30_000;
      socket = null;
      connectionLabel = 'Переподключение…'; renderShell(); retry();
    });
    socket.addEventListener('message', (event) => {
      if (!valid()) return;
      let message: RealtimeEvent;
      try { message = JSON.parse(String(event.data)) as RealtimeEvent; } catch { return; }
      if (message.type === 'ready' || message.type === 'pong') return;
      if (message.type === 'typing') {
        if (message.conversation_id && message.user_id && message.user_id !== bootstrap?.user.id) setTyping(message.conversation_id, message.user_id, message.active === true);
        return;
      }
      if (!message.conversation_id || !message.revision) return;
      // A scheduled message of ours just went out (or someone else wrote while ours waited): the
      // strip above the composer must stop showing what is no longer waiting.
      if (message.type === 'message.created' && message.conversation_id === current?.id && scheduledMessages?.length) {
        void loadScheduledMessages().then(() => renderShell()).catch(() => undefined);
      }
      // Someone pinned or unpinned: the bar under the header follows without a full reload.
      if (message.type === 'pin.updated' && message.conversation_id && message.conversation_id === current?.id) {
        void loadConversationPins().then(() => renderShell()).catch(() => undefined);
      }
      // Removed from a conversation: its cached copy must not be renderable any more.
      if (message.type === 'conversation.membership_changed' && message.conversation_id
          && message.payload?.state === 'removed' && message.payload.target_user_id === bootstrap?.user.id) {
        filesCards.forgetConversation(message.conversation_id);pmTaskPreviews.forgetConversation(message.conversation_id);
        forgetConversation(message.conversation_id);
      }
      if (message.type === 'message.created' && message.payload?.sender_user_id && message.payload.sender_user_id !== bootstrap?.user.id) {
        // Off the event path: audio setup can be slow on some systems and must never delay the catch-up below.
        window.setTimeout(() => void playMessageSound(message), 0);
        // Their message arrived: stop showing them as typing right away instead of waiting for expiry.
        if (typingByConversation.get(message.conversation_id)?.has(message.payload.sender_user_id)) setTyping(message.conversation_id, message.payload.sender_user_id, false);
      }
      const revision = realtimeRevision(message.revision); if (revision === null) return;
      if (message.conversation_id !== current?.id) {
        void scheduleLiveConversationRefresh();
        return;
      }
      if (snapshotLoading.has(message.conversation_id)) {
        bufferedEvents.clear(); bufferedEvents.set(message.conversation_id, [message]);
        pendingCurrentRevision = pendingCurrentRevision === null || revision > pendingCurrentRevision ? revision : pendingCurrentRevision;
        return;
      }
      void catchUp(message.conversation_id, revision);
    });
  } catch { if (valid()) { connectionLabel = 'REST-режим · Переподключение…'; renderShell(); retry(); } }
}

function realtimeRevision(value: string | undefined): bigint | null {
  return value && /^\d+$/.test(value) ? BigInt(value) : null;
}

// Return true also for a superseded request: its caller must not replace a newer window.
async function appendCreatedHistory(conversationId: string, ids: Set<string>, cursor: bigint, snapshot: bigint, active: () => boolean, signal: AbortSignal): Promise<boolean> {
  const history = root.querySelector<HTMLElement>('#messages');
  if (historyReplacementPending !== null || !historyWindowAtTail || !messages.length || messages.some(row => row.pending)
      || !history || history.dataset.conversationId !== conversationId || !captureHistoryAnchor(history, conversationId).atBottom) return false;
  const before = messages, generation = historyGeneration, epoch = currentDetails?.membership_epoch_id;
  const sameEpoch = () => currentDetails?.membership_epoch_id === epoch;
  const valid = () => active() && historyGeneration === generation && messages === before && revisionCursors.get(conversationId) === cursor;
  const result = await api<{ messages: Message[]; snapshot_revision: string }>(
    `/v1/conversations/${conversationId}/messages?after=${encodeURIComponent(before.at(-1)!.seq)}&limit=100`, { signal });
  if (!valid()) return true;
  if (!sameEpoch() || result.snapshot_revision !== snapshot.toString()) return false;
  const merged = mergeCreatedMessages(before, result.messages, ids, conversationId);
  if (!merged) return false;
  await resolveReplySources(result.messages, conversationId, () => valid() && sameEpoch());
  if (!valid()) return true;
  if (!sameEpoch()) return false;
  // The reader may have scrolled up while HTTP/reply lookups were pending.
  // Let the authoritative context path preserve that new anchor instead of trimming it.
  if (!captureHistoryAnchor(history, conversationId).atBottom) return false;
  messages = merged; ++historyGeneration; // Fence older-page requests captured against the old window.
  historyHasEarlier ||= before.length + result.messages.length > merged.length;
  contextTargetId = null; historyWindowAfter = null;
  revisionCursors.set(conversationId, snapshot);
  return true;
}

// Retain unaffected rows only within the same authorized epoch and exact revision.
async function updateChangedHistory(conversationId: string, ids: Set<string>, cursor: bigint, snapshot: bigint, active: () => boolean, signal: AbortSignal): Promise<boolean> {
  if (historyReplacementPending !== null || !messages.length || messages.some(row => row.pending)) return false;
  const before = messages, generation = historyGeneration, epoch = currentDetails?.membership_epoch_id;
  const affected = before.filter(row => ids.has(row.id) || Boolean(row.reply_to_message_id && ids.has(row.reply_to_message_id)));
  const requested = affected.map(row => row.id);
  // Even changes outside this window must perform an authorized read before advancing.
  if (!requested.length) requested.push(...ids);
  if (!epoch || requested.length > 100) return false;
  const valid = () => active() && historyGeneration === generation && messages === before && revisionCursors.get(conversationId) === cursor;
  let result: { messages: Message[]; snapshot_revision: string; membership_epoch_id: string };
  try {
    result = await api(`/v1/conversations/${conversationId}/messages/by-id?ids=${requested.map(encodeURIComponent).join(',')}`, { signal });
  } catch (error) {
    if (!valid()) return true;
    // Old server during rollback: its normal history route remains authoritative.
    if ((error as {status?: number})?.status === 404) return false;
    throw error;
  }
  if (!valid()) return true;
  if (currentDetails?.membership_epoch_id !== epoch || result.membership_epoch_id !== epoch || result.snapshot_revision !== snapshot.toString() || !Array.isArray(result.messages)) return false;
  const replacements = new Map(result.messages.map(row => [row.id, row]));
  if (replacements.size !== result.messages.length || result.messages.some(row => row.conversation_id !== conversationId || !requested.includes(row.id))
      || affected.some(row => replacements.get(row.id)?.seq !== row.seq)) return false;
  await resolveReplySources(result.messages, conversationId, () => valid() && currentDetails?.membership_epoch_id === epoch);
  if (!valid()) return true;
  if (currentDetails?.membership_epoch_id !== epoch) return false;
  messages = before.map(row => replacements.get(row.id) ?? row); ++historyGeneration;
  revisionCursors.set(conversationId, snapshot);
  return true;
}

async function catchUp(conversationId: string, requestedRevision: bigint | null = null): Promise<void> {
  const valid = sessionGuard(); const visible = selectionGuard();
  if (current?.id !== conversationId) { await scheduleLiveConversationRefresh(); return; }
  if (requestedRevision !== null) pendingCurrentRevision = pendingCurrentRevision === null || requestedRevision > pendingCurrentRevision ? requestedRevision : pendingCurrentRevision;
  const running = catchups.get(conversationId); if (running) return running;
  catchupController?.abort(); catchups.clear();
  const controller = new AbortController(); catchupController = controller;
  const task = (async () => {
    try {
      let cursor = revisionCursors.get(conversationId);
      if (cursor === undefined) {
        await refreshVisibleHistory(conversationId);
        refreshReadCursors(conversationId, () => valid() && current?.id === conversationId);
        if (!valid()) return;
        cursor = revisionCursors.get(conversationId) ?? 0n;
      }
      const membershipEpoch = currentDetails?.membership_epoch_id;
      const result = await api<{ events: RealtimeEvent[]; next_revision: string; snapshot_revision: string }>(
        `/v1/conversations/${conversationId}/events?after_revision=${cursor.toString()}&limit=100`, { signal: controller.signal },
      );
      if (!valid()) return;
      const next = BigInt(result.next_revision); const snapshot = BigInt(result.snapshot_revision);
      const changed = result.events.length > 0 || snapshot > cursor;
      // Other conversations' sidebar updates already arrive via their own WS push (the
      // 'message' handler calls scheduleLiveConversationRefresh() directly when an event's
      // conversation_id differs from the open one) -- this call was only for the open
      // conversation's own sidebar row (preview/unread), so it has nothing to do when this
      // conversation itself has no new events. Unconditional here was the other half of the
      // ~10s foreground-poll jitter: every tick refreshed (and re-rendered) the sidebar list
      // even when nothing in it had changed.
      if (changed) { const authorized = await scheduleLiveConversationRefresh(true); if (!valid() || !visible() || !authorized) return; }
      if (changed && visible() && current?.id === conversationId) {
        if (next === snapshot && membershipEpoch && membershipEpoch === currentDetails?.membership_epoch_id
            && onlyReadReceiptEvents(result.events, cursor, snapshot)) {
          revisionCursors.set(conversationId, snapshot);
          refreshReadCursors(conversationId, () => valid() && visible() && current?.id === conversationId
            && currentDetails?.membership_epoch_id === membershipEpoch);
          return;
        }
        const ids = next === snapshot && membershipEpoch && membershipEpoch === currentDetails?.membership_epoch_id
          ? createdEventIds(result.events, cursor, snapshot) : null;
        const changedIds = next === snapshot && membershipEpoch && membershipEpoch === currentDetails?.membership_epoch_id
          ? changedEventIds(result.events, cursor, snapshot) : null;
        const updated = ids ? await appendCreatedHistory(conversationId, ids, cursor, snapshot, () => valid() && visible(), controller.signal)
          : changedIds ? await updateChangedHistory(conversationId, changedIds, cursor, snapshot, () => valid() && visible(), controller.signal) : false;
        if (!updated && visible()) await refreshVisibleHistory(conversationId);
        refreshReadCursors(conversationId, () => valid() && visible() && current?.id === conversationId);
      } else { revisionCursors.clear(); revisionCursors.set(conversationId, next > snapshot ? next : snapshot); }
      if (!valid()) return;
      // A failed projection refresh must leave the previous cursor replayable. Only render when
      // something in THIS conversation actually changed: scheduleLiveConversationRefresh() above
      // already renders anything the sidebar itself needs (via loadConversations()), and the
      // foreground poll (every 10s while a conversation is open) was calling catchUp() -- and
      // this unconditional renderShell() -- on every single tick even when nothing changed,
      // tearing down and rebuilding the whole page (both columns) for no visible reason.
      if (changed && visible()) renderShell();
    } catch (error) {
      if (!valid()) return;
      const code = typeof error === 'object' && error !== null && 'code' in error ? String((error as { code?: unknown }).code ?? '') : '';
      if (code === 'RESYNC_REQUIRED' && visible() && current?.id === conversationId) {
        await refreshVisibleHistory(conversationId); if (!visible()) return; await scheduleLiveConversationRefresh(); if (visible()) renderShell();
      } else if (code === 'NOT_FOUND' && visible() && current?.id === conversationId) {
        filesCards.forgetConversation(conversationId);pmTaskPreviews.forgetConversation(conversationId);
        clearConversationSelection(); revisionCursors.delete(conversationId); const active = sessionGuard(); await loadConversations(active); if (active()) renderShell();
      }
    } finally {
      if (catchupController === controller) {
        catchups.delete(conversationId); catchupController = null;
        const observed = revisionCursors.get(conversationId) ?? 0n;
        const rerun = pendingCurrentRevision !== null && pendingCurrentRevision > observed;
        pendingCurrentRevision = null;
        if (rerun && valid() && current?.id === conversationId) queueMicrotask(() => void catchUp(conversationId));
      }
    }
  })();
  catchups.set(conversationId, task); return task;
}

function subscribeCurrent(): void {
  if (socket?.readyState !== WebSocket.OPEN || !current) return;
  reportedVisible = tabAttended(); reportedSuppressed = suppressBackgroundPush();
  socket.send(JSON.stringify({ type: 'subscribe', conversation_id: current.id, visible: reportedVisible, suppress_push: reportedSuppressed }));
}
function sendTyping(): void {
  if (!current || socket?.readyState !== WebSocket.OPEN || Date.now() - typingSentAt < 3000) return;
  typingSentAt = Date.now(); socket.send(JSON.stringify({ type: 'typing', conversation_id: current.id, active: true }));
}

async function pendingPut(conversationId: string, command: object, user: Bootstrap['user'], admitted = (): boolean => true): Promise<Record<string, unknown> | null> {
  const valid = sessionGuard(); const epoch = accountEpoch(user);
  const db = await openDb();
  try {
    if (!valid() || !admitted()) return null;
    const item = { ...command, conversation_id: conversationId, workspace_id: user.workspace_id, user_id: user.id, local_epoch: epoch };
    const tx = db.transaction('pending', 'readwrite');
    tx.objectStore('pending').put(item);
    await transactionDone(tx);
    return item;
  } finally { db.close(); }
}
async function pendingDelete(id: string, account = bootstrap, cancelledEpoch?: string): Promise<void> {
  const valid = sessionGuard(); const db = await openDb();
  try {
    // Cancellation cleanup must finish for its original owner even after logout/login.
    // Readwrite ordering removes any already-started put; later puts fail admission.
    if (!account || (cancelledEpoch === undefined && (!valid() || bootstrap !== account))) return;
    const tx = db.transaction('pending', 'readwrite'); const store = tx.objectStore('pending');
    const request = store.get(id);
    request.onsuccess = () => {
      const item = request.result;
      const allowed = cancelledEpoch === undefined ? valid() : (item?.local_epoch ?? '') === cancelledEpoch;
      if (allowed && item?.user_id === account.user.id && item?.workspace_id === account.user.workspace_id) store.delete(id);
    };
    await transactionDone(tx);
  } finally { db.close(); }
}
async function pendingGet(id: string): Promise<Record<string, unknown> | null> {
  const db = await openDb();
  try {
    return (await readStorageRequest(db.transaction('pending').objectStore('pending').get(id)) as Record<string, unknown> | undefined) ?? null;
  } finally { db.close(); }
}
async function pendingFailure(id: string, account: Bootstrap, error: unknown): Promise<void> {
  const status = (error as { status?: number })?.status;
  const permanent = [400, 401, 403, 404, 409, 410, 422].includes(status ?? 0);
  const valid = sessionGuard(); const db = await openDb();
  try {
    if (!valid() || bootstrap !== account) return;
    const tx = db.transaction('pending', 'readwrite'); const store = tx.objectStore('pending');
    const request = store.get(id);
    request.onsuccess = () => {
      const item = request.result;
      // Keep genuinely legacy rows unchanged, including their absent status fields.
      if (typeof item?.membership_epoch_id !== 'string' || !item.membership_epoch_id.trim()) return;
      if (valid() && item?.user_id === account.user.id && item?.workspace_id === account.user.workspace_id && (item.local_epoch ?? '') === accountEpoch(account.user)) store.put({ ...item, quarantined: Boolean(item.quarantined) || permanent, send_error: 'Не удалось отправить. Текст сохранён; повтор использует тот же ID.' });
    };
    await transactionDone(tx);
  } finally { db.close(); }
}
// Document-lifetime tombstones outlive flights and shell rerenders. IDB deletion,
// not these in-memory markers, prevents resurrection after reload.
const cancelledPending = new Set<string>();
function pendingKey(id: string, account: Bootstrap, epoch = accountEpoch(account.user)): string {
  return JSON.stringify([account.user.workspace_id, account.user.id, epoch, id]);
}
const pendingFlights = new Map<string, { cancelled: boolean; task: Promise<void> }>();
async function dispatchPending(id: string, account: Bootstrap, valid: () => boolean): Promise<void> {
  if (outboxResetOwner === account) throw new Error('Дождитесь завершения сброса очереди.');
  const flightKey = pendingKey(id, account);
  if (cancelledPending.has(flightKey)) return;
  const existing = pendingFlights.get(flightKey); if (existing) return existing.task;
  const flight = { cancelled: false, task: Promise.resolve() };
  pendingFlights.set(flightKey, flight);
  flight.task = (async () => {
    try {
      const item = await pendingGet(id);
      if (flight.cancelled || cancelledPending.has(flightKey) || !valid() || bootstrap !== account || !item || item.user_id !== account.user.id || item.workspace_id !== account.user.workspace_id || (item.local_epoch ?? '') !== accountEpoch(account.user)) return;
      if (outboxResetOwner === account) throw new Error('Дождитесь завершения сброса очереди.');
      requirePendingEpoch(item);
      if (item.managed_cancel_requested) return;
      if (item.quarantined) throw new Error('Автоматическая отправка остановлена: доступ запрещён.');
      // No await between the final local cancellation fence and actual dispatch.
      const managed=managedPendingCommand(item,account.managed_files_enabled===true);
      const command=managed?.body??toWireMessageCommand(item);
      const ownsSelection = selectionGuard();
      await loadingStatus.run('Отправляем сообщение…', () => valid() && ownsSelection() && current?.id === item.conversation_id && !flight.cancelled,
        () => api(managed?.path??`/v1/conversations/${item.conversation_id}/messages`, { method: 'POST', body: JSON.stringify(command) }));
      if (valid() && !flight.cancelled) await pendingDelete(id, account);
    } catch (error) {
      if (valid() && !flight.cancelled) {
        try { await pendingFailure(id, account, error); } catch { /* The caller must still receive the original failure if storage is unavailable. */ }
      }
      if (!flight.cancelled) throw error;
    } finally { pendingFlights.delete(flightKey); }
  })();
  return flight.task;
}
// A manual send/retry needs only its own durable command. A stalled scan of an
// unrelated old queue entry must not prevent this command's per-ID dispatch.
function projectPendingCommand(item: Record<string, unknown>, account: Bootstrap, valid: () => boolean): void {
  if (!valid() || bootstrap !== account || item.user_id !== account.user.id || item.workspace_id !== account.user.workspace_id
      || (item.local_epoch ?? '') !== accountEpoch(account.user) || (cancelledPending.has(pendingKey(String(item.client_message_id), account)) && !item.managed_cancel_requested)) return;
  const existing = legacyPending?.account === account && legacyPending.valid() ? legacyPending.items : [];
  legacyPending = {account, valid:sessionGuard(), items:[...existing.filter(row => row.client_message_id !== item.client_message_id), item]};
}
function forgetPendingProjection(id: string, account: Bootstrap): void {
  if (legacyPending?.account === account) legacyPending.items = legacyPending.items.filter(row => row.client_message_id !== id);
}
async function refreshPendingProjection(account: Bootstrap, valid: () => boolean): Promise<Record<string, unknown>[]> {
  const db = await openDb();
  try {
    const items = await readStorageRequest(db.transaction('pending').objectStore('pending').getAll()) as Record<string, unknown>[];
    if (!valid() || bootstrap !== account) return [];
    const owned = items.filter(item => item.user_id === account.user.id && item.workspace_id === account.user.workspace_id && (item.local_epoch ?? '') === accountEpoch(account.user) && (!cancelledPending.has(pendingKey(String(item.client_message_id), account)) || Boolean(item.managed_cancel_requested)));
    legacyPending = { account, valid, items: owned }; return owned;
  } finally { db.close(); }
}
async function retryPending(id: string): Promise<void> {
  const account = bootstrap; const valid = selectionGuard();
  try {
    const item = await pendingGet(id);
    if (!account || !valid() || !item || (item.local_epoch ?? '') !== accountEpoch(account.user) || typeof item.conversation_id !== 'string' || item.user_id !== account.user.id || item.workspace_id !== account.user.workspace_id) return;
    if (item.managed_cancel_requested) { await cancelPending(id); return; }
    requirePendingEpoch(item);
    if (item.quarantined) throw new Error('Автоматическая отправка остановлена: доступ запрещён.');
    await dispatchPending(id, account, valid);
    if (!valid()) return;
    forgetPendingProjection(id, account);
    if (current?.id === item.conversation_id) await loadMessages(item.conversation_id);
    if (!valid()) return;
    await loadConversations(valid); if (valid()) renderShell();
  } catch (error) {
    let failure = error;
    if (account && valid()) {
      try {
        await pendingFailure(id, account, error);
        const durable = await pendingGet(id); if (durable) projectPendingCommand(durable, account, valid);
      } catch (storageFailure) { failure = storageFailure; }
      if (valid()) renderShell();
    }
    if (valid()) toast(errorMessage(failure, 'Повтор не выполнен'));
  }
}

/** Serialized with pendingPut: preserve the original command and mark it before
 * remote cancellation. Unknown ACK survives reload and is never auto-sent. */
async function pendingMarkManagedCancellation(id: string, account: Bootstrap, epoch: string, seed?: Record<string, unknown>): Promise<boolean> {
  const valid = sessionGuard(); const db = await openDb(); let marked = false;
  try {
    if (!valid() || bootstrap !== account || accountEpoch(account.user) !== epoch) return false;
    const tx = db.transaction('pending', 'readwrite'); const store = tx.objectStore('pending');
    const request = store.get(id);
    request.onsuccess = () => {
      if (!valid() || bootstrap !== account || accountEpoch(account.user) !== epoch) return;
      const item = request.result ?? (seed ? {...seed,workspace_id:account.user.workspace_id,user_id:account.user.id,local_epoch:epoch} : null);
      if (!item || item.client_message_id !== id || item.user_id !== account.user.id || item.workspace_id !== account.user.workspace_id || (item.local_epoch ?? '') !== epoch || !Object.hasOwn(item,'managed_files')) return;
      store.put({...item,managed_cancel_requested:true}); marked = true;
    };
    await transactionDone(tx); return marked;
  } finally { db.close(); }
}
async function cancelPending(id: string): Promise<void> {
  const valid = sessionGuard(); const account = bootstrap; if (!account) return;
  const epoch = accountEpoch(account.user); const key = pendingKey(id, account, epoch);
  const saving = savingPending.get(key);
  const seed = saving?.account === account && saving.valid() ? saving.item : undefined;
  cancelledPending.add(key);
  const flight = pendingFlights.get(key); if (flight) flight.cancelled = true;
  try {
    const stored = await pendingGet(id);
    if (!valid() || bootstrap !== account) return;
    const owned = stored?.user_id === account.user.id && stored.workspace_id === account.user.workspace_id && (stored.local_epoch ?? '') === epoch ? stored : null;
    const item = owned ?? seed;
    if (item && Object.hasOwn(item,'managed_files')) {
      const result = await cancelManagedPending(item,account.managed_files_enabled===true,
        async <T>(path:string,options?:RequestInit):Promise<T> => {
          if (!valid() || bootstrap !== account) throw new DOMException('Запрос отменён','AbortError');
          return api<T>(path,options);
        },
        async () => {
          const marked = await pendingMarkManagedCancellation(id,account,epoch,item);
          if (marked && valid()) { await refreshPendingProjection(account,valid); if(valid()) renderShell(); }
          return marked;
        }, () => pendingDelete(id,account,epoch));
      if (!valid()) return;
      if (result.state === 'committed') { toast('Сообщение уже отправлено'); if (current?.id === item.conversation_id) void catchUp(String(item.conversation_id)); }
    } else {
      await pendingDelete(id, account, epoch);
    }
    if (!valid()) return;
    messages = messages.filter(message => !(message.pending && message.id === id));
    forgetPendingProjection(id,account); renderShell();
  } catch (error) {
    if (!valid()) return;
    await refreshPendingProjection(account,valid).catch(() => undefined);
    if(valid()) { renderShell(); toast(errorMessage(error,'Отмена ещё не подтверждена')); }
  }
}
async function flushPending(): Promise<void> {
  const account = bootstrap; const valid = sessionGuard(); if (!account) return;
  try {
    const owned = await refreshPendingProjection(account, valid); if (!valid()) return;
    if (owned.length) renderShell();
    const sendable = owned.filter(item => (!Object.hasOwn(item,'managed_files')||account.managed_files_enabled===true)&&!item.quarantined && !item.managed_cancel_requested && typeof item.membership_epoch_id === 'string' && item.membership_epoch_id.trim());
    if (!sendable.length) return;
    await Promise.all(sendable.map(item => dispatchPending(String(item.client_message_id), account, valid).catch(() => undefined)));
    if (!valid()) return;
    await refreshPendingProjection(account, valid); if (!valid()) return;
    const visible = selectionGuard(); await loadConversations(); if (!valid()) return;
    if (current && visible() && navigationState?.generation !== selectionGeneration) await loadMessages(current.id);
    if (visible()) renderShell();
  } catch { /* Network/storage failures retain the local command; no unhandled background rejection. */ }
}

function vapidKey(value: string): Uint8Array<ArrayBuffer> {
  const padded = `${value}${'='.repeat((4 - value.length % 4) % 4)}`.replace(/-/g, '+').replace(/_/g, '/');
  const bytes = Uint8Array.from(atob(padded), (char) => char.charCodeAt(0));
  return new Uint8Array(bytes.buffer);
}

async function savePushBinding(binding: Record<string, unknown>, account = bootstrap, valid = sessionGuard()): Promise<void> {
  if (!account || !valid()) return;
  const epoch = accountEpoch(account.user); const db = await openDb();
  try {
    if (!valid() || bootstrap !== account) return;
    const tx = db.transaction('meta', 'readwrite');
    tx.objectStore('meta').put({ ...binding, key: `push-binding:${account.user.workspace_id}:${account.user.id}`, workspace_id: account.user.workspace_id, user_id: account.user.id, local_epoch: epoch });
    await transactionDone(tx);
  } finally { db.close(); }
}

async function loadPushBinding(): Promise<Record<string, unknown> | null> {
  const account = bootstrap; const valid = sessionGuard(); const db = await openDb();
  try {
    if (!account || !valid()) return null;
    return (await readStorageRequest(db.transaction('meta').objectStore('meta').get(`push-binding:${account.user.workspace_id}:${account.user.id}`)) as Record<string, unknown> | undefined) ?? null;
  } finally { db.close(); }
}

// Chrome/Edge fire this when the app is installable; holding on to it lets the profile dialog
// offer a real "install" button instead of hunting through the browser menu.
let installPrompt: (Event & { prompt: () => Promise<void> }) | null = null;
window.addEventListener('beforeinstallprompt', (event) => {
  event.preventDefault();
  installPrompt = event as Event & { prompt: () => Promise<void> };
  if (activeProfileDialog) renderShell();
});
window.addEventListener('appinstalled', () => { installPrompt = null; rememberInstallChoice(); });

// One unobtrusive offer, once: the browser gives us the prompt, so the reader does not have to
// know it exists. Declining (or installing) puts it away for good on this device.
const INSTALL_OFFER_KEY = 'pm-chat:install-offer';
function installOfferDismissed(): boolean {
  try { return localStorage.getItem(INSTALL_OFFER_KEY) === 'done'; } catch { return true; }
}
function rememberInstallChoice(): void {
  try { localStorage.setItem(INSTALL_OFFER_KEY, 'done'); } catch { /* per-device convenience only */ }
}

// What this browser can do about notifications, for the profile dialog. iOS only exposes push to
// a PWA started from the Home Screen, so an iPhone in a normal Safari tab gets instructions
// instead of a dead button.
type PushSupport = 'ready' | 'denied' | 'ios-needs-install' | 'unsupported';
let pushDeviceBound = false;
function standaloneApp(): boolean {
  return window.matchMedia?.('(display-mode: standalone)').matches === true
    || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}
function appleMobile(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}
// Installing is a browser menu item, and every browser hides it somewhere else.
// Browsers never install a web app on their own: it always takes the browser's own prompt and a
// person confirming it. Where that prompt is available the button opens it; everywhere else the
// same button opens the steps for this browser, so there is one place to go either way.
function installHelpSteps(): { system: string; steps: string[] } {
  const agent = navigator.userAgent;
  const safari = /Safari/.test(agent) && !/Chrome|Chromium|Android|Edg/.test(agent);
  if (appleMobile()) return { system: 'iPhone и iPad', steps: [
    'Нажмите «Поделиться» (квадрат со стрелкой вверх) в нижней панели Safari.',
    'Выберите «На экран „Домой“» и подтвердите.',
    'Открывайте чат с домашнего экрана — там же работают уведомления.',
  ] };
  if (safari) return { system: 'Safari на macOS', steps: [
    'Меню «Файл» → «Добавить в Dock».',
    'Подтвердите название — чат появится в Dock и в «Программах».',
    'Дальше он открывается отдельным окном, без адресной строки.',
  ] };
  if (/Firefox/.test(agent)) return { system: 'Firefox', steps: [
    'Firefox не умеет устанавливать веб-приложения.',
    `Откройте ${location.origin} в Chrome, Chromium, Edge или Safari и установите оттуда.`,
  ] };
  if (/Android/.test(agent)) return { system: 'Android', steps: [
    'Меню браузера (три точки) → «Установить приложение» или «Добавить на главный экран».',
    'Подтвердите — значок появится на главном экране.',
  ] };
  return { system: 'Chrome, Chromium и Edge', steps: [
    'Значок установки в правой части адресной строки (монитор со стрелкой) — нажмите его.',
    'Если значка нет: меню браузера (три точки) → «Трансляция, сохранение и доступ» → «Установить страницу как приложение». В Edge — «Приложения» → «Установить этот сайт как приложение».',
    'Подтвердите установку: чат откроется отдельным окном и появится в меню приложений.',
  ] };
}

function pushSupport(): PushSupport {
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
    return appleMobile() && !standaloneApp() ? 'ios-needs-install' : 'unsupported';
  }
  if (Notification.permission === 'denied') return 'denied';
  return 'ready';
}
// Answers "the chat says notifications are on, but nothing arrives": this one bypasses every
// suppression rule, so if it appears the delivery path is fine and the silence was a rule
// (chat on screen, already read, quiet hours); if it does not, the browser or the system is
// swallowing notifications from this site.
// What this browser itself reports about its push plumbing. Shown in the profile because the two
// failures behind "nothing arrives" -- no worker, or a subscription the browser no longer honours
// -- are invisible from the server, which only sees that the push service accepted the message.
let pushDiagnostics: { worker: 'active' | 'installing' | 'none'; subscribed: boolean; host: string | null } | null = null;
async function loadPushDiagnostics(): Promise<void> {
  try {
    if (!('serviceWorker' in navigator)) { pushDiagnostics = { worker: 'none', subscribed: false, host: null }; return; }
    const registration = await navigator.serviceWorker.getRegistration('/');
    const worker = registration?.active ? 'active' : registration?.installing || registration?.waiting ? 'installing' : 'none';
    const subscription = registration ? await registration.pushManager.getSubscription() : null;
    let host: string | null = null;
    try { host = subscription ? new URL(subscription.endpoint).hostname : null; } catch { host = null; }
    pushDiagnostics = { worker, subscribed: Boolean(subscription), host };
  } catch { pushDiagnostics = null; }
}

// Putting the installed app where people expect to find it: the desktop, the dock, and at login.
// Everything here is outside the browser, so the chat can only name the steps for the system the
// reader is on.
function desktopHelpSteps(): { system: string; steps: string[] } {
  const agent = navigator.userAgent;
  if (appleMobile()) return { system: 'iPhone и iPad', steps: [
    'Чат добавляется на экран «Домой»: «Поделиться» → «На экран „Домой“». Значок и будет приложением.',
    'Автозапуска на iOS нет, но уведомления работают и при закрытом приложении.',
  ] };
  if (/Mac/.test(agent)) return { system: 'macOS', steps: [
    'Сначала установите приложение: в Chrome или Edge — значок установки в адресной строке; в Safari — меню «Файл» → «Добавить в Dock».',
    'Ярлык лежит в Launchpad и в папке «Программы» (у Chrome — «Chrome Apps»). Перетащите его в Dock, либо правой кнопкой по значку в Dock → «Параметры» → «Оставить в Dock».',
    'Автозапуск: правой кнопкой по значку в Dock → «Параметры» → «Открывать при входе». Либо Системные настройки → «Основные» → «Объекты входа» → «+» и выберите приложение.',
  ] };
  if (/Windows/.test(agent)) return { system: 'Windows', steps: [
    'Установите приложение: в Chrome или Edge — значок установки в адресной строке. При установке отметьте «Создать ярлык на рабочем столе».',
    'Ярлык можно закрепить: правой кнопкой по значку на панели задач → «Закрепить на панели задач».',
    'Автозапуск: нажмите Win+R, введите shell:startup и скопируйте туда ярлык приложения.',
  ] };
  if (/Android/.test(agent)) return { system: 'Android', steps: [
    'Меню браузера → «Установить приложение» (или «Добавить на главный экран»).',
    'Значок появится на главном экране; автозапуска на Android нет, уведомления работают и без него.',
  ] };
  return { system: 'Ubuntu и другие Linux', steps: [
    'Установите приложение: в Chrome, Chromium или Edge — значок установки в адресной строке либо меню → «Установить Chat Ostart».',
    'Браузер создаёт ярлык в ~/.local/share/applications (имя начинается с chrome-). Для значка на рабочем столе скопируйте его в ~/Desktop и разрешите запуск: правая кнопка → «Разрешить запуск».',
    'Автозапуск: скопируйте тот же файл в ~/.config/autostart — приложение будет открываться при входе в систему.',
    'В меню приложений значок можно закрепить на панели: правая кнопка → «Добавить в избранное».',
  ] };
}

function showInstallHelp(): void {
  showHelpDialog('Как установить приложение', installHelpSteps(),
    'Установку подтверждает сам браузер — без вашего согласия он этого не делает.');
}

function showDesktopHelp(): void {
  showHelpDialog('Приложение на рабочем столе и в автозапуске', desktopHelpSteps(),
    'Чат ставится из браузера и дальше живёт как обычная программа.');
}

// Where the operating system hides its notification switches. Reached from the Notifications
// section and shown automatically when a test push arrives but nothing appears on screen -- that
// combination always means the system or the browser is holding it back, never the chat.
function notificationHelpSteps(): { system: string; steps: string[] } {
  const agent = navigator.userAgent;
  const apple = /Mac|iPhone|iPad|iPod/.test(agent);
  if (appleMobile()) return { system: 'iPhone и iPad', steps: [
    'Чат должен быть добавлен на экран «Домой» и открыт оттуда — из вкладки Safari уведомления не приходят.',
    '«Настройки» → «Уведомления» → Chat Ostart: «Допуск уведомлений» включён, стиль «Баннеры».',
    'Проверьте режим «Не беспокоить» и «Фокусирование».',
  ] };
  if (apple) return { system: 'macOS', steps: [
    'Системные настройки → «Уведомления» → ваш браузер (Google Chrome или Safari): разрешить уведомления, стиль «Баннеры» или «Напоминания».',
    'Там же выключите «Не беспокоить» и «Фокусирование» либо добавьте браузер в разрешённые.',
    'В браузере: значок слева от адреса → «Уведомления» → «Разрешить».',
  ] };
  if (/Windows/.test(agent)) return { system: 'Windows', steps: [
    '«Параметры» → «Система» → «Уведомления»: уведомления включены, ваш браузер в списке разрешён.',
    'Выключите «Фокусировка внимания» («Не беспокоить») или добавьте браузер в приоритетные.',
    'В браузере: значок слева от адреса → «Уведомления» → «Разрешить».',
  ] };
  if (/Android/.test(agent)) return { system: 'Android', steps: [
    `«Настройки» → «Приложения» → ваш браузер → «Уведомления»: включены, и сайт ${location.host} в списке разрешён.`,
    'Проверьте режим «Не беспокоить».',
  ] };
  return { system: 'Linux', steps: [
    'Настройки системы → «Уведомления»: уведомления включены, ваш браузер разрешён.',
    'Выключите режим «Не беспокоить».',
    'В браузере: значок слева от адреса → «Уведомления» → «Разрешить».',
  ] };
}

function soundHelpSteps(): { system: string; steps: string[] } {
  const agent = navigator.userAgent;
  const browser = /Firefox/.test(agent) ? 'Firefox' : /Safari/.test(agent) && !/Chrome|Chromium|Edg/.test(agent) ? 'Safari' : 'Chrome';
  const site = browser === 'Safari'
    ? 'Safari → «Настройки для этого веб-сайта» → «Автовоспроизведение»: «Разрешить всё».'
    : browser === 'Firefox'
      ? 'Значок слева от адреса → «Разрешения» → «Воспроизведение звука»: «Разрешить».'
      : 'Значок слева от адреса → «Настройки сайта» → «Звук»: «Разрешить».';
  return { system: browser, steps: [
    'Нажмите «Проверить звук» выше: если браузер заглушил звук, там будет написано именно это.',
    'Браузер включает звук на странице только после первого нажатия на неё — щёлкните по чату и проверьте снова.',
    site,
    'Вкладка не должна быть заглушена: правый клик по вкладке → «Включить звук на сайте».',
    'Громкость системы и то, что звук идёт в нужное устройство (наушники, колонки).',
    'Звук звучит только при открытом чате. Когда чат закрыт, о сообщении сообщает уведомление — если не приходят и они, откройте «Не приходят уведомления?».',
  ] };
}

function showSoundHelp(): void {
  showHelpDialog('Если не слышно звука сообщений', soundHelpSteps(),
    'Звук проигрывает браузер, и он же может его заглушить.');
}

function showNotificationHelp(): void {
  showHelpDialog('Если уведомления не приходят', notificationHelpSteps(),
    'Чат отправляет уведомление, но показывает его операционная система.');
}

function showHelpDialog(title: string, help: { system: string; steps: string[] }, lead: string): void {
  if (activeEditor) return;
  const dialog = element('dialog', 'message-editor notification-help');
  dialog.setAttribute('aria-label', title);
  const valid = sessionGuard();
  const close = (): void => { if (activeEditor?.close === close) activeEditor = null; if (dialog.open) dialog.close(); dialog.remove(); };
  dialog.addEventListener('close', () => { if (activeEditor?.close === close) activeEditor = null; dialog.remove(); });
  activeEditor = { dialog, valid, close };
  const header = element('div', 'chat-info-head');
  header.append(element('h3', '', title));
  const closeButton = element('button', 'ghost', '✕'); closeButton.type = 'button';
  closeButton.setAttribute('aria-label', 'Закрыть подсказку'); closeButton.addEventListener('click', close);
  header.append(closeButton);
  const body = element('div', 'notification-help-body');
  body.append(element('p', 'meta', `${lead} Шаги для ${help.system}:`));
  const list = element('ol', 'notification-help-steps');
  for (const step of help.steps) list.append(element('li', '', step));
  body.append(list);

  const done = element('button', '', 'Понятно'); done.type = 'button'; done.addEventListener('click', close);
  body.append(done);
  dialog.append(header, body);
  document.body.append(dialog);
  dialog.showModal();
}

// Full-screen look at a photo from a message. The thumbnail in the bubble is 640px at most, so the
// original is shown here; downloading stays a separate button. Esc (the dialog's own cancel) or a
// click anywhere but the picture or the buttons closes it.
function openImageViewer(attachment: Attachment): void {
  if (document.querySelector('dialog.image-viewer[open]')) return;
  const dialog = element('dialog', 'image-viewer'); dialog.setAttribute('aria-label', attachment.name);
  const close = (): void => { if (dialog.open) dialog.close(); dialog.remove(); };
  dialog.addEventListener('close', () => dialog.remove());
  const bar = element('div', 'image-viewer-bar');
  bar.append(element('span', 'image-viewer-name', attachment.name));
  const download = element('a', 'image-viewer-download', 'Скачать');
  const downloadPath=attachmentPath(attachment,'download',bootstrap?.managed_files_enabled===true);if(downloadPath)download.href=downloadPath; download.rel = 'nofollow noopener'; download.target = '_blank'; download.download = attachment.name;
  const closeButton = element('button', 'image-viewer-close', '✕'); closeButton.type = 'button';
  closeButton.setAttribute('aria-label', 'Закрыть просмотр'); closeButton.addEventListener('click', close);
  bar.append(download, closeButton);
  const image = element('img', 'image-viewer-image'); const imagePath=attachmentPath(attachment,attachment.managed?'preview':'download',bootstrap?.managed_files_enabled===true);if(imagePath)image.src=imagePath; image.alt = attachment.name;
  dialog.addEventListener('click', (event) => { if (event.target === dialog || (event.target as HTMLElement).classList.contains('image-viewer-stage')) close(); });
  const stage = element('div', 'image-viewer-stage'); stage.append(image);
  dialog.append(bar, stage);
  document.body.append(dialog);
  dialog.showModal();
  closeButton.focus();
}

let lastPushReceived: { at: string; type?: string } | null = null;
async function loadLastPushReceived(): Promise<void> {
  try {
    const db = await openDb();
    try {
      lastPushReceived = (await readStorageRequest(db.transaction('meta').objectStore('meta').get('push-received')) as { at: string; type?: string } | undefined) ?? null;
    } finally { db.close(); }
  } catch { lastPushReceived = null; }
}

async function testBrowserPush(): Promise<void> {
  return runUiAction(async (request) => {
    await loadLastPushReceived();
    const before = lastPushReceived?.at ?? '';
    await request('/v1/push/test', { method: 'POST', body: JSON.stringify({ device_id: deviceId() }) });
    toast('Отправили проверочное уведомление');
    // Then say what happened, instead of leaving the reader to tell "never arrived" apart from
    // "arrived and was not shown" -- the two look identical on screen.
    const deadline = Date.now() + 12_000;
    const check = async (): Promise<void> => {
      await loadLastPushReceived();
      const arrived = Boolean(lastPushReceived?.at) && lastPushReceived!.at !== before;
      if (!arrived && Date.now() < deadline) { window.setTimeout(() => void check(), 700); return; }
      if (activeProfileDialog) renderShell();
      await loadPushDiagnostics();
      if (activeProfileDialog) renderShell();
      if (arrived) {
        toast('Уведомление дошло до браузера');
        // If it arrived and the reader still asks where it is, the answer is in the system settings.
        window.setTimeout(() => { if (!activeEditor) showNotificationHelp(); }, 1_500);
      } else {
        toast('До браузера уведомление не дошло. Выключите и включите уведомления здесь, а затем проверьте ещё раз.');
      }
    };
    window.setTimeout(() => void check(), 1_200);
  });
}

async function unbindBrowserPush(): Promise<void> {
  const valid = sessionGuard();
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) await subscription.unsubscribe();
    await api(`/v1/push/subscription/${encodeURIComponent(deviceId())}`, { method: 'DELETE' });
  } catch { /* the server drops an unreachable subscription by itself */ }
  pushDeviceBound = false;
  await savePushBinding({}, bootstrap, valid).catch(() => undefined);
  if (valid()) { toast('Уведомления на этом устройстве выключены'); renderShell(); }
}

async function bindBrowserPush(requestPermission: boolean): Promise<void> {
  const account = bootstrap; const valid = sessionGuard();
  if (!account?.push.enabled || !('serviceWorker' in navigator) || !('PushManager' in window)) return;
  if (requestPermission && Notification.permission === 'default') await Notification.requestPermission();
  if (!valid()) return;
  if (Notification.permission !== 'granted') { if (requestPermission) toast('Уведомления запрещены в браузере'); return; }
  const registration = await navigator.serviceWorker.ready;
  if (!valid()) return;
  let subscription = await registration.pushManager.getSubscription();
  if (!valid()) return;
  const stored = await loadPushBinding();
  if (!valid()) return;
  if (subscription && stored?.vapid_key_id !== account.push.vapid_key_id) { await subscription.unsubscribe(); subscription = null; }
  if (!valid()) return;
  if (!subscription && (requestPermission || Notification.permission === 'granted')) {
    subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: vapidKey(account.push.vapid_public_key) });
  }
  if (!subscription || !valid()) return;
  const serialized = subscription.toJSON();
  const binding = await api<Record<string, unknown>>('/v1/push/subscription', { method: 'PUT', body: JSON.stringify({
    device_id: deviceId(), endpoint: subscription.endpoint, p256dh: serialized.keys?.p256dh, auth: serialized.keys?.auth,
  }) });
  if (!valid()) return;
  await savePushBinding({ ...binding, vapid_key_id: account.push.vapid_key_id }, account, valid);
  pushDeviceBound = true;
  if (valid()) { if (requestPermission) toast('Уведомления включены'); renderShell(); }
}

async function clearAccountStorage(user: Bootstrap['user'], epoch: string): Promise<void> {
  const db = await openDb();
  try {
    // A readwrite transaction is ordered after already-started puts. Late opens are fenced.
    const tx = db.transaction(['pending', 'meta'], 'readwrite');
    for (const name of ['pending', 'meta']) {
      const request = tx.objectStore(name).openCursor();
      request.onsuccess = () => {
        const cursor = request.result; if (!cursor) return;
        const item = cursor.value;
        if (item.user_id === user.id && item.workspace_id === user.workspace_id && item.local_epoch !== epoch) cursor.delete();
        cursor.continue();
      };
    }
    await transactionDone(tx);
  } finally { db.close(); }
}

function renderLogoutRecovery(): void {
  loadingStatus.refresh();
  activeEditor?.close(false);
  activeProfileDialog?.close();
  resetTitleAndFavicon();
  const screen = element('main', 'login');
  const card = element('section', 'login-card');
  card.append(element('h1', '', 'Chat Ostart'), element('p', 'error', logoutStatus));
  card.append(element('p', 'notice', 'При смене сессии в другой вкладке прежний отзыв не подтверждён. Новая сессия не будет удалена. Обратитесь к администратору для завершения прежней сессии; автоматического обхода блокировки нет.'));
  if (logoutFence.storageFailed) card.append(element('p', 'error', 'Хранилище недоступно: блокировка действует в этой вкладке, сохранение после перезагрузки не гарантируется.'));
  const retry = element('button', 'ghost', logoutBusy ? 'Проверка выхода…' : 'Повторить выход');
  retry.disabled = logoutBusy;
  retry.addEventListener('click', () => { const intent = logoutFence.read(); if (intent) void retryLogout(intent); });
  suspendMenu();
  card.append(retry); screen.append(card); root.replaceChildren(screen);
}

async function retryLogout(intent: LogoutIntent, account?: Bootstrap): Promise<void> {
  if (logoutBusy || logoutFence.read()?.id !== intent.id) return;
  logoutBusy = true; renderLogoutRecovery();
  try {
    const auth = account ?? await api<Bootstrap>('/v1/bootstrap');
    if (logoutFence.read()?.id !== intent.id) return;
    if (!intent.handle || auth.session_handle !== intent.handle) throw new Error('Сессия изменилась. Отзыв прежней сессии не подтверждён; новый вход заблокирован.');
    const response = await api<{ok: boolean; session_handle: string}>('/v1/session', {
      method: 'DELETE', headers: { 'X-CSRF-Token': auth.csrf_token },
      body: JSON.stringify({ expected_session_handle: intent.handle, device_id: (() => { try { return deviceId(); } catch { return undefined; } })() }),
    });
    if (!response.ok || response.session_handle !== intent.handle) throw new Error('Нет подтверждения точной сессии');
    logoutFence.acknowledge(intent);
  } catch (error) {
    logoutStatus = `${errorMessage(error)}. Отзыв прежней сессии не подтверждён. Личные данные и новый вход заблокированы.`;
  } finally {
    logoutBusy = false;
    if (logoutFence.read()) renderLogoutRecovery(); else if (!bootstrap) void renderLogin();
  }
}

async function logoutUser(remote = false): Promise<void> {
  const account = bootstrap; if (!account) return;
  if (!remote && !confirm('Выйти? Неотправленные сообщения и черновики этого аккаунта будут удалены.')) return;
  // Durable intent precedes all network and local async work. Never persist CSRF/cookie.
  const intent = remote ? logoutFence.read() ?? logoutFence.begin(account.session_handle) : logoutFence.begin(account.session_handle);
  cancelConversationSidebar();
  pmTaskPreviews.clear(); filesCards.clear();
  restoreNow=null;restoringMessages.clear();
  if(restoreDeadlineTimer!==null)window.clearTimeout(restoreDeadlineTimer);restoreDeadlineTimer=null;
  bugReportDraft = null;
  bootstrap = null; sessionGeneration += 1; selectionGeneration += 1; historyGeneration += 1; replyWork.cancel();clearManagedAttachmentLoads();
  const epoch = remote ? accountEpoch(account.user) : crypto.randomUUID();
  if (!remote) try { localStorage.setItem(`pm-chat:logout:${account.user.workspace_id}:${account.user.id}`, epoch); } catch { /* Immediate purge must still run. */ }
  reconnectEnabled = false; realtimeGeneration += 1;
  if (reconnectTimer !== null) window.clearTimeout(reconnectTimer); reconnectTimer = null;
  if (realtimeSettleTimer !== null) { window.clearTimeout(realtimeSettleTimer); realtimeSettleTimer = null; }
  reconnectDelay = 1500;
  socket?.close(); socket = null;
  if (foregroundPollTimer !== null) window.clearInterval(foregroundPollTimer); foregroundPollTimer = null;
  if (heartbeatTimer !== null) window.clearInterval(heartbeatTimer); heartbeatTimer = null;
  catchupController?.abort(); catchupController = null; pendingCurrentRevision = null;
  revisionCursors.clear(); bufferedEvents.clear(); snapshotLoading.clear(); catchups.clear(); clearAllTyping(); conversationCache.clear();
  mentionCandidates = null; conversations = []; messages = []; readCursors = []; current = null; currentDetails = null; historyAccess = null; currentMembers = []; chatInfoOpen = false; chatInfoMembersLoading = false; chatInfoAvailableUsers = null; conversationPins = null; chatInfoPeer = null; chatInfoAddUserId = ''; contextTargetId = null; scheduledMessages = null;
  refreshAfterNavigation = false; lastReadSent = null; messageNodes.clear();
  replyToMessageId = null; composerReplyTarget = null; composerMentionIds = []; composerMentionTokens.clear(); mentionPicker = null; composerAttachmentIds = []; composerStickerAttachmentIds.clear(); composerAttachments = [];
  const prefix = `pm-chat:draft:${account.user.workspace_id}:${account.user.id}:`;
  try { for (const key of Object.keys(localStorage)) if (key.startsWith(prefix)) localStorage.removeItem(key); } catch { /* Durable-storage limitation is shown on recovery screen. */ }
  history.replaceState({}, '', '/');
  void renderLogin();
  if (!remote) void retryLogout(intent, account);
  const generation = sessionGeneration;
  if ('serviceWorker' in navigator) void (async () => {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration || bootstrap || generation !== sessionGeneration) return;
    const subscription = await registration.pushManager.getSubscription();
    if (bootstrap || generation !== sessionGeneration) return;
    await subscription?.unsubscribe();
    if (bootstrap || generation !== sessionGeneration) return;
    const notifications = await registration.getNotifications();
    if (bootstrap || generation !== sessionGeneration) return;
    for (const notification of notifications) notification.close();
  })().catch(() => undefined);
  try { await clearAccountStorage(account.user, epoch); }
  catch { if (!bootstrap && generation === sessionGeneration) toast('Не удалось очистить локальную очередь. Очередь заблокирована; очистите данные сайта.'); }
}

window.addEventListener('storage', (event) => {
  if (bootstrap && event.key === backgroundPushPreferenceKey()) { reportConversationVisibility(); return; }
  if (event.key === logoutFenceKey || event.key?.startsWith(logoutFencePrefix) || event.key?.startsWith(`${logoutFenceKey}:legacy-ack:`) || event.key === null) {
    if (logoutFence.read()) {
      if (bootstrap) void logoutUser(true);
      else { sessionGeneration += 1; renderLogoutRecovery(); }
    } else if (!bootstrap) void renderLogin();
    return;
  }
  if (bootstrap && event.key === `pm-chat:logout:${bootstrap.user.workspace_id}:${bootstrap.user.id}`) void logoutUser(true);
});

// Opens a permalink or chat URL in place. A notification click reuses this instead of letting the
// service worker navigate the tab, which reloaded the whole app in front of the reader. The address
// is read by the same parser the address bar and Back/Forward use -- one place decides what a chat
// URL is.
async function openRoute(pathname: string): Promise<boolean> {
  if (!bootstrap) return false;
  const route = parseChatRoute(pathname, bootstrap.user.workspace_id);
  if (!route) return false;
  // A permalink names a message, so the address keeps its /m/ form instead of collapsing to /c/<id>,
  // and gets an entry of its own like any other navigation -- Back returns the reader to the chat
  // the link was in. Without a message, openConversation() below writes the entry itself.
  if (route.targetId && location.pathname !== pathname) {
    if (matchMedia('(max-width:760px)').matches) updateMobileChatHistory(history, pathname, mobileHistoryOwner());
    else history.pushState({}, '', pathname);
  }
  await openConversation(conversations.find((item) => item.id === route.conversationId) ?? route.conversationId, route.targetId);
  return true;
}

// Browser/Android Back and Forward: follow the address bar, which already names the destination.
window.addEventListener('popstate', () => {
  if (!bootstrap || logoutFence.read()) return;
  const route = parseChatRoute(location.pathname, bootstrap.user.workspace_id);
  if (route) {
    if (route.targetId || appliedPath !== location.pathname) void openConversation(conversations.find((item) => item.id === route.conversationId) ?? route.conversationId, route.targetId);
    else if (mobileListVisible) { mobileListVisible = false; renderShell(); }
    return;
  }
  if (current && matchMedia('(max-width:760px)').matches && sessionGuard()()
    && history.state?.pmChatList === mobileHistoryOwner()) {
    // A list return hides the owned mounted view instead of rebuilding it on Forward.
    rememberConversation();
    if (document.activeElement instanceof HTMLElement && document.activeElement.closest('.composer')) document.activeElement.blur();
    mobileListVisible = true; chatInfoOpen = false; appliedPath = location.pathname;
    renderShell(); reportConversationVisibility();
    return;
  }
  if (current) {
    rememberConversation();
    catchupController?.abort(); catchupController = null; catchups.clear(); pendingCurrentRevision = null;
    void conversationPages.select(null); clearConversationSelection(false);
  }
  chatInfoOpen = false;
  mobileListVisible = true; renderShell();
});

async function start(): Promise<void> {
  const generation = ++sessionGeneration; replyWork.cancel();
  const startedFenced = Boolean(logoutFence.read());
  if (startedFenced) renderLogoutRecovery();
  const progress = loadingStatus.begin('Подключаемся к чату…', () => generation === sessionGeneration && !logoutFence.read(), 0);
  root.querySelector(':scope > .startup-loading')?.remove();
  try {
    const result = await api<Bootstrap>('/v1/bootstrap');
    if (generation !== sessionGeneration) return;
    if (logoutFence.read()) { renderLogoutRecovery(); return; }
    if (startedFenced) { await renderLogin(); return; }
    restoreNow=anchorRestoreClock(result.server_time);
    bootstrap = result;
    try {
      const returnPath = takeFileAccessReturn(sessionStorage, result.user, location.pathname);
      if (returnPath) history.replaceState({}, '', returnPath);
    } catch { /* Optional navigation storage must not turn valid auth into login failure. */ }
  } catch { if (generation === sessionGeneration) await renderLogin(); return; }
  finally { progress.finish(); }
  const valid = sessionGuard();
  const account = bootstrap;
  try { await activateConversationSidebar(account); }
  catch { /* Retry remains available in the rendered sidebar. */ }
  if (!valid()) return; applyConversationSnapshot(true); renderShell();
  reconnectEnabled = true;
  // A route outside the first page is authorized by its independent detail GET.
  const route = parseChatRoute(location.pathname, bootstrap.user.workspace_id);
  if (route) await openConversation(conversations.find((item) => item.id === route.conversationId) ?? route.conversationId, route.targetId);
  if (!valid()) return;
  await connectRealtime(); if (!valid()) return; await flushPending(); if (!valid()) return;
  if (foregroundPollTimer !== null) window.clearInterval(foregroundPollTimer);
  foregroundPollTimer = window.setInterval(() => {
    if (!document.hidden && bootstrap) {
      // loadConversations() already renders internally (via runSidebarOperation()) on every
      // path that actually fetches anything; the extra unconditional renderShell() here was
      // a third full-page rebuild on top of those two, on every single 10s tick, contributing
      // to the same background jitter as catchUp()'s own redundant render (see there).
      if (current) void catchUp(current.id); else void loadConversations(selectionGuard());
    }
  }, 10_000);
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', (event) => {
      const data = event.data as { type?: string; url?: string; payload?: Record<string, unknown> } | null;
      if (data?.type === 'check-push-focus' && event.ports[0]) {
        const valid = sessionGuard();
        void loadPushBinding().then(binding => event.ports[0]?.postMessage({suppress:valid() && shouldSuppressOpenChatPush(data.payload ?? {}, {
          attended:tabAttended() && !mobileListVisible, suppressWhileOpen:suppressBackgroundPush(), conversationId:current?.id ?? null, binding,
        })})).catch(() => event.ports[0]?.postMessage({suppress:false}));
        return;
      }
      if (data?.type !== 'open-url' || typeof data.url !== 'string' || !data.url.startsWith('/')) return;
      void openRoute(data.url);
    });
    const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/', updateViaCache:'none' });
    if (!valid()) return;
    registration.addEventListener('updatefound', () => { if (valid()) toast('Доступна новая версия. Она применится после закрытия вкладок.'); });
  }
  await bindBrowserPush(false).catch(() => undefined);
}

window.addEventListener('online', () => void flushPending());
window.addEventListener('offline', renderShell);
// "Visible" for the server means the reader can actually see this chat, so it matches tabAttended()
// rather than document.hidden: a window sitting behind another app blinks its title for a new
// message, and must therefore also get the notification instead of having it suppressed as
// "already on screen".
let reportedVisible: boolean | null = null;
let reportedSuppressed = false;
function reportConversationVisibility(): void {
  if (socket?.readyState !== WebSocket.OPEN || !current) { reportedVisible = null; return; }
  const visible = tabAttended();
  const suppress = suppressBackgroundPush();
  if (visible === reportedVisible && suppress === reportedSuppressed) return;
  reportedVisible = visible; reportedSuppressed = suppress;
  socket.send(JSON.stringify({ type: 'visibility', conversation_id: current.id, visible, suppress_push: suppress }));
}
for (const type of ['focus', 'blur']) window.addEventListener(type, () => {
  reportConversationVisibility();
  // Coming back to the window is when the open chat is actually read; the render sends the receipt.
  if (current && document.hasFocus()) renderShell();
});
document.addEventListener('visibilitychange', () => {
  reportConversationVisibility();
  if (!document.hidden && current) void catchUp(current.id);
});
installTooltips();
installMessageMenuGestures(root);
if (window.visualViewport) blurOnKeyboardClose(window.visualViewport, () => document.activeElement instanceof HTMLElement ? document.activeElement : null, () => matchMedia('(pointer: coarse)').matches);
void start().catch(startupFailure).finally(() => { startupSettled = true; });
